import { useState, useEffect, useMemo, useRef } from 'react';
import { PlusCircle, Trophy, TrendingUp, Calendar, Calendar as CalendarIcon, DollarSign, LogOut, Settings, Users, Shield, Search, X, Edit2, Trash2, MapPin, Clock, CalendarDays, HelpCircle, Eye, UserPlus, Pill, LayoutGrid, Flag } from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import './App.css';
import './App.responsive.css';
import HorseCare from './components/HorseCare/HorseCare';
import Plotly from 'plotly.js-basic-dist';

// Chart colors constant
const CHART_COLORS = ['#ea580c', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

// Helper function to parse dates correctly (handles both "YYYY-MM-DD" and full ISO timestamps)
// Ensures dates display in local timezone without shifting to previous day
function parseLocalDate(dateString) {
  if (!dateString) return null;
  // Extract just the YYYY-MM-DD part and create date at local midnight
  const datePart = dateString.split('T')[0];
  return new Date(datePart + 'T00:00:00');
}

// API URL configuration
const API_URL = 'https://saddlepulse.com/api';

// Stripe configuration
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51SKUaoLIv2RpZnrNF8hioVjPCmjYcAMovP0SBqWhyNDA5nhnZcmUKRUBSigqnYXeqOfs2UcvghY34yJXnqSqa6Gn00a4dL0QW9';

export default function SaddlePulse() {
  const [user, setUser] = useState(null);
  const [authView, setAuthView] = useState('login');
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [horses, setHorses] = useState([]);
  const [races, setRaces] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedHorse, setExpandedHorse] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState('');
  const [expandedHorseVenueFilter, setExpandedHorseVenueFilter] = useState('');
  const [barnVenueFilter, setBarnVenueFilter] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  const [horseName, setHorseName] = useState('');
  const [horseFoalingDate, setHorseFoalingDate] = useState('');
  const [registeredName, setRegisteredName] = useState('');
  // Delete horse modal state
  const [showDeleteHorseModal, setShowDeleteHorseModal] = useState(false);
  const [horseToDelete, setHorseToDelete] = useState(null);
  const [deleteHorseLoading, setDeleteHorseLoading] = useState(false);
  // Transfer horse modal state
  const [showTransferHorseModal, setShowTransferHorseModal] = useState(false);
  const [horseToTransfer, setHorseToTransfer] = useState(null);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferHorseLoading, setTransferHorseLoading] = useState(false);
  // Edit horse modal state
  const [showEditHorseModal, setShowEditHorseModal] = useState(false);
  const [horseToEdit, setHorseToEdit] = useState(null);
  const [editHorseName, setEditHorseName] = useState('');
  const [editHorseFoalingDate, setEditHorseFoalingDate] = useState('');
  const [editHorseRegisteredName, setEditHorseRegisteredName] = useState('');
  const [editHorseLoading, setEditHorseLoading] = useState(false);
  const [raceDate, setRaceDate] = useState('');
  const [raceVenue, setRaceVenue] = useState('');
  const [raceTime, setRaceTime] = useState('');
  const [placement, setPlacement] = useState('');
  const [prizeMoney, setPrizeMoney] = useState('');
  const [entryFee, setEntryFee] = useState('');
  const [notes, setNotes] = useState('');
  const [noTime, setNoTime] = useState(false);
  const [barrelIssues, setBarrelIssues] = useState([]);
  const [riderName, setRiderName] = useState('');
  // Placements state (new structured placement system)
  const [placements, setPlacements] = useState([]);
  const [expenseDate, setExpenseDate] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseDescription, setExpenseDescription] = useState('');
  
  // Edit race state
  const [editingRace, setEditingRace] = useState(null);
  const [editRaceDate, setEditRaceDate] = useState('');
  const [editRaceVenue, setEditRaceVenue] = useState('');
  const [editRaceTime, setEditRaceTime] = useState('');
  const [editPlacement, setEditPlacement] = useState('');
  const [editPrizeMoney, setEditPrizeMoney] = useState('');
  const [editEntryFee, setEditEntryFee] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editNoTime, setEditNoTime] = useState(false);
  const [editBarrelIssues, setEditBarrelIssues] = useState([]);
  const [editRiderName, setEditRiderName] = useState('');
  const [editPlacements, setEditPlacements] = useState([]);

  // Barn state
  const [barnClients, setBarnClients] = useState([]);
  const [barnHorses, setBarnHorses] = useState([]);
  const [barnRaces, setBarnRaces] = useState([]);
  const [barnExpenses, setBarnExpenses] = useState([]);
  const [barnCode, setBarnCode] = useState('');
  const [selectedClient, setSelectedClient] = useState(null); // Filter by client in My Barn view
  const [expandedBarnHorse, setExpandedBarnHorse] = useState(null); // Expanded horse in barn view

  // Admin state
  const [allUsers, setAllUsers] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [viewingAsUser, setViewingAsUser] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');
  const [generatedResetLink, setGeneratedResetLink] = useState('');
  // Help/Support modal state
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpSubject, setHelpSubject] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpSuccess, setHelpSuccess] = useState('');
  const [helpError, setHelpError] = useState('');
  const [helpEmail, setHelpEmail] = useState('');
  // Barn cancellation state
  const [showCancelBarnModal, setShowCancelBarnModal] = useState(false);
  const [cancelBarnLoading, setCancelBarnLoading] = useState(false);
  // Demo modal state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  // Demo email capture state
  const [showDemoEmailModal, setShowDemoEmailModal] = useState(false);
  const [demoEmail, setDemoEmail] = useState('');
  const [demoEmailLoading, setDemoEmailLoading] = useState(false);

  const demoFeatures = [
    {
      title: 'Track Your Horses',
      description: 'Manage all your barrel racing horses in one place. Track their performance, expenses, and race history.',
      features: ['Add multiple horses', 'View detailed stats', 'Monitor performance trends']
    },
    {
      title: 'Record Race Results',
      description: 'Log every race with times, venues, and winnings. Track which horses are performing best.',
      features: ['Record race times', 'Track prize money', 'No-time tracking', 'Multiple venues']
    },
    {
      title: 'Manage Expenses',
      description: 'Keep track of all horse-related costs including vet visits, training, equipment, and more.',
      features: ['Categorize expenses', 'Track spending per horse', 'View expense history']
    },
    {
      title: 'Performance Analytics',
      description: 'Get insights into your horses performance with charts, statistics, and profit tracking.',
      features: ['Performance charts', 'Profit/loss analysis', 'Best time tracking', 'Improvement trends']
    },
    {
      title: 'Barn Management',
      description: 'Trainers can create a barn and share a barn code with clients to manage multiple riders.',
      features: ['Create your barn', 'Share barn code', 'View all client horses', 'Track barn statistics']
    }
  ];

  // Calendar/Events state
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [eventFilter, setEventFilter] = useState('all'); // 'all', 'barn', 'personal'
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [calendarHorseFilter, setCalendarHorseFilter] = useState('all');
  const calendarRef = useRef(null);
  const [isEditingEvent, setIsEditingEvent] = useState(false);

  // Event form state
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventType, setEventType] = useState('other');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventAllDay, setEventAllDay] = useState(false);
  const [eventHorseId, setEventHorseId] = useState('');
  const [eventVisibility, setEventVisibility] = useState('private');
  const [eventNotes, setEventNotes] = useState('');

  // Log Race Result state (convert calendar event to race)
  const [showLogRaceForm, setShowLogRaceForm] = useState(false);
  const [logRaceHorseId, setLogRaceHorseId] = useState('');
  const [logRaceTime, setLogRaceTime] = useState('');
  const [logRacePlacement, setLogRacePlacement] = useState('');
  const [logRacePrizeMoney, setLogRacePrizeMoney] = useState('');
  const [logRaceEntryFee, setLogRaceEntryFee] = useState('');
  const [logRaceNotes, setLogRaceNotes] = useState('');
  const [logRaceNoTime, setLogRaceNoTime] = useState(false);
  const [logRaceBarrelIssues, setLogRaceBarrelIssues] = useState([]);
  const [logRaceDeleteEvent, setLogRaceDeleteEvent] = useState(true);

  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionEndsAt, setSubscriptionEndsAt] = useState(null);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
  // Race search state
  const [raceSearchQuery, setRaceSearchQuery] = useState('');

  // External Events state (Find Events feature)
  const [externalEvents, setExternalEvents] = useState([]);
  const [savedExternalEvents, setSavedExternalEvents] = useState([]);
  const [externalEventsLoading, setExternalEventsLoading] = useState(false);
  const [externalEventsPage, setExternalEventsPage] = useState(1);
  const [externalEventsTotalPages, setExternalEventsTotalPages] = useState(1);
  const [externalEventsTotal, setExternalEventsTotal] = useState(0);
  const [externalEventsStateFilter, setExternalEventsStateFilter] = useState('');
  const [externalEventsAssociationFilter, setExternalEventsAssociationFilter] = useState('');
  const [externalEventsSearch, setExternalEventsSearch] = useState('');
  const [externalEventsFilters, setExternalEventsFilters] = useState({ states: [], associations: [], eventTypes: [] });
  const [externalEventsViewMode, setExternalEventsViewMode] = useState('cards'); // 'cards' or 'calendar'
  const [externalEventsSortBy, setExternalEventsSortBy] = useState('date'); // 'date' or 'distance'
  const [showAddExternalEventModal, setShowAddExternalEventModal] = useState(false);
  const [selectedExternalEvent, setSelectedExternalEvent] = useState(null);
  const [externalEventNotes, setExternalEventNotes] = useState('');
  const [externalEventHorsesEntered, setExternalEventHorsesEntered] = useState([]);
  const [externalEventStallReservation, setExternalEventStallReservation] = useState('');
  const [externalEventEntryStatus, setExternalEventEntryStatus] = useState('planning');
  // Location-based filtering state
  const [userZipcode, setUserZipcode] = useState('');
  const [userSearchRadius, setUserSearchRadius] = useState(100);
  const [locationFilterEnabled, setLocationFilterEnabled] = useState(false);
  // Submit Event modal state
  const [showSubmitEventModal, setShowSubmitEventModal] = useState(false);
  const [submitEventName, setSubmitEventName] = useState('');
  const [submitEventDate, setSubmitEventDate] = useState('');
  const [submitEventEndDate, setSubmitEventEndDate] = useState('');
  const [submitEventCity, setSubmitEventCity] = useState('');
  const [submitEventState, setSubmitEventState] = useState('');
  const [submitEventLocation, setSubmitEventLocation] = useState('');
  const [submitEventAssociation, setSubmitEventAssociation] = useState('');
  const [submitEventUrl, setSubmitEventUrl] = useState('');
  const [submitEventLoading, setSubmitEventLoading] = useState(false);
  // Public events state (for login page - no auth required)
  const [publicEvents, setPublicEvents] = useState([]);
  const [publicEventsLoading, setPublicEventsLoading] = useState(false);
  const [publicEventsPage, setPublicEventsPage] = useState(1);
  const [publicEventsTotalPages, setPublicEventsTotalPages] = useState(1);
  const [publicEventsTotal, setPublicEventsTotal] = useState(0);
  const [publicEventsStateFilter, setPublicEventsStateFilter] = useState('');
  const [publicEventsSearch, setPublicEventsSearch] = useState('');
  const [publicEventsFilters, setPublicEventsFilters] = useState({ states: [], associations: [] });
  const [showPublicEvents, setShowPublicEvents] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken(token);
    }
  }, []);

  // Check for password reset token in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setResetToken(token);
      setAuthView('reset-password');
      // Clear the URL parameters without reloading the page
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Check for demo parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const demoParam = urlParams.get('demo');
    if (demoParam === 'true' && !user) {
      // Show email capture modal before demo login
      setShowDemoEmailModal(true);
      // Clear the URL parameters without reloading the page
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // Check for view=register param (from landing page CTAs)
    const viewParam = urlParams.get('view');
    if (viewParam === 'register' && !user) {
      setAuthView('register');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Check for checkout success - show onboarding after Stripe checkout completes
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutParam = urlParams.get('checkout');
    if (checkoutParam === 'success' && user && !user.onboarding_completed) {
      setShowOnboarding(true);
      setOnboardingStep(1);
      // Clear the URL parameters without reloading the page
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user]);

  // Load public events when showPublicEvents is toggled (login page)
  useEffect(() => {
    if (showPublicEvents && !user) {
      loadPublicEvents(1);
      loadPublicEventsFilters();
    }
  }, [showPublicEvents, user]);

  // Reload public events when filters change
  useEffect(() => {
    if (showPublicEvents && !user) {
      loadPublicEvents(1);
    }
  }, [publicEventsStateFilter, publicEventsSearch]);

  // Load data when user logs in
  useEffect(() => {
    console.log('🔄 useEffect triggered - user:', user ? 'EXISTS' : 'NULL');
    if (user) {
      console.log('👤 User exists, loading data...');
      loadHorses();
      loadRaces();
      loadExpenses();
      loadSubscription();
    } else {
      console.log('❌ No user, skipping data load');
    }
  }, [user]);

  // Reset form fields and errors when changing views
  useEffect(() => {
    setError('');
    setSuccess('');
  }, [activeView]);

  // Auto-dismiss error and success messages after 2 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Handle window resize for responsive mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ESC key handler for closing expanded horse view
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (expandedBarnHorse) {
          setExpandedBarnHorse(null);
        } else if (expandedHorse) {
          setExpandedHorse(null);
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [expandedHorse, expandedBarnHorse]);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_URL}/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (err) {
      console.error('Token verification failed:', err);
      localStorage.removeItem('token');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const handleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        setIsDemoMode(false); // Ensure demo mode is turned off

        // Show onboarding for first-time users
        if (!data.user.onboarding_completed) {
          setShowOnboarding(true);
          setOnboardingStep(1);
        }

        setEmail('');
        setPassword('');
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const handleDemoEmailCapture = async () => {
    // Email is optional - allow skipping
    if (demoEmail.trim()) {
      setDemoEmailLoading(true);
      try {
        await fetch(`${API_URL}/demo/capture-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: demoEmail })
        });
      } catch (err) {
        console.error('Failed to capture demo email:', err);
        // Continue anyway - don't block demo access
      }
      setDemoEmailLoading(false);
    }

    // Close email modal and proceed to demo login
    setShowDemoEmailModal(false);
    handleDemoLogin();
  };

  const handleDemoLogin = async () => {
    setError('');
    setLoading(true);

    // Track demo login event
    if (window.gtag) {
      window.gtag('event', 'demo_login', {
        event_category: 'engagement',
        event_label: 'Demo Account Login'
      });
    }

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: 'demo.trainer@saddlepulse.com',
          password: 'Demo123!'
        })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        setIsDemoMode(true);
        setShowDemoModal(false); // Close demo modal after login
      } else {
        setError(data.message || 'Demo login failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    setError('');
    setSuccess('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Track registration event
        if (window.gtag) {
          window.gtag('event', 'sign_up', {
            event_category: 'engagement',
            event_label: 'User Registration'
          });
        }

        // Auto-login user with returned token, then redirect to Stripe checkout for trial
        if (data.token && data.user) {
          localStorage.setItem('token', data.token);
          setUser(data.user);
          setIsDemoMode(false); // Ensure demo mode is turned off

          // Clear form
          setEmail('');
          setPassword('');
          setConfirmPassword('');
          setFirstName('');
          setLastName('');

          // Immediately redirect to Stripe checkout to start trial with credit card
          try {
            const checkoutResponse = await fetch(`${API_URL}/stripe/create-checkout-session`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${data.token}`
              }
            });
            const checkoutData = await checkoutResponse.json();
            if (checkoutResponse.ok && checkoutData.url) {
              window.location.href = checkoutData.url;
              return;
            }
          } catch (checkoutErr) {
            console.error('Checkout redirect error:', checkoutErr);
            // User will see subscription gate on next load
          }
        } else {
          setSuccess('Registration successful! Please log in.');
          setAuthView('login');
        }
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsDemoMode(false); // Clear demo mode state
    setHorses([]);
    setRaces([]);
    setExpenses([]);
    // Reset subscription state
    setSubscriptionStatus(null);
    setTrialEndDate(null);
    setHasStripeCustomer(false);
  };

  const loadHorses = async () => {
    try {
      const response = await fetch(`${API_URL}/horses`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setHorses(data);
    } catch (err) {
      console.error('Failed to load horses:', err);
    }
  };

  const loadRaces = async () => {
    try {
      const response = await fetch(`${API_URL}/races`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setRaces(data);
    } catch (err) {
      console.error('Failed to load races:', err);
    }
  };

  const loadExpenses = async () => {
    try {
      const response = await fetch(`${API_URL}/expenses`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setExpenses(data);
    } catch (err) {
      console.error('Failed to load expenses:', err);
    }
  };

  // Barn data loading functions
  const loadBarnClients = async () => {
    try {
      const response = await fetch(`${API_URL}/barn/clients`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBarnClients(data);
      }
    } catch (err) {
      console.error('Failed to load barn clients:', err);
    }
  };

  const loadBarnHorses = async () => {
    try {
      const response = await fetch(`${API_URL}/barn/horses`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBarnHorses(data);
      }
    } catch (err) {
      console.error('Failed to load barn horses:', err);
    }
  };

  const loadBarnRaces = async () => {
    try {
      const response = await fetch(`${API_URL}/barn/races`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBarnRaces(data);
      }
    } catch (err) {
      console.error('Failed to load barn races:', err);
    }
  };

  const loadBarnExpenses = async () => {
    try {
      const response = await fetch(`${API_URL}/barn/expenses`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBarnExpenses(data);
      }
    } catch (err) {
      console.error('Failed to load barn expenses:', err);
    }
  };

  const loadBarnData = () => {
    if (user?.barn_code) {
      loadBarnClients();
      loadBarnHorses();
      loadBarnRaces();
      loadBarnExpenses();
    }
  };

  // Admin Functions
  const loadAdminData = async () => {
    try {
      const token = localStorage.getItem('token');

      // Load users
      const usersRes = await fetch(`${API_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const usersData = await usersRes.json();
      setAllUsers(usersData.users);

      // Load stats
      const statsRes = await fetch(`${API_URL}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setAdminStats(statsData);
    } catch (err) {
      console.error('Admin data load error:', err);
      setError('Failed to load admin data');
    }
  };

  const viewAsUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/users/${userId}/data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();

      setViewingAsUser(data.user);
      setHorses(data.horses);
      setRaces(data.races);
      setExpenses(data.expenses);
      setActiveView('dashboard');
    } catch (err) {
      console.error('View as user error:', err);
      setError('Failed to load user data');
    }
  };

  const exitAdminView = () => {
    setViewingAsUser(null);
    loadHorses();
    loadRaces();
    loadExpenses();
      loadSubscription();
    setActiveView('admin-panel');
  };

  // Password Reset Functions
  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setError('Please enter your email');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setGeneratedToken('');
    setGeneratedResetLink('');

    try {
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });

      const data = await res.json();

      if (data.success) {
        setGeneratedToken(data.token);
        setGeneratedResetLink(data.resetLink);
        setSuccess('Reset token generated successfully!');
        setResetEmail('');
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess(`${type} copied to clipboard!`);
      setTimeout(() => {
        if (generatedToken) {
          setSuccess('Reset token generated successfully!');
        }
      }, 2000);
    }).catch(() => {
      setError('Failed to copy to clipboard');
    });
  };

  const loadSubscription = async () => {
    console.log('🔍 loadSubscription() called');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/user/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log("📊 Subscription API response:", data);
        console.log("📊 Setting subscriptionStatus to:", data.status);
        setSubscriptionStatus(data.status);
        console.log("✅ Loaded subscription status:", data.status);
        setSubscriptionEndsAt(data.subscriptionEndsAt);
        setTrialEndDate(data.trialEndDate);
        setHasStripeCustomer(data.hasStripeCustomer);
      }
    } catch (err) {
      console.error('Error loading subscription:', err);
    }
  };

  
  const handleHelpSubmit = async () => {
    // For guest users (not logged in), require email
    if (!user && !helpEmail.trim()) {
      setHelpError('Please provide your email address');
      return;
    }

    if (!helpSubject.trim() || !helpMessage.trim()) {
      setHelpError('Please fill in both subject and message');
      return;
    }

    setHelpLoading(true);
    setHelpError('');
    setHelpSuccess('');

    try {
      let response;

      if (user) {
        // Authenticated user - use existing endpoint
        const token = localStorage.getItem('token');
        response = await fetch(`${API_URL}/support/help`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            subject: helpSubject,
            message: helpMessage
          })
        });
      } else {
        // Guest user - use unauthenticated endpoint
        response = await fetch(`${API_URL}/support/guest-help`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: helpEmail,
            subject: helpSubject,
            message: helpMessage
          })
        });
      }

      const data = await response.json();

      if (response.ok) {
        setHelpSuccess('Your support request has been sent successfully!');
        setHelpEmail('');
        setHelpSubject('');
        setHelpMessage('');
        setTimeout(() => {
          setShowHelpModal(false);
          setHelpSuccess('');
        }, 2000);
      } else {
        setHelpError(data.message || 'Failed to send support request');
      }
    } catch (err) {
      setHelpError('Connection error. Please try again.');
    } finally {
      setHelpLoading(false);
    }
  };

  const handleCancelBarn = async () => {
    setCancelBarnLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/barn/cancel`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Update user state to remove barn_code
        setUser(data.user);
        setBarnCode('');
        setBarnClients([]);
        setBarnHorses([]);
        setBarnRaces([]);
        setBarnExpenses([]);
        setShowCancelBarnModal(false);
        // Switch back to dashboard
        setActiveView('dashboard');
      } else {
        alert(data.message || 'Failed to cancel barn');
      }
    } catch (err) {
      alert('Connection error. Please try again.');
    } finally {
      setCancelBarnLoading(false);
    }
  };

  const handleSubscribeClick = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const error = await response.json();
        setError(error.message || 'Failed to create checkout session');
      }
    } catch (err) {
      setError('Error initiating checkout');
      console.error('Checkout error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const error = await response.json();
        setError(error.message || 'Failed to open subscription management');
      }
    } catch (err) {
      setError('Error opening subscription management');
      console.error('Portal error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetToken || !newPassword) {
      setError('Please enter reset token and new password');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, newPassword })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(data.message);
        setResetToken('');
        setNewPassword('');
        setConfirmNewPassword('');
        setTimeout(() => setAuthView('login'), 2000);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Calendar/Events Functions
  const loadEvents = async () => {
    try {
      const res = await fetch(`${API_URL}/events`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setEvents(data.events || []);
    } catch (err) {
      console.error('Load events error:', err);
    }
  };

  // External Events Functions (Find Events feature)
  const loadExternalEvents = async (page = 1, forCalendar = false) => {
    setExternalEventsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: forCalendar ? '500' : '30'  // Load more events for calendar view
      });

      if (externalEventsStateFilter) params.append('state', externalEventsStateFilter);
      if (externalEventsAssociationFilter) params.append('association', externalEventsAssociationFilter);
      if (externalEventsSearch) params.append('search', externalEventsSearch);
      // Add location-based filtering
      if (locationFilterEnabled && userZipcode) {
        params.append('zipcode', userZipcode);
        params.append('radius', userSearchRadius.toString());
      }
      // Add sort parameter
      params.append('sort', externalEventsSortBy);

      const res = await fetch(`${API_URL}/external-events?${params}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();

      if (res.ok) {
        let events = data.events || [];
        // Client-side sorting based on preference
        if (externalEventsSortBy === 'date') {
          events = events.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
        } else if (externalEventsSortBy === 'distance' && locationFilterEnabled) {
          events = events.sort((a, b) => {
            if (a.distance === null) return 1;
            if (b.distance === null) return -1;
            return a.distance - b.distance;
          });
        }
        setExternalEvents(events);
        setExternalEventsPage(data.pagination?.page || 1);
        setExternalEventsTotalPages(data.pagination?.totalPages || 1);
        setExternalEventsTotal(data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Load external events error:', err);
    } finally {
      setExternalEventsLoading(false);
    }
  };

  const loadExternalEventsFilters = async () => {
    try {
      const res = await fetch(`${API_URL}/external-events/filters`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();

      if (res.ok) {
        setExternalEventsFilters({
          states: data.states || [],
          associations: data.associations || [],
          eventTypes: data.eventTypes || []
        });
      }
    } catch (err) {
      console.error('Load external events filters error:', err);
    }
  };

  // Public Events Functions (for login page - no auth required)
  const loadPublicEvents = async (page = 1) => {
    setPublicEventsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      if (publicEventsStateFilter) params.append('state', publicEventsStateFilter);
      if (publicEventsSearch) params.append('search', publicEventsSearch);

      const res = await fetch(`${API_URL}/public/events?${params}`);
      const data = await res.json();

      if (res.ok) {
        setPublicEvents(data.events || []);
        setPublicEventsPage(data.pagination?.page || 1);
        setPublicEventsTotalPages(data.pagination?.totalPages || 1);
        setPublicEventsTotal(data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Load public events error:', err);
    } finally {
      setPublicEventsLoading(false);
    }
  };

  const loadPublicEventsFilters = async () => {
    try {
      const res = await fetch(`${API_URL}/public/events/filters`);
      const data = await res.json();

      if (res.ok) {
        setPublicEventsFilters({
          states: data.states || [],
          associations: data.associations || []
        });
      }
    } catch (err) {
      console.error('Load public events filters error:', err);
    }
  };

  const loadSavedExternalEvents = async () => {
    try {
      const res = await fetch(`${API_URL}/external-events/saved`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();

      if (res.ok) {
        setSavedExternalEvents(data.savedEvents || []);
      }
    } catch (err) {
      console.error('Load saved external events error:', err);
    }
  };

  const handleAddExternalEventToCalendar = async () => {
    if (!selectedExternalEvent) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/external-events/${selectedExternalEvent.id}/add-to-calendar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          notes: externalEventNotes,
          horses_entered: externalEventHorsesEntered,
          stall_reservation: externalEventStallReservation,
          entry_status: externalEventEntryStatus
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('Event added to your calendar!');
        setShowAddExternalEventModal(false);
        loadSavedExternalEvents();
        // Reset form
        setExternalEventNotes('');
        setExternalEventHorsesEntered([]);
        setExternalEventStallReservation('');
        setExternalEventEntryStatus('planning');
        setSelectedExternalEvent(null);
      } else {
        setError(data.message || 'Failed to add event to calendar');
      }
    } catch (err) {
      setError('Failed to add event to calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSavedExternalEvent = async (savedEventId) => {
    if (!window.confirm('Remove this event from your calendar?')) return;

    try {
      const res = await fetch(`${API_URL}/external-events/saved/${savedEventId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        setSuccess('Event removed from your calendar');
        loadSavedExternalEvents();
      }
    } catch (err) {
      setError('Failed to remove event');
    }
  };

  const openAddExternalEventModal = (event) => {
    setSelectedExternalEvent(event);
    setExternalEventNotes('');
    setExternalEventHorsesEntered([]);
    setExternalEventStallReservation('');
    setExternalEventEntryStatus('planning');
    setShowAddExternalEventModal(true);
  };

  const handleSubmitEvent = async () => {
    if (!submitEventName || !submitEventDate) {
      setError('Event name and date are required');
      return;
    }

    setSubmitEventLoading(true);
    try {
      const res = await fetch(`${API_URL}/external-events/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: submitEventName,
          event_date: submitEventDate,
          end_date: submitEventEndDate || null,
          city: submitEventCity,
          state: submitEventState,
          location: submitEventLocation,
          association: submitEventAssociation,
          registration_url: submitEventUrl
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('Event submitted! It will appear after review.');
        setShowSubmitEventModal(false);
        // Reset form
        setSubmitEventName('');
        setSubmitEventDate('');
        setSubmitEventEndDate('');
        setSubmitEventCity('');
        setSubmitEventState('');
        setSubmitEventLocation('');
        setSubmitEventAssociation('');
        setSubmitEventUrl('');
      } else {
        setError(data.message || 'Failed to submit event');
      }
    } catch (err) {
      setError('Failed to submit event');
    } finally {
      setSubmitEventLoading(false);
    }
  };

  const loadUserLocation = async () => {
    try {
      const res = await fetch(`${API_URL}/user/location`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok && data.zipcode) {
        setUserZipcode(data.zipcode);
        setUserSearchRadius(data.radius || 100);
        setLocationFilterEnabled(true);
      }
    } catch (err) {
      console.error('Load user location error:', err);
    }
  };

  const saveUserLocation = async (zip, radius) => {
    try {
      const res = await fetch(`${API_URL}/user/location`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ zipcode: zip, radius: radius })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess('Location saved!');
        setLocationFilterEnabled(true);
        loadExternalEvents(1);
      } else {
        setError(data.message || 'Failed to save location');
      }
    } catch (err) {
      setError('Failed to save location');
    }
  };

  const handleAddEvent = async () => {
    if (!eventTitle || !eventStartDate) {
      setError('Event title and start date are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const startDateTime = eventAllDay
        ? eventStartDate
        : `${eventStartDate}T${eventStartTime || '00:00'}`;

      const endDateTime = eventEndDate
        ? (eventAllDay ? eventEndDate : `${eventEndDate}T${eventEndTime || '23:59'}`)
        : null;

      const barnCodeToUse = eventVisibility === 'barn' ? user.barn_code : null;

      const res = await fetch(`${API_URL}/events`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: eventTitle,
          description: eventDescription,
          event_type: eventType,
          start_date: startDateTime,
          end_date: endDateTime,
          location: eventLocation,
          all_day: eventAllDay,
          horse_id: eventHorseId || null,
          visibility: eventVisibility,
          notes: eventNotes,
          barn_code: barnCodeToUse
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('Event created successfully!');
        loadEvents();
        setShowAddEvent(false);
        // Reset form
        setEventTitle('');
        setEventDescription('');
        setEventType('other');
        setEventStartDate('');
        setEventStartTime('');
        setEventEndDate('');
        setEventEndTime('');
        setEventLocation('');
        setEventAllDay(false);
        setEventHorseId('');
        setEventVisibility('private');
        setEventNotes('');
      } else {
        setError(data.message || 'Failed to create event');
      }
    } catch (err) {
      setError('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEvent = async () => {
    if (!selectedEvent) return;

    setLoading(true);
    setError('');

    try {
      const startDateTime = eventAllDay
        ? eventStartDate
        : `${eventStartDate}T${eventStartTime || '00:00'}`;

      const endDateTime = eventEndDate
        ? (eventAllDay ? eventEndDate : `${eventEndDate}T${eventEndTime || '23:59'}`)
        : null;

      const barnCodeToUse = eventVisibility === 'barn' ? user.barn_code : null;

      const res = await fetch(`${API_URL}/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: eventTitle,
          description: eventDescription,
          event_type: eventType,
          start_date: startDateTime,
          end_date: endDateTime,
          location: eventLocation,
          all_day: eventAllDay,
          horse_id: eventHorseId || null,
          visibility: eventVisibility,
          notes: eventNotes,
          barn_code: barnCodeToUse
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('Event updated successfully!');
        loadEvents();
        setShowEventModal(false);
        setSelectedEvent(null);
      } else {
        setError(data.message || 'Failed to update event');
      }
    } catch (err) {
      setError('Failed to update event');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/events/${eventId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        setSuccess('Event deleted successfully!');
        loadEvents();
        setShowEventModal(false);
        setSelectedEvent(null);
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to delete event');
      }
    } catch (err) {
      setError('Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  // Handle logging a race result from a calendar event
  const handleLogRaceResult = async (e) => {
    e.preventDefault();
    if (isDemoMode) {
      setError('Demo mode: Cannot log race results');
      return;
    }

    if (!logRaceHorseId) {
      setError('Please select a horse');
      return;
    }

    if (!logRaceNoTime && !logRaceTime) {
      setError('Please enter a time or mark as no-time');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/events/${selectedEvent.id}/log-race-result`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          horse_id: parseInt(logRaceHorseId),
          time: logRaceNoTime ? null : parseFloat(logRaceTime),
          placement: logRacePlacement || null,
          prize_money: logRacePrizeMoney ? parseFloat(logRacePrizeMoney) : null,
          entry_fee: logRaceEntryFee ? parseFloat(logRaceEntryFee) : null,
          notes: logRaceNotes || null,
          no_time: logRaceNoTime,
          barrel_issues: logRaceBarrelIssues,
          delete_event: logRaceDeleteEvent
        })
      });

      if (res.ok) {
        setSuccess('Race result logged successfully!');
        // Refresh data
        loadRaces();
        if (logRaceDeleteEvent) {
          loadEvents();
        }
        // Close modal and reset form
        setShowEventModal(false);
        setSelectedEvent(null);
        setShowLogRaceForm(false);
        resetLogRaceForm();
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to log race result');
      }
    } catch (err) {
      setError('Failed to log race result');
    } finally {
      setLoading(false);
    }
  };

  // Reset log race form
  const resetLogRaceForm = () => {
    setLogRaceHorseId('');
    setLogRaceTime('');
    setLogRacePlacement('');
    setLogRacePrizeMoney('');
    setLogRaceEntryFee('');
    setLogRaceNotes('');
    setLogRaceNoTime(false);
    setLogRaceBarrelIssues([]);
    setLogRaceDeleteEvent(true);
  };

  const openEventModal = (event) => {
    setSelectedEvent(event);

    // Pre-fill form if user owns the event
    if (event.created_by === user.id) {
      setEventTitle(event.title);
      setEventDescription(event.description || '');
      setEventType(event.event_type);

      const startDate = new Date(event.start_date);
      setEventStartDate(startDate.toISOString().split('T')[0]);
      if (!event.all_day) {
        setEventStartTime(startDate.toTimeString().slice(0, 5));
      }

      if (event.end_date) {
        const endDate = new Date(event.end_date);
        setEventEndDate(endDate.toISOString().split('T')[0]);
        if (!event.all_day) {
          setEventEndTime(endDate.toTimeString().slice(0, 5));
        }
      }

      setEventLocation(event.location || '');
      setEventAllDay(event.all_day);
      setEventHorseId(event.horse_id || '');
      setEventVisibility(event.visibility);
      setEventNotes(event.notes || '');
    }

    setShowEventModal(true);
  };

  // Calculate age from foaling date
  const calculateAge = (foalingDate) => {
    if (!foalingDate) return null;
    const birth = new Date(foalingDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Get display age - from foaling_date if available, otherwise static age field
  const getHorseAge = (horse) => {
    if (horse.foaling_date) {
      return calculateAge(horse.foaling_date);
    }
    return horse.age;
  };

  const getEventColor = (eventType) => {
    const colors = {
      competition: '#ea580c', // Orange
      practice: '#3b82f6',    // Blue
      vet: '#dc2626',         // Red
      farrier: '#92400e',     // Brown
      training: '#10b981',    // Green
      other: '#6b7280'        // Gray
    };
    return colors[eventType] || colors.other;
  };

  const addHorse = async () => {
    if (isDemoMode) {
      setError('Cannot add horses in demo mode. This is read-only access.');
      return;
    }
    if (!horseName.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/horses`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: horseName,
          foaling_date: horseFoalingDate || null,
          registered_name: registeredName || null
        })
      });

      if (response.ok) {
        await loadHorses();
        setHorseName('');
        setHorseFoalingDate('');
        setRegisteredName('');
        setSuccess('Horse added successfully!');
        setTimeout(() => {
          setSuccess('');
          setActiveView('dashboard');
        }, 1500);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to add horse');
      }
    } catch (err) {
      setError('Failed to add horse');
    }
    setLoading(false);
  };

  const editHorse = async () => {
    if (isDemoMode) {
      setError('Cannot edit horses in demo mode. This is read-only access.');
      setShowEditHorseModal(false);
      return;
    }
    if (!horseToEdit || !editHorseName.trim()) {
      setError('Please enter a horse name');
      return;
    }

    setEditHorseLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/horses/${horseToEdit.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: editHorseName.trim(),
          foaling_date: editHorseFoalingDate || null,
          registered_name: editHorseRegisteredName.trim() || null
        })
      });

      if (response.ok) {
        await loadHorses();
        setSuccess('Horse updated successfully!');
        setShowEditHorseModal(false);
        setHorseToEdit(null);
        setEditHorseName('');
        setEditHorseFoalingDate('');
        setEditHorseRegisteredName('');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update horse');
      }
    } catch (err) {
      setError('Failed to update horse');
    }
    setEditHorseLoading(false);
  };

  const deleteHorse = async () => {
    if (isDemoMode) {
      setError('Cannot delete horses in demo mode. This is read-only access.');
      setShowDeleteHorseModal(false);
      return;
    }
    if (!horseToDelete) return;

    setDeleteHorseLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/horses/${horseToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await loadHorses();
        await loadRaces();
        await loadExpenses();
        setSuccess('Horse deleted successfully!');
        setShowDeleteHorseModal(false);
        setHorseToDelete(null);
        setExpandedHorse(null);
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError('Failed to delete horse');
      }
    } catch (err) {
      setError('Failed to delete horse');
    }
    setDeleteHorseLoading(false);
  };

  const transferHorse = async () => {
    if (isDemoMode) {
      setError('Cannot transfer horses in demo mode. This is read-only access.');
      setShowTransferHorseModal(false);
      return;
    }
    if (!horseToTransfer || !transferEmail.trim()) {
      setError('Please enter recipient email address');
      return;
    }

    setTransferHorseLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/horses/${horseToTransfer.id}/transfer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          recipient_email: transferEmail
        })
      });

      if (response.ok) {
        await loadHorses();
        await loadRaces();
        await loadExpenses();
        setSuccess('Horse transferred successfully!');
        setShowTransferHorseModal(false);
        setHorseToTransfer(null);
        setTransferEmail('');
        setExpandedHorse(null);
        setTimeout(() => setSuccess(''), 2000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to transfer horse');
      }
    } catch (err) {
      setError('Failed to transfer horse');
    }
    setTransferHorseLoading(false);
  };

  const addRace = async () => {
    if (isDemoMode) {
      setError('Cannot add races in demo mode. This is read-only access.');
      return;
    }
    if (!selectedHorse || !raceDate || !raceVenue) {
      setError('Please fill in all required race fields');
      return;
    }
    
    if (!noTime && !raceTime) {
      setError('Please enter a time or select "No Time"');
      return;
    }
    
    if (noTime && barrelIssues.length === 0) {
      setError('Please select which barrel(s) were hit or indicate off-pattern');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/races`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          horse_id: selectedHorse,
          date: raceDate,
          venue: raceVenue,
          time: raceTime ? parseFloat(raceTime) : null,
          no_time: noTime,
          barrel_issues: noTime ? barrelIssues : [],
          placement: placement || null,
          prize_money: prizeMoney ? parseFloat(prizeMoney) : null,
          entry_fee: entryFee ? parseFloat(entryFee) : null,
          notes: notes || null,
          rider_name: riderName.trim() || null,
          placements: placements.length > 0 ? placements : []
        })
      });

      if (response.ok) {
        await loadRaces();
        setSelectedHorse(null);
        setRaceDate('');
        setRaceVenue('');
        setRaceTime('');
        setNoTime(false);
        setBarrelIssues([]);
        setPlacement('');
        setPrizeMoney('');
        setEntryFee('');
        setNotes('');
        setRiderName('');
        setPlacements([]);
        setSuccess('Race added successfully!');
        setTimeout(() => {
          setSuccess('');
          setActiveView('dashboard');
        }, 1500);
      } else {
        setError('Failed to add race');
      }
    } catch (err) {
      setError('Failed to add race');
    }
    setLoading(false);
  };

  const addExpense = async () => {
    if (isDemoMode) {
      setError('Cannot add expenses in demo mode. This is read-only access.');
      return;
    }
    if (!selectedHorse || !expenseDate || !expenseCategory || !expenseAmount) {
      setError('Please fill in all required expense fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/expenses`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          horse_id: selectedHorse,
          date: expenseDate,
          category: expenseCategory,
          amount: parseFloat(expenseAmount),
          description: expenseDescription || null
        })
      });
      
      if (response.ok) {
        await loadExpenses();
      loadSubscription();
        setSelectedHorse(null);
        setExpenseDate('');
        setExpenseCategory('');
        setExpenseAmount('');
        setExpenseDescription('');
        setSuccess('Expense added successfully!');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError('Failed to add expense');
      }
    } catch (err) {
      setError('Failed to add expense');
    }
    setLoading(false);
  };

  const startEditRace = (race) => {
    setEditingRace(race.id);
    setSelectedHorse(race.horse_id);
    // Convert date to YYYY-MM-DD format for date input
    const dateStr = race.date.split('T')[0];
    setEditRaceDate(dateStr);
    setEditRaceVenue(race.venue);
    // Preserve exact decimal precision for time (3 decimal places)
    setEditRaceTime(race.time ? parseFloat(race.time).toFixed(3) : '');
    setEditNoTime(race.no_time || false);
    setEditBarrelIssues(race.barrel_issues || []);
    setEditPlacement(race.placement || '');
    setEditPrizeMoney(race.prize_money ? parseFloat(race.prize_money).toFixed(2) : '');
    setEditEntryFee(race.entry_fee ? parseFloat(race.entry_fee).toFixed(2) : '');
    setEditNotes(race.notes || '');
    setEditRiderName(race.rider_name || '');
    setEditPlacements(race.placements || []);
  };

  const cancelEditRace = () => {
    setEditingRace(null);
    setEditRaceDate('');
    setEditRaceVenue('');
    setEditRaceTime('');
    setEditNoTime(false);
    setEditBarrelIssues([]);
    setEditPlacement('');
    setEditPrizeMoney('');
    setEditEntryFee('');
    setEditNotes('');
    setEditRiderName('');
    setEditPlacements([]);
  };

  const updateRace = async (raceId) => {
    if (isDemoMode) {
      setError('Cannot update races in demo mode. This is read-only access.');
      return;
    }
    if (!editRaceDate || !editRaceVenue) {
      setError('Please fill in all required race fields');
      return;
    }
    
    if (!editNoTime && !editRaceTime) {
      setError('Please enter a time or select "No Time"');
      return;
    }
    
    if (editNoTime && editBarrelIssues.length === 0) {
      setError('Please select which barrel(s) were hit or indicate off-pattern');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/races/${raceId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          date: editRaceDate,
          venue: editRaceVenue,
          time: editRaceTime ? parseFloat(editRaceTime) : null,
          no_time: editNoTime,
          barrel_issues: editNoTime ? editBarrelIssues : [],
          placement: editPlacement || null,
          prize_money: editPrizeMoney ? parseFloat(editPrizeMoney) : null,
          entry_fee: editEntryFee ? parseFloat(editEntryFee) : null,
          notes: editNotes || null,
          rider_name: editRiderName.trim() || null,
          placements: editPlacements
        })
      });
      
      if (response.ok) {
        await loadRaces();
        setSuccess('Race updated successfully!');
        setTimeout(() => setSuccess(''), 2000);
        cancelEditRace();
        setActiveView('race-list');
      } else {
        setError('Failed to update race');
      }
    } catch (err) {
      setError('Failed to update race');
    }
    setLoading(false);
  };

  const deleteRace = async (raceId) => {
    if (isDemoMode) {
      setError('Cannot delete races in demo mode. This is read-only access.');
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this race?')) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/races/${raceId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await loadRaces();
        setSuccess('Race deleted successfully!');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError('Failed to delete race');
      }
    } catch (err) {
      setError('Failed to delete race');
    }
    setLoading(false);
  };

  // Barn management functions
  const createBarn = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/barn/create`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        setSuccess(`Barn created! Your barn code is: ${data.user.barn_code}`);
        loadBarnData();
      } else {
        setError(data.message || 'Failed to create barn');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const joinBarn = async () => {
    if (isDemoMode) {
      setError('Cannot join barn in demo mode. This is read-only access.');
      return;
    }
    if (!barnCode.trim()) {
      setError('Please enter a barn code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/barn/join`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ barn_code: barnCode.trim().toUpperCase() })
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        setSuccess(data.message);
        setBarnCode('');
      } else {
        setError(data.message || 'Failed to join barn');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const leaveBarn = async () => {
    if (isDemoMode) {
      setError('Cannot leave barn in demo mode. This is read-only access.');
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to leave this barn?')) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/barn/leave`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        setSuccess(data.message);
      } else {
        setError(data.message || 'Failed to leave barn');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  const getHorseStats = (horseId) => {
    const allHorseRaces = races.filter(r => r.horse_id === horseId);

    if (allHorseRaces.length === 0) return null;

    const noTimeRaces = allHorseRaces.filter(r => r.no_time === true);
    // Clean runs: has a valid time AND is not marked as no_time
    const cleanRuns = allHorseRaces.filter(r => r.time !== null && !r.no_time);

    const noTimePercent = allHorseRaces.length > 0
      ? ((noTimeRaces.length / allHorseRaces.length) * 100).toFixed(1)
      : '0.0';

    const barrelIssueStats = {};
    if (noTimeRaces.length > 0) {
      const issueCount = {};

      noTimeRaces.forEach(race => {
        const issues = race.barrel_issues || [];
        issues.forEach(issue => {
          issueCount[issue] = (issueCount[issue] || 0) + 1;
        });
      });

      Object.keys(issueCount).forEach(issue => {
        barrelIssueStats[issue] = ((issueCount[issue] / noTimeRaces.length) * 100).toFixed(1);
      });
    }

    let bestTime = null;
    let avgTime = null;

    if (cleanRuns.length > 0) {
      const times = cleanRuns.map(r => parseFloat(r.time));
      bestTime = Math.min(...times).toFixed(3);
      avgTime = (times.reduce((a, b) => a + b, 0) / times.length).toFixed(3);
    }
    
    const recentRaces = allHorseRaces.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);
    // Calculate total prizes from placements payouts (new system) or fallback to prize_money (legacy)
    const totalPrizes = allHorseRaces.reduce((sum, r) => {
      // If race has placements with payouts, sum those
      if (r.placements && r.placements.length > 0) {
        return sum + r.placements.reduce((pSum, p) => pSum + (parseFloat(p.payout) || 0), 0);
      }
      // Fallback to legacy prize_money field
      return sum + (parseFloat(r.prize_money) || 0);
    }, 0);
    
    return {
      totalRaces: allHorseRaces.length,
      completedRaces: cleanRuns.length,
      noTimeCount: noTimeRaces.length,
      noTimePercent: noTimePercent,
      barrelIssueStats: barrelIssueStats,
      bestTime: bestTime,
      avgTime: avgTime,
      recentRaces,
      totalPrizes
    };
  };

  const getHorseExpenses = (horseId) => {
    const horseExpenses = expenses.filter(e => e.horse_id === horseId);
    const entryFees = races.filter(r => r.horse_id === horseId).reduce((sum, r) => sum + (parseFloat(r.entry_fee) || 0), 0);
    return horseExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0) + entryFees;
  };

  const getBarrelPenaltyDescription = (barrelIssues) => {
    if (!barrelIssues || barrelIssues.length === 0) return null;

    const hasOffPattern = barrelIssues.includes('off-pattern');
    const barrels = barrelIssues.filter(b => b !== 'off-pattern').sort();

    if (hasOffPattern && barrels.length === 0) return 'Off Pattern';

    if (barrels.length === 3) return 'All Barrels';
    if (barrels.length === 2) {
      if (barrels.includes('1') && barrels.includes('2')) return '1st and 2nd Barrel';
      if (barrels.includes('1') && barrels.includes('3')) return '1st and 3rd Barrel';
      if (barrels.includes('2') && barrels.includes('3')) return '2nd and 3rd Barrel';
    }
    if (barrels.length === 1) {
      if (barrels[0] === '1') return '1st Barrel';
      if (barrels[0] === '2') return '2nd Barrel';
      if (barrels[0] === '3') return '3rd Barrel';
    }

    return null;
  };

  // Helper function to parse date strings as local dates (fixes timezone offset issue)
  const parseLocalDate = (dateString) => {
    if (!dateString) return new Date();
    const [year, month, day] = dateString.split('T')[0].split('-');
    return new Date(year, month - 1, day);
  };

  const horsesWithStats = useMemo(() =>
    horses.map(horse => ({
      ...horse,
      stats: getHorseStats(horse.id),
      totalExpenses: getHorseExpenses(horse.id)
    })).sort((a, b) => {
      // Sort by total races descending (most races first)
      const aRaces = a.stats?.totalRaces || 0;
      const bRaces = b.stats?.totalRaces || 0;
      return bRaces - aRaces;
    })
  , [horses, races, expenses]);

  // Filtered races based on search query
  const filteredRaces = useMemo(() => {
    let result = races;

    if (raceSearchQuery.trim()) {
      const query = raceSearchQuery.toLowerCase().trim();
      result = races.filter(race => {
        const horse = horses.find(h => h.id === race.horse_id);
        const horseName = (horse?.name || '').toLowerCase();
        const venue = (race.venue || '').toLowerCase();
        // Check rider name, or owner name if no rider specified
        const riderName = race.rider_name
          ? race.rider_name.toLowerCase()
          : `${user.first_name} ${user.last_name}`.toLowerCase();

        return horseName.includes(query) ||
               venue.includes(query) ||
               riderName.includes(query);
      });
    }

    // Sort by date (newest first)
    return [...result].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [races, horses, raceSearchQuery, user]);

  const getPerformanceChartData = (horseId, groupBy = 'venue') => {
    const horseRaces = races
      .filter(r => r.horse_id === horseId && r.time !== null)
      .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

    // Debug logging
    console.log(`[Chart Debug] Horse ID: ${horseId}, Total races with time: ${horseRaces.length}`);
    horseRaces.forEach((r, i) => {
      console.log(`  Race ${i+1}: date=${r.date}, time=${r.time}, rider=${r.rider_name || 'Owner'}, venue=${r.venue}`);
    });

    if (horseRaces.length === 0) return null;

    // Get owner's first name for chart legend
    const ownerFirstName = viewingAsUser ? viewingAsUser.first_name : user.first_name;

    const groupedData = {};
    horseRaces.forEach(race => {
      const groupKey = groupBy === 'rider'
        ? (race.rider_name || ownerFirstName)
        : race.venue;

      if (!groupedData[groupKey]) {
        groupedData[groupKey] = [];
      }
      groupedData[groupKey].push({
        id: race.id,
        date: race.date,
        time: parseFloat(race.time),
        no_time: race.no_time || false,
        venue: race.venue,
        barrel_issues: race.barrel_issues || []
      });
    });

    // Debug grouped data
    console.log(`[Chart Debug] Grouped by ${groupBy}:`, Object.keys(groupedData).map(k => `${k}: ${groupedData[k].length} races`));

    return groupedData;
  };

  // Get all unique venues from races (normalized and trimmed)
  const getAllVenues = () => {
    const venues = [...new Set(races.map(r => r.venue ? r.venue.trim() : ''))].filter(Boolean).sort();
    return venues;
  };

  // Calculate venue-specific stats for each horse
  const getVenueStats = (venue) => {
    if (!venue) return [];

    return horsesWithStats.map(horse => {
      const venueRaces = races.filter(r => r.horse_id === horse.id && r.venue && r.venue.trim() === venue.trim());
      const totalRaces = venueRaces.length;

      if (totalRaces === 0) return null;

      // A clean run has a valid time AND is not marked as no_time
      const cleanRuns = venueRaces.filter(r => r.time !== null && !r.no_time);
      const cleanPercent = totalRaces > 0 ? (cleanRuns.length / totalRaces * 100) : 0;

      const times = cleanRuns.map(r => parseFloat(r.time));
      const avgTime = times.length > 0 ? (times.reduce((sum, t) => sum + t, 0) / times.length) : null;
      const bestTime = times.length > 0 ? Math.min(...times) : null;

      // Calculate Last 3 Average - sort clean runs by date descending and take last 3
      const sortedCleanRuns = cleanRuns
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 3);
      const last3Times = sortedCleanRuns.map(r => parseFloat(r.time));
      const last3Avg = last3Times.length > 0 ? (last3Times.reduce((sum, t) => sum + t, 0) / last3Times.length) : null;

      return {
        horseId: horse.id,
        horseName: horse.name,
        totalRaces,
        cleanPercent: cleanPercent.toFixed(1),
        avgTime: avgTime ? avgTime.toFixed(2) : 'N/A',
        bestTime: bestTime ? bestTime.toFixed(2) : 'N/A',
        last3Avg: last3Avg ? last3Avg.toFixed(2) : 'N/A',
        last3Count: last3Times.length // How many of the last 3 we actually have
      };
    }).filter(Boolean);
  };

  const PerformanceChart = ({ horseId, compact = false }) => {
    const [chartGroupBy, setChartGroupBy] = useState('venue');
    const chartRef = useRef(null);

    const chartData = getPerformanceChartData(horseId, chartGroupBy);
    const allRaces = races.filter(r => r.horse_id === horseId && r.time !== null).sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

    // Check if there are multiple riders
    const ownerName = viewingAsUser ? viewingAsUser.first_name : user.first_name;
    const uniqueRiders = new Set(allRaces.map(r => r.rider_name || ownerName));
    const hasMultipleRiders = uniqueRiders.size > 1;

    // Responsive chart height
    const chartHeight = isMobile ? 250 : (compact ? 300 : 350);

    // Create Plotly chart
    useEffect(() => {
      if (!chartRef.current || !chartData || Object.keys(chartData).length === 0) return;

      const groups = Object.keys(chartData);

      // Create traces for each venue/rider
      const traces = groups.map((group, idx) => {
        const groupRaces = chartData[group];
        const color = CHART_COLORS[idx % CHART_COLORS.length];

        const traces = [];

        // Main line connecting all points
        if (groupRaces.length > 0) {
          traces.push({
            x: groupRaces.map(r => r.date),
            y: groupRaces.map(r => r.time),
            type: 'scatter',
            mode: groupRaces.length === 1 ? 'markers' : 'lines+markers',
            name: group,
            legendgroup: group,
            marker: {
              color: groupRaces.map(r => r.no_time ? color : color),
              size: groupRaces.map(r => r.no_time ? 12 : 10),
              symbol: groupRaces.map(r => r.no_time ? 'x' : 'circle'),
              line: { color: 'white', width: 2 }
            },
            line: {
              color: color,
              width: 2
            },
            customdata: groupRaces.map(r => r.no_time),
            hovertemplate: groupRaces.map(r =>
              r.no_time
                ? '<b>%{x|%b %d, %Y}</b><br>Time: %{y:.2f}s<br>' + `Venue: ${group}<br><span style="color:#dc2626">No Time Run</span><extra></extra>`
                : '<b>%{x|%b %d, %Y}</b><br>Time: %{y:.2f}s<br>' + `Venue: ${group}<extra></extra>`
            )
          });
        }

        return traces;
      }).flat();

      // Calculate Y-axis range with padding
      const raceTimes = allRaces.map(r => r.time);
      const dataMin = raceTimes.length > 0 ? Math.min(...raceTimes) : 0;
      const dataMax = raceTimes.length > 0 ? Math.max(...raceTimes) : 30;
      const padding = (dataMax - dataMin) * 0.15 || 1;

      const layout = {
        title: null,
        showlegend: !isMobile,
        legend: {
          orientation: 'h',
          yanchor: 'top',
          y: -0.15,
          xanchor: 'center',
          x: 0.5,
          font: { size: 11, color: '#374151' },
          bgcolor: 'rgba(255,255,255,0.9)',
          bordercolor: '#e5e7eb',
          borderwidth: 1,
          itemwidth: 30,
          tracegroupgap: 5
        },
        margin: {
          l: isMobile ? 50 : 60,
          r: isMobile ? 15 : 30,
          t: 20,
          b: isMobile ? 30 : 60
        },
        xaxis: {
          title: null,
          type: 'date',
          tickformat: isMobile ? '%m/%d' : '%b %d, %Y',
          tickfont: { size: isMobile ? 9 : 11, color: '#6b7280' },
          tickangle: isMobile ? -45 : 0,
          nticks: isMobile ? 5 : 8,
          gridcolor: '#e5e7eb',
          linecolor: '#e5e7eb',
          showgrid: true,
          zeroline: false
        },
        yaxis: {
          title: { text: 'Time (s)', font: { size: 11, color: '#6b7280' } },
          tickformat: '.2f',
          ticksuffix: 's',
          tickfont: { size: isMobile ? 10 : 11, color: '#6b7280' },
          gridcolor: '#e5e7eb',
          linecolor: '#e5e7eb',
          showgrid: true,
          zeroline: false,
          range: [Math.max(0, dataMin - padding), dataMax + padding],
          autorange: false
        },
        plot_bgcolor: 'white',
        paper_bgcolor: 'white',
        hovermode: 'closest',
        dragmode: 'zoom'
      };

      const config = {
        responsive: true,
        displayModeBar: true,
        modeBarButtonsToRemove: ['select2d', 'lasso2d', 'autoScale2d'],
        displaylogo: false
      };

      Plotly.newPlot(chartRef.current, traces, layout, config);

      // Cleanup on unmount
      const currentRef = chartRef.current;
      return () => {
        if (currentRef) {
          Plotly.purge(currentRef);
        }
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData, allRaces, compact, chartGroupBy]);

    if (!chartData || Object.keys(chartData).length === 0) {
      return <p style={{color: '#6b7280', fontStyle: 'italic', marginTop: '1rem'}}>No race data to display chart</p>;
    }

    return (
      <div className="chart-container" style={{background: 'white', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: '0.5rem', marginTop: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
        <div style={{display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: '1rem', gap: isMobile ? '0.75rem' : '0'}}>
          <h4 style={{fontWeight: '600', color: '#374151', margin: 0, fontSize: isMobile ? '1rem' : '1.125rem'}}>Performance Trend</h4>

          {hasMultipleRiders && (
            <div style={{display: 'flex', gap: '0.5rem', background: '#f3f4f6', padding: '0.25rem', borderRadius: '0.5rem'}}>
              <button
                onClick={() => setChartGroupBy('venue')}
                style={{
                  padding: isMobile ? '0.4rem 0.75rem' : '0.5rem 1rem',
                  background: chartGroupBy === 'venue' ? 'white' : 'transparent',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  fontWeight: chartGroupBy === 'venue' ? '600' : '500',
                  color: chartGroupBy === 'venue' ? '#ea580c' : '#6b7280',
                  boxShadow: chartGroupBy === 'venue' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                By Venue
              </button>
              <button
                onClick={() => setChartGroupBy('rider')}
                style={{
                  padding: isMobile ? '0.4rem 0.75rem' : '0.5rem 1rem',
                  background: chartGroupBy === 'rider' ? 'white' : 'transparent',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  fontWeight: chartGroupBy === 'rider' ? '600' : '500',
                  color: chartGroupBy === 'rider' ? '#7c3aed' : '#6b7280',
                  boxShadow: chartGroupBy === 'rider' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                By Rider
              </button>
            </div>
          )}
        </div>

        <div ref={chartRef} style={{ width: '100%', height: `${chartHeight}px` }} />

        {/* Custom scrollable legend for mobile */}
        {isMobile && chartData && Object.keys(chartData).length > 0 && (
          <div style={{
            marginTop: '0.75rem',
            maxHeight: '80px',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '0.5rem',
            background: '#f9fafb',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              justifyContent: 'center'
            }}>
              {Object.keys(chartData).map((group, idx) => (
                <div key={group} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.7rem',
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}>
                  <span style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: CHART_COLORS[idx % CHART_COLORS.length],
                    flexShrink: 0
                  }} />
                  <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {group}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  // Check if user needs to subscribe (but allow trialing users through)
  console.log("🚪 Subscription gate check:");
  console.log("  - user exists:", !!user);
  console.log("  - subscriptionStatus:", subscriptionStatus);
  console.log("  - trialEndDate:", trialEndDate);

  // If user is logged in but subscription status hasn't loaded yet, show loading
  if (user && subscriptionStatus === null) {
    return (
      <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb'}}>
        <div style={{textAlign: 'center'}}>
          <div style={{fontSize: '2rem', marginBottom: '1rem'}}>Loading...</div>
        </div>
      </div>
    );
  }

  // Show subscription gate if status is inactive or canceled (trial expired or never subscribed)
  const shouldShowGate = user && (subscriptionStatus === 'inactive' || subscriptionStatus === 'canceled' || subscriptionStatus === 'incomplete');

  console.log("  - will show gate:", shouldShowGate);
  if (shouldShowGate) {
    return (
      <div style={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: '2rem'}}>
        <div style={{maxWidth: '500px', width: '100%', background: 'white', borderRadius: '0.75rem', padding: '3rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', textAlign: 'center'}}>
          <img src="/logo.png" alt="SaddlePulse Logo" style={{height: '100px', width: 'auto', margin: '0 auto 1rem auto', display: 'block'}} />
          <h1 style={{fontSize: '1.875rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem'}}>
            Welcome to SaddlePulse!
          </h1>
          <p style={{fontSize: '1.125rem', color: '#6b7280', marginBottom: '2rem'}}>
            Start your 7-day free trial to begin using SaddlePulse.
          </p>

          <div style={{background: '#f3f4f6', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '2rem'}}>
            <div style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#ea580c', marginBottom: '0.5rem'}}>
              $4.99<span style={{fontSize: '1.25rem', color: '#6b7280'}}>/month</span>
            </div>
            <div style={{fontSize: '1rem', color: '#6b7280', fontWeight: '600', marginBottom: '1rem'}}>
              7-Day Free Trial, then $4.99/month
            </div>
            <ul style={{listStyle: 'none', padding: 0, textAlign: 'left'}}>
              {[
                'Unlimited horses',
                'Unlimited race tracking',
                'Barn management',
                'Calendar and events',
                'Expense tracking',
                'Performance analytics'
              ].map((feature, idx) => (
                <li key={idx} style={{display: 'flex', alignItems: 'center', padding: '0.5rem 0', color: '#374151'}}>
                  <span style={{color: '#10b981', marginRight: '0.75rem', fontSize: '1.25rem'}}>✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <button
            onClick={handleSubscribeClick}
            disabled={loading}
            style={{width: '100%', padding: '1rem', background: '#ea580c', color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.125rem', fontWeight: '700', cursor: 'pointer', marginBottom: '1rem'}}
          >
            {loading ? 'Loading...' : 'Start Free Trial'}
          </button>

          <p style={{fontSize: '0.75rem', color: '#9ca3af'}}>
            Cancel anytime. Billed monthly.
          </p>

          <button
            onClick={() => {
              localStorage.removeItem('token');
              setUser(null);
            }}
            style={{marginTop: '1rem', padding: '0.5rem', background: 'transparent', color: '#6b7280', border: 'none', cursor: 'pointer', fontSize: '0.875rem'}}
          >
            Logout
          </button>

          {error && (
            <div style={{marginTop: '1rem', padding: '1rem', background: '#fee2e2', border: '1px solid #ef4444', borderRadius: '0.375rem', color: '#991b1b', fontSize: '0.875rem'}}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (!user) {
    return (
      <div style={{minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: showPublicEvents ? '2rem' : '10vh', paddingBottom: '100px', paddingLeft: '1rem', paddingRight: '1rem', overflowY: 'auto'}}>
        <div className="auth-container" style={{width: '100%', maxWidth: '400px'}}>
          {/* Login Card with integrated branding */}
          <div style={{background: 'white', borderRadius: '1rem', padding: '2rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'}}>
            {/* Logo and branding inside the card */}
            <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
              <img src="/logo.png" alt="SaddlePulse Logo" style={{height: '70px', width: 'auto', margin: '0 auto 0.75rem auto', display: 'block'}} />
              <h1 style={{fontSize: '1.75rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.25rem'}}>SaddlePulse</h1>
              <p style={{color: '#6b7280', fontSize: '0.875rem'}}>Professional Barrel Racing Tracker</p>
            </div>
            {/* NEW: Prominent Demo CTA - Show on Login page */}
            {authView === 'login' && (
              <div style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '0.75rem', padding: '1.25rem', marginBottom: '1.5rem', textAlign: 'center', color: 'white'}}>
                <div style={{fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.5rem'}}>
                  See SaddlePulse in Action
                </div>
                <div style={{fontSize: '0.875rem', opacity: 0.9, marginBottom: '1rem'}}>
                  Explore the demo with sample data • No signup required
                </div>
                <button
                  onClick={handleDemoLogin}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: 'white',
                    color: '#059669',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.15)'
                  }}
                >
                  {loading ? 'Loading...' : 'Try Demo Now →'}
                </button>
              </div>
            )}

            {/* Pricing Banner - Only show on Register page */}
            {authView === 'register' && (
              <div style={{background: '#f0fdf4', border: '2px solid #10b981', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', textAlign: 'center'}}>
                <div style={{color: '#065f46', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.25rem'}}>
                  7-Day Free Trial • Cancel Anytime
                </div>
                <div style={{color: '#111827', fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.5rem'}}>
                  Then $4.99<span style={{fontSize: '1rem', fontWeight: '500', color: '#6b7280'}}>/month</span>
                </div>
                <div style={{color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem'}}>
                  Cancel anytime
                </div>
              </div>
            )}

            <div style={{display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: '1.5rem'}}>
              <button
                onClick={() => { setAuthView('login'); setError(''); setSuccess(''); }}
                style={{flex: 1, padding: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', color: authView === 'login' ? '#ea580c' : '#6b7280', borderBottom: authView === 'login' ? '2px solid #ea580c' : 'none', marginBottom: '-2px'}}
              >
                Login
              </button>
              <button
                onClick={() => { setAuthView('register'); setError(''); setSuccess(''); }}
                style={{flex: 1, padding: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', color: authView === 'register' ? '#ea580c' : '#6b7280', borderBottom: authView === 'register' ? '2px solid #ea580c' : 'none', marginBottom: '-2px'}}
              >
                Register
              </button>
            </div>

            {error && <div style={{padding: '0.75rem', background: '#fee2e2', color: '#991b1b', borderRadius: '0.375rem', marginBottom: '1rem', fontSize: '0.875rem'}}>{error}</div>}
            {success && <div style={{padding: '0.75rem', background: '#d1fae5', color: '#065f46', borderRadius: '0.375rem', marginBottom: '1rem', fontSize: '0.875rem'}}>{success}</div>}

            {authView === 'login' && (
              <div>
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                    placeholder="your@email.com"
                  />
                </div>
                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                    placeholder="••••••••"
                  />
                </div>
                <button
                  onClick={handleLogin}
                  disabled={loading}
                  style={{width: '100%', padding: '0.75rem', background: loading ? '#9ca3af' : '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '1rem'}}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
                <div style={{textAlign: 'center', marginTop: '1rem'}}>
                  <button
                    onClick={() => setAuthView('forgot-password')}
                    style={{background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.875rem', textDecoration: 'underline'}}
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* Value Proposition */}
                <div style={{marginTop: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb'}}>
                  <div style={{fontSize: '0.875rem', fontWeight: '600', color: '#111827', marginBottom: '0.75rem', textAlign: 'center'}}>
                    What You Get for $4.99/month:
                  </div>
                  <ul style={{listStyle: 'none', padding: 0, margin: 0, fontSize: '0.75rem', color: '#374151'}}>
                    {[
                      'Unlimited horses',
                      'Performance analytics',
                      'Expense tracking',
                      'Barn management',
                      'Calendar & events',
                      'Horse care tracking'
                    ].map((feature, idx) => (
                      <li key={idx} style={{display: 'flex', alignItems: 'center', padding: '0.25rem 0'}}>
                        <span style={{color: '#10b981', marginRight: '0.5rem', fontSize: '1rem'}}>✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {authView === 'register' && (
              <div>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                  <div>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>First Name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Last Name</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                    placeholder="your@email.com"
                  />
                </div>
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                    placeholder="••••••••"
                  />
                </div>
                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleRegister()}
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                    placeholder="••••••••"
                  />
                </div>
                <button
                  onClick={handleRegister}
                  disabled={loading}
                  style={{width: '100%', padding: '0.75rem', background: loading ? '#9ca3af' : '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '1rem'}}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            )}

            {authView === 'forgot-password' && (
              <div>
                <h3 style={{fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '1.5rem', textAlign: 'center'}}>
                  Reset Password
                </h3>

                {!generatedToken ? (
                  <>
                    <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem', textAlign: 'center'}}>
                      Enter your email to receive a password reset token
                    </p>
                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleForgotPassword()}
                        style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                        placeholder="your@email.com"
                      />
                    </div>
                    <button
                      onClick={handleForgotPassword}
                      disabled={loading}
                      style={{width: '100%', padding: '0.75rem', background: loading ? '#9ca3af' : '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '1rem', marginBottom: '1rem'}}
                    >
                      {loading ? 'Sending...' : 'Send Reset Token'}
                    </button>
                    <button
                      onClick={() => setAuthView('login')}
                      style={{width: '100%', padding: '0.75rem', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer', fontSize: '0.875rem'}}
                    >
                      Back to Login
                    </button>
                  </>  ) : (
                  <>
                    <div style={{marginBottom: '1.5rem', padding: '1.5rem', background: '#d1fae5', borderRadius: '0.5rem', border: '1px solid #059669', textAlign: 'center'}}>
                      <div style={{fontSize: '3rem', marginBottom: '1rem'}}>📧</div>
                      <p style={{fontSize: '1.125rem', fontWeight: '600', color: '#065f46', marginBottom: '0.75rem'}}>
                        Check Your Email
                      </p>
                      <p style={{fontSize: '0.875rem', color: '#047857', margin: 0, lineHeight: '1.5'}}>
                        We've sent a password reset link to your email address. Please check your inbox and click the link to reset your password.
                      </p>
                      <p style={{fontSize: '0.75rem', color: '#059669', marginTop: '1rem', margin: 0}}>
                        The link will expire in 1 hour.
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        setAuthView('login');
                        setGeneratedToken('');
                        setGeneratedResetLink('');
                      }}
                      style={{width: '100%', padding: '0.75rem', background: '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: 'pointer', fontSize: '1rem'}}
                    >
                      Back to Login
                    </button>
                  </>
                )}

                {!generatedToken && (
                  <p style={{marginTop: '1rem', fontSize: '0.75rem', color: '#6b7280', textAlign: 'center'}}>
                    Already have a token?{' '}
                    <button
                      onClick={() => setAuthView('reset-password')}
                      style={{background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline'}}
                    >
                      Reset Password
                    </button>
                  </p>
                )}
              </div>
            )}

            {authView === 'reset-password' && (
              <div>
                <h3 style={{fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '1.5rem', textAlign: 'center'}}>
                  Create New Password
                </h3>

                {success && success.includes('Password reset successful') ? (
                  <div style={{marginBottom: '1.5rem', padding: '1.5rem', background: '#d1fae5', borderRadius: '0.5rem', border: '1px solid #059669', textAlign: 'center'}}>
                    <div style={{fontSize: '3rem', marginBottom: '0.5rem'}}>✓</div>
                    <p style={{fontSize: '1rem', fontWeight: '600', color: '#065f46', marginBottom: '0.5rem'}}>
                      Password Reset Successful!
                    </p>
                    <p style={{fontSize: '0.875rem', color: '#065f46', margin: 0}}>
                      Redirecting to login page...
                    </p>
                  </div>
                ) : (
                  <>
                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                        Reset Token
                      </label>
                      <input
                        type="text"
                        value={resetToken}
                        onChange={(e) => setResetToken(e.target.value)}
                        style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', fontFamily: 'monospace', color: '#111827'}}
                        placeholder="Paste your reset token here"
                      />
                      <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>
                        Enter the token you received or from the forgot password page
                      </p>
                    </div>
                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                        New Password
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                        placeholder="Enter new password"
                      />
                      <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>
                        Must be at least 6 characters
                      </p>
                    </div>
                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleResetPassword()}
                        style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <button
                      onClick={handleResetPassword}
                      disabled={loading}
                      style={{width: '100%', padding: '0.75rem', background: loading ? '#9ca3af' : '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '1rem', marginBottom: '1rem'}}
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                    <button
                      onClick={() => {
                        setAuthView('login');
                        setResetToken('');
                        setNewPassword('');
                        setConfirmNewPassword('');
                      }}
                      style={{width: '100%', padding: '0.75rem', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer', fontSize: '0.875rem'}}
                    >
                      Back to Login
                    </button>

                    <p style={{marginTop: '1rem', fontSize: '0.75rem', color: '#6b7280', textAlign: 'center'}}>
                      Don't have a token?{' '}
                      <button
                        onClick={() => {
                          setAuthView('forgot-password');
                          setResetToken('');
                          setNewPassword('');
                          setConfirmNewPassword('');
                        }}
                        style={{background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline'}}
                      >
                        Request Reset Token
                      </button>
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Fixed Bottom Action Bar */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          padding: '0.75rem 1rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '0.75rem',
          flexWrap: 'wrap',
          zIndex: 100,
          boxShadow: '0 -4px 6px rgba(0,0,0,0.1)'
        }}>
          <button
            onClick={() => setShowDemoModal(true)}
            type="button"
            style={{display: 'inline-flex', alignItems: 'center', gap: '0.375rem', background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}
          >
            <Eye size={16} />
            <span>See Demo</span>
          </button>
          <button
            onClick={() => setShowPublicEvents(!showPublicEvents)}
            style={{display: 'inline-flex', alignItems: 'center', gap: '0.375rem', background: '#4f46e5', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}
          >
            <CalendarDays size={16} />
            <span>{showPublicEvents ? 'Hide Events' : 'Browse Events'}</span>
          </button>
          <button
            onClick={() => setShowHelpModal(true)}
            style={{display: 'inline-flex', alignItems: 'center', gap: '0.375rem', background: '#6b7280', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
          >
            <HelpCircle size={16} />
            <span>Help</span>
          </button>
        </div>

        {/* Public Events Section */}
        {showPublicEvents && (
          <div style={{width: '100%', maxWidth: '900px', marginTop: '2rem', padding: '0 1rem'}}>
            <div style={{background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem'}}>
                <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0}}>
                  Upcoming Barrel Racing Events
                </h2>
                <span style={{background: '#f0fdf4', color: '#065f46', padding: '0.375rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600'}}>
                  {publicEventsTotal} events found
                </span>
              </div>

              {/* Subscription Upsell for Location Filter */}
              <div style={{background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '2px solid #f59e0b', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1.5rem'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap'}}>
                  <MapPin size={24} style={{color: '#d97706'}} />
                  <div style={{flex: 1, minWidth: '200px'}}>
                    <div style={{fontWeight: '700', color: '#92400e', marginBottom: '0.25rem'}}>
                      Find Events Near You
                    </div>
                    <div style={{fontSize: '0.875rem', color: '#a16207'}}>
                      Subscribe to filter by location and distance from your zip code
                    </div>
                  </div>
                  <button
                    onClick={() => setAuthView('register')}
                    style={{background: '#ea580c', color: 'white', border: 'none', padding: '0.625rem 1.25rem', borderRadius: '0.5rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap'}}
                  >
                    Start Free Trial
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap'}}>
                <div style={{flex: 1, minWidth: '150px'}}>
                  <select
                    value={publicEventsStateFilter}
                    onChange={(e) => setPublicEventsStateFilter(e.target.value)}
                    style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem'}}
                  >
                    <option value="">All States</option>
                    {publicEventsFilters.states.map(s => (
                      <option key={s.state} value={s.state}>{s.state} ({s.count})</option>
                    ))}
                  </select>
                </div>
                <div style={{flex: 2, minWidth: '200px'}}>
                  <div style={{position: 'relative'}}>
                    <Search size={18} style={{position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af'}} />
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={publicEventsSearch}
                      onChange={(e) => setPublicEventsSearch(e.target.value)}
                      style={{width: '100%', padding: '0.5rem 0.5rem 0.5rem 2.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem'}}
                    />
                  </div>
                </div>
              </div>

              {/* Events List */}
              {publicEventsLoading ? (
                <div style={{textAlign: 'center', padding: '2rem', color: '#6b7280'}}>Loading events...</div>
              ) : publicEvents.length === 0 ? (
                <div style={{textAlign: 'center', padding: '2rem', color: '#6b7280'}}>No events found matching your filters</div>
              ) : (
                <div style={{display: 'grid', gap: '0.75rem'}}>
                  {publicEvents.map(event => (
                    <div
                      key={event.id}
                      style={{border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '1rem', background: '#fafafa', display: 'flex', flexDirection: 'column', gap: '0.5rem'}}
                    >
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap'}}>
                        <div style={{flex: 1}}>
                          <h3 style={{fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: 0}}>{event.name}</h3>
                          <div style={{fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem'}}>
                            <span style={{display: 'inline-flex', alignItems: 'center', gap: '0.25rem'}}>
                              <MapPin size={14} />
                              {event.city}, {event.state}
                            </span>
                            {event.location && (
                              <span style={{marginLeft: '0.75rem', color: '#9ca3af'}}>{event.location}</span>
                            )}
                          </div>
                        </div>
                        <div style={{textAlign: 'right'}}>
                          <div style={{fontSize: '0.875rem', fontWeight: '600', color: '#ea580c'}}>
                            {parseLocalDate(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                          {event.end_date && event.end_date !== event.event_date && (
                            <div style={{fontSize: '0.75rem', color: '#9ca3af'}}>
                              - {parseLocalDate(event.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center'}}>
                        {event.association && (
                          <span style={{background: '#e0e7ff', color: '#4338ca', padding: '0.25rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '500'}}>
                            {event.association}
                          </span>
                        )}
                        {event.event_type && (
                          <span style={{background: '#fef3c7', color: '#92400e', padding: '0.25rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '500'}}>
                            {event.event_type.replace('_', ' ')}
                          </span>
                        )}
                        {event.registration_url && (
                          <a
                            href={event.registration_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{marginLeft: 'auto', fontSize: '0.75rem', color: '#3b82f6', textDecoration: 'underline'}}
                          >
                            More Info →
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {publicEventsTotalPages > 1 && (
                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.5rem'}}>
                  <button
                    onClick={() => loadPublicEvents(publicEventsPage - 1)}
                    disabled={publicEventsPage <= 1}
                    style={{padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', background: publicEventsPage <= 1 ? '#f3f4f6' : 'white', color: publicEventsPage <= 1 ? '#9ca3af' : '#374151', cursor: publicEventsPage <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.875rem'}}
                  >
                    Previous
                  </button>
                  <span style={{fontSize: '0.875rem', color: '#6b7280'}}>
                    Page {publicEventsPage} of {publicEventsTotalPages}
                  </span>
                  <button
                    onClick={() => loadPublicEvents(publicEventsPage + 1)}
                    disabled={publicEventsPage >= publicEventsTotalPages}
                    style={{padding: '0.5rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', background: publicEventsPage >= publicEventsTotalPages ? '#f3f4f6' : 'white', color: publicEventsPage >= publicEventsTotalPages ? '#9ca3af' : '#374151', cursor: publicEventsPage >= publicEventsTotalPages ? 'not-allowed' : 'pointer', fontSize: '0.875rem'}}
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Sign Up CTA */}
              <div style={{marginTop: '1.5rem', padding: '1.25rem', background: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)', borderRadius: '0.75rem', textAlign: 'center'}}>
                <div style={{fontSize: '1.125rem', fontWeight: '700', color: 'white', marginBottom: '0.5rem'}}>
                  Want to save events and track your races?
                </div>
                <div style={{fontSize: '0.875rem', color: 'rgba(255,255,255,0.9)', marginBottom: '1rem'}}>
                  Start your 7-day free trial today • Then just $4.99/month
                </div>
                <button
                  onClick={() => setAuthView('register')}
                  style={{background: 'white', color: '#ea580c', border: 'none', padding: '0.75rem 2rem', borderRadius: '0.5rem', fontWeight: '700', cursor: 'pointer', fontSize: '1rem'}}
                >
                  Create Free Account
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Help/Support Modal for Login Page */}
      {showHelpModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem'}}>
          <div style={{background: 'white', borderRadius: '0.75rem', maxWidth: '500px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', maxHeight: '90vh', overflow: 'auto'}}>
            <div style={{padding: '2rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0}}>Help & Support</h2>
                <button
                  onClick={() => {
                    setShowHelpModal(false);
                    setHelpError('');
                    setHelpSuccess('');
                    setHelpEmail('');
                  }}
                  style={{background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.5rem'}}
                >
                  <X size={24} />
                </button>
              </div>

              {helpSuccess && (
                <div style={{background: '#d1fae5', border: '1px solid #10b981', color: '#065f46', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem'}}>
                  {helpSuccess}
                </div>
              )}

              {helpError && (
                <div style={{background: '#fee2e2', border: '1px solid #ef4444', color: '#991b1b', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem'}}>
                  {helpError}
                </div>
              )}

              {!user && (
                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem', color: '#374151'}}>
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={helpEmail}
                    onChange={(e) => setHelpEmail(e.target.value)}
                    placeholder="your@email.com"
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  />
                </div>
              )}

              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem', color: '#374151'}}>
                  Subject
                </label>
                <input
                  type="text"
                  value={helpSubject}
                  onChange={(e) => setHelpSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                />
              </div>

              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem', color: '#374151'}}>
                  Message
                </label>
                <textarea
                  value={helpMessage}
                  onChange={(e) => setHelpMessage(e.target.value)}
                  placeholder="Please describe your issue in detail..."
                  rows={6}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem', resize: 'vertical'}}
                />
              </div>

              <div style={{display: 'flex', gap: '0.75rem', justifyContent: 'flex-end'}}>
                <button
                  onClick={() => {
                    setShowHelpModal(false);
                    setHelpError('');
                    setHelpSuccess('');
                    setHelpEmail('');
                  }}
                  disabled={helpLoading}
                  style={{padding: '0.625rem 1.25rem', border: '1px solid #d1d5db', background: 'white', color: '#374151', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '500'}}
                >
                  Cancel
                </button>
                <button
                  onClick={handleHelpSubmit}
                  disabled={helpLoading}
                  style={{padding: '0.625rem 1.25rem', border: 'none', background: '#ea580c', color: 'white', borderRadius: '0.375rem', cursor: helpLoading ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: helpLoading ? 0.5 : 1}}
                >
                  {helpLoading ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo Email Capture Modal */}
      {showDemoEmailModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem'}}>
          <div style={{background: 'white', borderRadius: '1rem', maxWidth: '450px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)'}}>
            <div style={{padding: '2rem'}}>
              <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
                <img src="/logo.png" alt="SaddlePulse Logo" style={{height: '80px', width: 'auto', margin: '0 auto 1rem auto', display: 'block'}} />
                <h2 style={{fontSize: '1.75rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem'}}>
                  Welcome to SaddlePulse Demo
                </h2>
                <p style={{fontSize: '1rem', color: '#6b7280'}}>
                  See how SaddlePulse can help you track your barrel racing performance
                </p>
              </div>

              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Email (optional)
                </label>
                <input
                  type="email"
                  value={demoEmail}
                  onChange={(e) => setDemoEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleDemoEmailCapture()}
                  placeholder="your@email.com"
                  style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '1rem'}}
                />
                <p style={{fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem'}}>
                  Optional: We'll send you tips and updates about SaddlePulse
                </p>
              </div>

              <button
                onClick={handleDemoEmailCapture}
                disabled={demoEmailLoading}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: '#ea580c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  cursor: demoEmailLoading ? 'not-allowed' : 'pointer',
                  marginBottom: '0.75rem',
                  opacity: demoEmailLoading ? 0.5 : 1
                }}
              >
                {demoEmailLoading ? 'Loading...' : 'Try Demo Now'}
              </button>

              <button
                onClick={() => {
                  setShowDemoEmailModal(false);
                  handleDemoLogin();
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'transparent',
                  color: '#6b7280',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Skip and continue to demo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Demo Modal */}
      {showDemoModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem'}}>
          <div style={{background: 'white', borderRadius: '1rem', maxWidth: '600px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)', maxHeight: '90vh', overflow: 'auto'}}>
            <div style={{padding: '2rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
                <h2 style={{fontSize: '1.75rem', fontWeight: 'bold', color: '#1f2937', margin: 0}}>
                  Welcome to SaddlePulse
                </h2>
                <button
                  onClick={() => {
                    setShowDemoModal(false);
                    setDemoStep(0);
                  }}
                  style={{background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.5rem'}}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Feature Card */}
              <div style={{background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', padding: '2rem', borderRadius: '0.75rem', marginBottom: '2rem', color: 'white'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: 'white'}}>
                  {demoFeatures[demoStep].title}
                </h3>
                <p style={{fontSize: '1rem', marginBottom: '1.5rem', color: 'rgba(255,255,255,0.95)'}}>
                  {demoFeatures[demoStep].description}
                </p>
                <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                  {demoFeatures[demoStep].features.map((feature, idx) => (
                    <li key={idx} style={{display: 'flex', alignItems: 'center', marginBottom: '0.75rem', fontSize: '0.95rem'}}>
                      <span style={{marginRight: '0.75rem'}}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Progress Dots */}
              <div style={{display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '2rem'}}>
                {demoFeatures.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setDemoStep(idx)}
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      border: 'none',
                      background: idx === demoStep ? '#f97316' : '#d1d5db',
                      cursor: 'pointer',
                      transition: 'all 0.3s'
                    }}
                  />
                ))}
              </div>

              {/* Step Counter */}
              <div style={{textAlign: 'center', marginBottom: '2rem', color: '#6b7280', fontSize: '0.875rem'}}>
                Step {demoStep + 1} of {demoFeatures.length}
              </div>

              {/* Navigation Buttons */}
              <div style={{display: 'flex', gap: '0.75rem', justifyContent: 'space-between', marginBottom: '1.5rem'}}>
                <button
                  onClick={() => setDemoStep(Math.max(0, demoStep - 1))}
                  disabled={demoStep === 0}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#374151',
                    borderRadius: '0.5rem',
                    cursor: demoStep === 0 ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: demoStep === 0 ? 0.5 : 1,
                    flex: 1
                  }}
                >
                  Previous
                </button>
                {demoStep < demoFeatures.length - 1 ? (
                  <button
                    onClick={() => setDemoStep(Math.min(demoFeatures.length - 1, demoStep + 1))}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      background: '#f97316',
                      color: 'white',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontWeight: '500',
                      flex: 1
                    }}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowDemoModal(false);
                      setDemoStep(0);
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      background: '#10b981',
                      color: 'white',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontWeight: '500',
                      flex: 1
                    }}
                  >
                    Get Started
                  </button>
                )}
              </div>

              {/* Login with Demo Account */}
              <div style={{textAlign: 'center', padding: '1.5rem', background: '#f3f4f6', borderRadius: '0.5rem'}}>
                <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem'}}>
                  Want to explore first? Try the demo account:
                </p>
                <button
                  onClick={handleDemoLogin}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {loading ? 'Loading...' : '🐴 Try Demo Account (Read-Only)'}
                </button>
                <p style={{fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem', fontStyle: 'italic'}}>
                  View sample data without making any changes
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', background: '#f3f4f6'}}>
      {/* Header */}
      <header className="app-header" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '1.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
            <img src="/logo.png" alt="SaddlePulse Logo" style={{height: isMobile ? '40px' : '50px', width: 'auto'}} />
            <h1 style={{fontSize: '1.875rem', fontWeight: 'bold', margin: 0}}>SaddlePulse</h1>
          </div>
          <div className="user-info" style={{display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap'}}>
            <span className="user-welcome">Welcome, {user.first_name}!</span>
            <button
              onClick={() => setShowHelpModal(true)}
              className="touch-button"
              style={{display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
            >
              <HelpCircle size={16} />
              <span>Help</span>
            </button>
            {isDemoMode ? (
              <button
                onClick={() => {
                  handleLogout();
                  setAuthView('register');
                }}
                className="touch-button"
                style={{display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#10b981', color: 'white', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'}}
              >
                <UserPlus size={16} />
                <span>Sign Up - Start Free Trial</span>
              </button>
            ) : (
              <button
                onClick={handleLogout}
                className="touch-button"
                style={{display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          padding: '0.75rem',
          textAlign: 'center',
          fontWeight: '600',
          fontSize: '0.875rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Eye size={16} style={{display: 'inline', marginRight: '0.5rem', verticalAlign: 'text-bottom'}} />
          DEMO MODE - Read-Only Access | You can explore but cannot modify data
        </div>
      )}

      {/* Free Trial Banner */}
      {!isDemoMode && subscriptionStatus === 'trialing' && trialEndDate && (() => {
        const now = new Date();
        const endDate = new Date(trialEndDate);
        const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        const hoursRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60));

        return (
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            padding: '0.75rem',
            textAlign: 'center',
            fontWeight: '600',
            fontSize: '0.875rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {daysRemaining > 0 ? (
              <>FREE TRIAL - {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining | Subscribe for just $4.99/month</>
            ) : hoursRemaining > 0 ? (
              <>FREE TRIAL - {hoursRemaining} hour{hoursRemaining !== 1 ? 's' : ''} remaining | Subscribe for just $4.99/month</>
            ) : (
              <>FREE TRIAL EXPIRED - Subscribe for $4.99/month to continue</>
            )}
          </div>
        );
      })()}

      {/* Navigation */}
      <nav style={{background: 'white', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 10}}>
        <div
          className="nav-container"
          style={{maxWidth: '1200px', margin: '0 auto'}}
          onScroll={(e) => {
            if (!isMobile) return;
            const container = e.target;
            const scrollLeft = container.scrollLeft;
            const scrollWidth = container.scrollWidth;
            const clientWidth = container.clientWidth;
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 5;

            const arrow = document.getElementById('nav-scroll-arrow');
            if (arrow) {
              arrow.style.display = isAtEnd ? 'none' : 'block';
            }
          }}
        >
          {[
            { id: 'dashboard', label: 'Dashboard', icon: Trophy },
            ...(user?.barn_code ? [{ id: 'my-barn', label: 'My Barn', icon: Users }] : []),
            ...(user?.is_admin ? [{ id: 'admin-panel', label: 'Admin Panel', icon: Shield }] : []),
            { id: 'calendar', label: 'Calendar', icon: CalendarDays },
            { id: 'find-events', label: 'Find Events', icon: Search },
            { id: 'horse-care', label: 'Horse Care', icon: Pill },
            { id: 'race-list', label: 'Race List', icon: TrendingUp },
            { id: 'add-horse', label: 'Add Horse', icon: PlusCircle },
            { id: 'add-race', label: 'Add Race', icon: CalendarIcon },
            { id: 'add-expense', label: 'Add Expense', icon: DollarSign },
            { id: 'settings', label: 'Settings', icon: Settings }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => {
                setActiveView(id);
                if (id === 'my-barn') loadBarnData();
                if (id === 'admin-panel') loadAdminData();
                if (id === 'calendar') loadEvents();
                if (id === 'find-events') {
                  loadUserLocation();
                  loadExternalEvents();
                  loadExternalEventsFilters();
                  loadSavedExternalEvents();
                }
              }}
              className="nav-button touch-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                background: activeView === id ? '#ea580c' : 'transparent',
                color: activeView === id ? 'white' : '#6b7280',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>
        {/* Scroll indicator arrow - separate from scrolling container */}
        {isMobile && (
          <div
            id="nav-scroll-arrow"
            style={{
              position: 'absolute',
              top: '50%',
              right: '15px',
              transform: 'translateY(-50%)',
              fontSize: '24px',
              color: '#ea580c',
              fontWeight: 'bold',
              pointerEvents: 'none',
              zIndex: 102,
              textShadow: '0 0 10px rgba(255,255,255,0.8)',
              animation: 'scroll-hint 2s ease-in-out infinite'
            }}
          >
            →
          </div>
        )}
      </nav>

      {/* Fixed Toast Notifications */}
      {error && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '80px' : '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '1rem 1.5rem',
          background: '#fee2e2',
          color: '#991b1b',
          borderRadius: '0.5rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          zIndex: 1000,
          minWidth: '300px',
          maxWidth: '90vw',
          textAlign: 'center',
          fontWeight: '500',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '80px' : '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '1rem 1.5rem',
          background: '#d1fae5',
          color: '#065f46',
          borderRadius: '0.5rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          zIndex: 1000,
          minWidth: '300px',
          maxWidth: '90vw',
          textAlign: 'center',
          fontWeight: '500',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {success}
        </div>
      )}

      {/* Main Content */}
      <main className="main-content" style={{maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem'}}>
        {/* Admin Banner */}
        {viewingAsUser && (
          <div style={{background: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem'}}>
            <div>
              <p style={{fontSize: '1rem', fontWeight: '600', color: '#92400e', marginBottom: '0.25rem'}}>
                Admin Mode: Viewing as {viewingAsUser.first_name} {viewingAsUser.last_name}
              </p>
              <p style={{fontSize: '0.875rem', color: '#92400e', margin: 0}}>
                {viewingAsUser.email}
              </p>
            </div>
            <button
              onClick={exitAdminView}
              style={{padding: '0.5rem 1rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600'}}
            >
              Exit Admin View
            </button>
          </div>
        )}

        {/* Dashboard View */}
        {activeView === 'dashboard' && (
          <div>
            {/* Overall Summary Section */}
            {horsesWithStats.length > 0 && (
              <div style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}}>
                <h3 style={{fontSize: '1rem', fontWeight: '600', color: 'white', marginBottom: '1rem', opacity: 0.9, textAlign: 'center'}}>Portfolio Summary</h3>
                <div className="portfolio-summary-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem'}}>
                  <div className="portfolio-summary-item" style={{textAlign: 'center'}}>
                    <p style={{fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', margin: 0, marginBottom: '0.25rem'}}>Total Earnings</p>
                    <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'white', margin: 0}}>
                      ${horsesWithStats.reduce((sum, h) => sum + (h.stats?.totalPrizes || 0), 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="portfolio-summary-item" style={{textAlign: 'center'}}>
                    <p style={{fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', margin: 0, marginBottom: '0.25rem'}}>Total Expenses</p>
                    <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: 'white', margin: 0}}>
                      ${horsesWithStats.reduce((sum, h) => sum + (h.totalExpenses || 0), 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="portfolio-summary-item" style={{textAlign: 'center'}}>
                    <p style={{fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', margin: 0, marginBottom: '0.25rem'}}>Net Profit</p>
                    <p style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      margin: 0,
                      color: horsesWithStats.reduce((sum, h) => sum + (h.stats?.totalPrizes || 0) - (h.totalExpenses || 0), 0) >= 0 ? '#10b981' : '#fca5a5'
                    }}>
                      ${horsesWithStats.reduce((sum, h) => sum + (h.stats?.totalPrizes || 0) - (h.totalExpenses || 0), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Venue Analyzer Section */}
            {horsesWithStats.length > 0 && races.length > 0 && (
              <div style={{background: 'white', borderRadius: '1rem', padding: isMobile ? '1rem' : '1.5rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap'}}>
                  <h3 style={{fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0}}>Venue Performance</h3>
                  <select
                    value={selectedVenue}
                    onChange={(e) => setSelectedVenue(e.target.value)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      color: '#374151',
                      background: 'white',
                      cursor: 'pointer',
                      flex: '1',
                      minWidth: isMobile ? '100%' : '200px',
                      maxWidth: isMobile ? '100%' : '300px'
                    }}
                  >
                    <option value="">Select a venue...</option>
                    {getAllVenues().map(venue => (
                      <option key={venue} value={venue}>{venue}</option>
                    ))}
                  </select>
                </div>

                {selectedVenue && getVenueStats(selectedVenue).length > 0 ? (
                  isMobile ? (
                    // Mobile card layout
                    <div>
                      {getVenueStats(selectedVenue).map((stat) => (
                        <div key={stat.horseId} style={{
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          padding: '1rem',
                          marginBottom: '0.75rem'
                        }}>
                          <h4 style={{fontSize: '1rem', fontWeight: '700', color: '#111827', margin: '0 0 0.75rem 0'}}>
                            {stat.horseName}
                          </h4>
                          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.875rem'}}>
                            <div>
                              <span style={{color: '#6b7280', display: 'block', fontSize: '0.75rem'}}>Runs</span>
                              <span style={{fontWeight: '600', color: '#111827'}}>{stat.totalRaces}</span>
                            </div>
                            <div>
                              <span style={{color: '#6b7280', display: 'block', fontSize: '0.75rem'}}>Clean %</span>
                              <span style={{
                                fontWeight: '700',
                                color: parseFloat(stat.cleanPercent) >= 85 ? '#10b981' : parseFloat(stat.cleanPercent) >= 70 ? '#f59e0b' : '#ef4444'
                              }}>
                                {stat.cleanPercent}%
                              </span>
                            </div>
                            <div>
                              <span style={{color: '#6b7280', display: 'block', fontSize: '0.75rem'}}>Avg Time</span>
                              <span style={{fontWeight: '600', color: '#111827'}}>
                                {stat.avgTime === 'N/A' ? 'N/A' : `${stat.avgTime}s`}
                              </span>
                            </div>
                            <div>
                              <span style={{color: '#6b7280', display: 'block', fontSize: '0.75rem'}}>Best Time</span>
                              <span style={{fontWeight: '700', color: '#ea580c'}}>
                                {stat.bestTime === 'N/A' ? 'N/A' : `${stat.bestTime}s`}
                              </span>
                            </div>
                            <div style={{gridColumn: 'span 2'}}>
                              <span style={{color: '#6b7280', display: 'block', fontSize: '0.75rem'}}>Last 3 Avg</span>
                              <span style={{fontWeight: '700', color: '#7c3aed'}}>
                                {stat.last3Avg === 'N/A' ? 'N/A' : (
                                  <>
                                    {stat.last3Avg}s
                                    {stat.last3Count < 3 && <span style={{fontSize: '0.7rem', color: '#9ca3af', marginLeft: '0.25rem'}}>({stat.last3Count})</span>}
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Desktop table layout
                    <div style={{overflowX: 'auto'}}>
                      <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem'}}>
                      <thead>
                        <tr style={{borderBottom: '2px solid #e5e7eb'}}>
                          <th style={{textAlign: 'left', padding: '0.75rem', color: '#6b7280', fontWeight: '600'}}>Horse</th>
                          <th style={{textAlign: 'center', padding: '0.75rem', color: '#6b7280', fontWeight: '600'}}>Runs</th>
                          <th style={{textAlign: 'center', padding: '0.75rem', color: '#6b7280', fontWeight: '600'}}>Clean %</th>
                          <th style={{textAlign: 'center', padding: '0.75rem', color: '#6b7280', fontWeight: '600'}}>Avg Time</th>
                          <th style={{textAlign: 'center', padding: '0.75rem', color: '#6b7280', fontWeight: '600'}}>Best Time</th>
                          <th style={{textAlign: 'center', padding: '0.75rem', color: '#6b7280', fontWeight: '600'}}>Last 3 Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getVenueStats(selectedVenue).map((stat, idx) => (
                          <tr key={stat.horseId} style={{borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? 'white' : '#f9fafb'}}>
                            <td style={{padding: '0.75rem', fontWeight: '600', color: '#111827'}}>{stat.horseName}</td>
                            <td style={{padding: '0.75rem', textAlign: 'center', color: '#374151'}}>{stat.totalRaces}</td>
                            <td style={{
                              padding: '0.75rem',
                              textAlign: 'center',
                              color: parseFloat(stat.cleanPercent) >= 85 ? '#10b981' : parseFloat(stat.cleanPercent) >= 70 ? '#f59e0b' : '#ef4444',
                              fontWeight: '600'
                            }}>
                              {stat.cleanPercent}%
                            </td>
                            <td style={{padding: '0.75rem', textAlign: 'center', color: '#374151', fontWeight: '500'}}>
                              {stat.avgTime === 'N/A' ? 'N/A' : `${stat.avgTime}s`}
                            </td>
                            <td style={{padding: '0.75rem', textAlign: 'center', color: '#ea580c', fontWeight: '700'}}>
                              {stat.bestTime === 'N/A' ? 'N/A' : `${stat.bestTime}s`}
                            </td>
                            <td style={{
                              padding: '0.75rem',
                              textAlign: 'center',
                              color: '#7c3aed',
                              fontWeight: '600'
                            }}>
                              {stat.last3Avg === 'N/A' ? 'N/A' : (
                                <>
                                  {stat.last3Avg}s
                                  {stat.last3Count < 3 && (
                                    <span style={{fontSize: '0.7rem', color: '#9ca3af', marginLeft: '0.25rem'}}>
                                      ({stat.last3Count})
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )
                ) : selectedVenue ? (
                  <p style={{color: '#6b7280', fontStyle: 'italic', textAlign: 'center', margin: '1rem 0'}}>
                    No races recorded at {selectedVenue} yet.
                  </p>
                ) : (
                  <p style={{color: '#6b7280', fontStyle: 'italic', textAlign: 'center', margin: '1rem 0'}}>
                    Select a venue to see performance statistics for each horse.
                  </p>
                )}
              </div>
            )}

            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>Your Horses</h2>

            {horsesWithStats.length === 0 ? (
              <div style={{textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                <Trophy size={48} style={{color: '#d1d5db', margin: '0 auto 1rem'}} />
                <p style={{color: '#6b7280', fontSize: '1.125rem', marginBottom: '1rem'}}>No horses yet</p>
                <button
                  onClick={() => setActiveView('add-horse')}
                  style={{padding: '0.75rem 1.5rem', background: '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '1rem', fontWeight: '600'}}
                >
                  Add Your First Horse
                </button>
              </div>
            ) : (
              <div className="horse-cards-grid" data-count={horsesWithStats.length}>
                {horsesWithStats.map(horse => (
                  <div
                    key={horse.id}
                    className="horse-card"
                    onClick={() => setExpandedHorse(horse)}
                    style={{
                      background: 'white',
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      border: '2px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      ':hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 12px rgba(0,0,0,0.15)'
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                    }}
                  >
                    {/* Horse Header */}
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem'}}>
                      <div>
                        <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', margin: 0}}>{horse.name}</h3>
                        {getHorseAge(horse) && <p style={{color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0'}}>Age: {getHorseAge(horse)}</p>}
                      </div>
                      <Trophy size={24} style={{color: '#ea580c'}} />
                    </div>

                    {/* Stats Grid */}
                    {horse.stats ? (
                      <>
                        <div className="stats-grid" style={{marginBottom: '1rem'}}>
                          <div style={{background: '#f9fafb', padding: '0.75rem', borderRadius: '0.375rem'}}>
                            <p style={{color: '#6b7280', fontSize: '0.75rem', margin: 0}}>Total Races</p>
                            <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '0.25rem 0 0 0'}}>{horse.stats.totalRaces}</p>
                          </div>
                          <div style={{background: '#f9fafb', padding: '0.75rem', borderRadius: '0.375rem'}}>
                            <p style={{color: '#6b7280', fontSize: '0.75rem', margin: 0}}>Best Time</p>
                            <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#ea580c', margin: '0.25rem 0 0 0'}}>
                              {horse.stats.bestTime || '-'}s
                            </p>
                          </div>
                          <div style={{background: '#f9fafb', padding: '0.75rem', borderRadius: '0.375rem'}}>
                            <p style={{color: '#6b7280', fontSize: '0.75rem', margin: 0}}>Avg Time</p>
                            <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: '0.25rem 0 0 0'}}>
                              {horse.stats.avgTime || '-'}s
                            </p>
                          </div>
                          <div style={{background: '#f9fafb', padding: '0.75rem', borderRadius: '0.375rem'}}>
                            <p style={{color: '#6b7280', fontSize: '0.75rem', margin: 0}}>No-Time %</p>
                            <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: horse.stats.noTimePercent > 20 ? '#dc2626' : '#111827', margin: '0.25rem 0 0 0'}}>
                              {horse.stats.noTimePercent}%
                            </p>
                          </div>
                        </div>

                        {/* Barrel Issues */}
                        {horse.stats.noTimeCount > 0 && Object.keys(horse.stats.barrelIssueStats).length > 0 && (
                          <div style={{marginBottom: '1rem', padding: isMobile ? '0.5rem' : '0.75rem', background: '#fef3c7', borderRadius: '0.375rem', border: '1px solid #fbbf24'}}>
                            <p style={{fontSize: '0.7rem', fontWeight: '600', color: '#92400e', margin: '0 0 0.35rem 0'}}>Common Issues:</p>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.35rem'}}>
                              {Object.entries(horse.stats.barrelIssueStats).map(([issue, percent]) => (
                                <span key={issue} style={{fontSize: '0.7rem', padding: '0.2rem 0.4rem', background: 'white', borderRadius: '0.25rem', color: '#92400e'}}>
                                  {issue === 'off-pattern' ? 'Off-Pattern' : `Barrel ${issue}`}: {percent}%
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Financial Summary */}
                        <div style={{padding: '0.75rem', background: '#f0fdf4', borderRadius: '0.375rem', marginBottom: '1rem'}}>
                          <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem'}}>
                            <div>
                              <p style={{color: '#065f46', fontSize: '0.75rem', margin: 0}}>Total Prizes</p>
                              <p style={{fontSize: '1.125rem', fontWeight: 'bold', color: '#059669', margin: '0.25rem 0 0 0'}}>
                                ${horse.stats.totalPrizes.toFixed(2)}
                              </p>
                            </div>
                            <div style={{textAlign: 'right'}}>
                              <p style={{color: '#7c2d12', fontSize: '0.75rem', margin: 0}}>Total Expenses</p>
                              <p style={{fontSize: '1.125rem', fontWeight: 'bold', color: '#dc2626', margin: '0.25rem 0 0 0'}}>
                                ${horse.totalExpenses.toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <div style={{borderTop: '1px solid #bbf7d0', paddingTop: '0.75rem'}}>
                            <p style={{fontSize: '0.75rem', margin: '0 0 0.25rem 0', color: horse.stats.totalPrizes - horse.totalExpenses >= 0 ? '#065f46' : '#7c2d12', fontWeight: '600'}}>Net Profit</p>
                            <p style={{fontSize: '1.25rem', fontWeight: 'bold', margin: 0, color: horse.stats.totalPrizes - horse.totalExpenses >= 0 ? '#059669' : '#dc2626'}}>
                              ${(horse.stats.totalPrizes - horse.totalExpenses).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p style={{color: '#6b7280', fontSize: '0.875rem', textAlign: 'center', padding: '1rem'}}>No race data yet</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Expanded Horse Modal */}
            {expandedHorse && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.7)',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2rem',
                  animation: 'fadeIn 0.3s ease-in-out'
                }}
                onClick={() => setExpandedHorse(null)}
              >
                <div
                  style={{
                    background: 'white',
                    borderRadius: '1rem',
                    padding: '1.5rem',
                    maxWidth: '95vw',
                    width: '100%',
                    height: '95vh',
                    overflowY: 'auto',
                    position: 'relative',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                    animation: 'slideUp 0.3s ease-out'
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="expanded-horse-modal"
                >
                  {/* Close Button */}
                  <button
                    onClick={() => setExpandedHorse(null)}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      zIndex: 10
                    }}
                  >
                    ×
                  </button>

                  {/* Horse Header */}
                  <div style={{marginBottom: '2rem'}}>
                    <h2 style={{fontSize: '2rem', fontWeight: 'bold', color: '#111827', margin: 0}}>{expandedHorse.name}</h2>
                    {expandedHorse.age && <p style={{color: '#6b7280', fontSize: '1rem', margin: '0.5rem 0 0 0'}}>Age: {expandedHorse.age}</p>}
                  </div>

                  {/* Stats Grid - Compact */}
                  {expandedHorse.stats ? (
                    <>
                      <div className="stats-grid" style={{marginBottom: '1rem', gap: '0.5rem'}}>
                        <div style={{background: '#f9fafb', padding: '0.5rem', borderRadius: '0.375rem'}}>
                          <p style={{color: '#6b7280', fontSize: '0.625rem', margin: 0}}>Total Races</p>
                          <p style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', margin: '0.125rem 0 0 0'}}>{expandedHorse.stats.totalRaces}</p>
                        </div>
                        <div style={{background: '#f9fafb', padding: '0.5rem', borderRadius: '0.375rem'}}>
                          <p style={{color: '#6b7280', fontSize: '0.625rem', margin: 0}}>Best Time</p>
                          <p style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#ea580c', margin: '0.125rem 0 0 0'}}>
                            {expandedHorse.stats.bestTime || '-'}s
                          </p>
                        </div>
                        <div style={{background: '#f9fafb', padding: '0.5rem', borderRadius: '0.375rem'}}>
                          <p style={{color: '#6b7280', fontSize: '0.625rem', margin: 0}}>Avg Time</p>
                          <p style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', margin: '0.125rem 0 0 0'}}>
                            {expandedHorse.stats.avgTime || '-'}s
                          </p>
                        </div>
                        <div style={{background: '#f9fafb', padding: '0.5rem', borderRadius: '0.375rem'}}>
                          <p style={{color: '#6b7280', fontSize: '0.625rem', margin: 0}}>No-Time %</p>
                          <p style={{fontSize: '1.25rem', fontWeight: 'bold', color: expandedHorse.stats.noTimePercent > 20 ? '#dc2626' : '#111827', margin: '0.125rem 0 0 0'}}>
                            {expandedHorse.stats.noTimePercent}%
                          </p>
                        </div>
                      </div>

                      {/* Barrel Issues - Compact */}
                      {expandedHorse.stats.noTimeCount > 0 && Object.keys(expandedHorse.stats.barrelIssueStats).length > 0 && (
                        <div style={{marginBottom: '1rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '0.375rem', border: '1px solid #fbbf24'}}>
                          <p style={{fontSize: '0.75rem', fontWeight: '600', color: '#92400e', margin: '0 0 0.5rem 0'}}>Common Issues:</p>
                          <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                            {Object.entries(expandedHorse.stats.barrelIssueStats).map(([issue, percent]) => (
                              <span key={issue} style={{fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: 'white', borderRadius: '0.25rem', color: '#92400e', fontWeight: '500'}}>
                                {issue === 'off-pattern' ? 'Off-Pattern' : `Barrel ${issue}`}: {percent}%
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Rider Breakdown */}
                      {(() => {
                        const horseRaces = races.filter(r => r.horse_id === expandedHorse.id);
                        const ownerFirstName = viewingAsUser ? viewingAsUser.first_name : user.first_name;
                        const uniqueRiders = [...new Set(horseRaces.map(r => r.rider_name || ownerFirstName))];
                        if (uniqueRiders.length > 1) {
                          const riderStats = uniqueRiders.map(riderName => {
                            const riderRaces = horseRaces.filter(r => (r.rider_name || ownerFirstName) === riderName);
                            const riderValidRaces = riderRaces.filter(r => r.time && !r.no_time);
                            const riderNoTimeRaces = riderRaces.filter(r => r.no_time);
                            const riderBestTime = riderValidRaces.length > 0 ? Math.min(...riderValidRaces.map(r => parseFloat(r.time))).toFixed(3) : null;
                            const riderAvgTime = riderValidRaces.length > 0 ? (riderValidRaces.reduce((sum, r) => sum + parseFloat(r.time), 0) / riderValidRaces.length).toFixed(3) : null;
                            const riderNoTimePercent = riderRaces.length > 0 ? ((riderNoTimeRaces.length / riderRaces.length) * 100).toFixed(1) : 0;

                            return {
                              name: riderName,
                              totalRaces: riderRaces.length,
                              bestTime: riderBestTime,
                              avgTime: riderAvgTime,
                              noTimePercent: riderNoTimePercent
                            };
                          }).sort((a, b) => b.totalRaces - a.totalRaces);

                          return (
                            <div style={{marginBottom: '2rem', padding: '1.5rem', background: '#f5f3ff', borderRadius: '0.5rem', border: '1px solid #c4b5fd'}}>
                              <h3 style={{fontSize: '1.125rem', fontWeight: '600', color: '#5b21b6', margin: '0 0 1rem 0'}}>Performance by Rider</h3>
                              <div style={{display: 'grid', gap: '1rem'}}>
                                {riderStats.map(rider => (
                                  <div key={rider.name} style={{background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e9d5ff'}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem'}}>
                                      <h4 style={{fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0}}>
                                        <span style={{color: '#7c3aed'}}>{rider.name}</span>
                                      </h4>
                                      <span style={{fontSize: '0.875rem', color: '#6b7280', fontWeight: '500'}}>
                                        {rider.totalRaces} {rider.totalRaces === 1 ? 'race' : 'races'}
                                      </span>
                                    </div>
                                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem'}}>
                                      <div>
                                        <p style={{fontSize: '0.75rem', color: '#6b7280', margin: 0}}>Best Time</p>
                                        <p style={{fontSize: '1.125rem', fontWeight: '600', color: '#ea580c', margin: '0.25rem 0 0 0'}}>
                                          {rider.bestTime ? `${rider.bestTime}s` : '-'}
                                        </p>
                                      </div>
                                      <div>
                                        <p style={{fontSize: '0.75rem', color: '#6b7280', margin: 0}}>Avg Time</p>
                                        <p style={{fontSize: '1.125rem', fontWeight: '600', color: '#111827', margin: '0.25rem 0 0 0'}}>
                                          {rider.avgTime ? `${rider.avgTime}s` : '-'}
                                        </p>
                                      </div>
                                      <div>
                                        <p style={{fontSize: '0.75rem', color: '#6b7280', margin: 0}}>No-Time %</p>
                                        <p style={{fontSize: '1.125rem', fontWeight: '600', color: parseFloat(rider.noTimePercent) > 20 ? '#dc2626' : '#111827', margin: '0.25rem 0 0 0'}}>
                                          {rider.noTimePercent}%
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Performance Chart - Full Width */}
                      <PerformanceChart horseId={expandedHorse.id} />
                    </>
                  ) : (
                    <p style={{color: '#6b7280', fontSize: '1rem', textAlign: 'center', padding: '2rem'}}>No race data yet</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Race List View */}
        {activeView === 'race-list' && (
          <div>
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>All Races</h2>

            {/* Search Bar */}
            <div style={{marginBottom: '1.5rem'}}>
              <div style={{position: 'relative', maxWidth: '500px'}}>
                <input
                  type="text"
                  placeholder="Search by venue, horse, or rider..."
                  value={raceSearchQuery}
                  onChange={(e) => setRaceSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem 0.75rem 2.5rem',
                    fontSize: '1rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ea580c'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                <Search
                  size={20}
                  style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af'
                  }}
                />
                {raceSearchQuery && (
                  <button
                    onClick={() => setRaceSearchQuery('')}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>

            {races.length === 0 ? (
              <div style={{textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                <Calendar size={48} style={{color: '#d1d5db', margin: '0 auto 1rem'}} />
                <p style={{color: '#6b7280', fontSize: '1.125rem', marginBottom: '1rem'}}>No races recorded yet</p>
                <button
                  onClick={() => setActiveView('add-race')}
                  style={{padding: '0.75rem 1.5rem', background: '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '1rem', fontWeight: '600'}}
                >
                  Add Your First Race
                </button>
              </div>
            ) : filteredRaces.length === 0 ? (
              <div style={{textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                <Search size={48} style={{color: '#d1d5db', margin: '0 auto 1rem'}} />
                <p style={{color: '#6b7280', fontSize: '1.125rem', marginBottom: '1rem'}}>No races found matching "{raceSearchQuery}"</p>
                <button
                  onClick={() => setRaceSearchQuery('')}
                  style={{padding: '0.75rem 1.5rem', background: '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '1rem', fontWeight: '600'}}
                >
                  Clear Search
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="desktop-table" style={{background: 'white', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden'}}>
                  <div className="table-container">
                    <table style={{width: '100%', borderCollapse: 'collapse'}}>
                      <thead style={{background: '#f9fafb', borderBottom: '2px solid #e5e7eb'}}>
                        <tr>
                          <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Date</th>
                          <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Horse</th>
                          <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Rider</th>
                          <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Venue</th>
                          <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Time</th>
                          <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Placement</th>
                          <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Prize</th>
                          <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Entry Fee</th>
                          <th style={{padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Actions</th>
                        </tr>
                      </thead>
                    <tbody>
                      {filteredRaces.map((race, index) => {
                        const horse = horses.find(h => h.id === race.horse_id);
                        return (
                          <tr key={race.id} style={{borderBottom: index < filteredRaces.length - 1 ? '1px solid #e5e7eb' : 'none'}}>
                            <td style={{padding: '1rem', fontSize: '0.875rem', color: '#111827'}}>
                              {parseLocalDate(race.date).toLocaleDateString()}
                            </td>
                            <td style={{padding: '1rem', fontSize: '0.875rem', fontWeight: '500', color: '#111827'}}>
                              {horse?.name || 'Unknown'}
                            </td>
                            <td style={{padding: '1rem', fontSize: '0.875rem', color: '#111827'}}>
                              {race.rider_name ? (
                                <span style={{fontWeight: '500', color: '#7c3aed'}}>{race.rider_name}</span>
                              ) : (
                                <span style={{fontWeight: '500'}}>{viewingAsUser ? viewingAsUser.first_name : user.first_name}</span>
                              )}
                            </td>
                            <td style={{padding: '1rem', fontSize: '0.875rem', color: '#111827'}}>
                              {race.venue}
                            </td>
                            <td style={{padding: '1rem'}}>
                              {race.time ? (
                                <div>
                                  <span style={{fontSize: '0.875rem', fontWeight: '600', color: race.no_time ? '#dc2626' : '#ea580c'}}>
                                    {race.time}s
                                  </span>
                                  {race.no_time && race.barrel_issues && race.barrel_issues.length > 0 && (
                                    <div style={{fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                                      <span>🛢️</span>
                                      <span style={{fontWeight: '500'}}>{getBarrelPenaltyDescription(race.barrel_issues)}</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{fontSize: '0.875rem', color: '#6b7280'}}>-</span>
                              )}
                            </td>
                            <td style={{padding: '1rem', fontSize: '0.875rem', color: '#111827'}}>
                              {race.placements && race.placements.length > 0 ? (
                                <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                                  {race.placements.map((p, idx) => (
                                    <span key={idx} style={{fontSize: '0.75rem'}}>
                                      {[p.division, p.class, p.place ? `${p.place}${p.place === 1 ? 'st' : p.place === 2 ? 'nd' : p.place === 3 ? 'rd' : 'th'}` : null].filter(Boolean).join(' ') || '-'}
                                      {p.placement_type && p.placement_type !== 'Main' && <span style={{color: '#6b7280'}}> ({p.placement_type})</span>}
                                    </span>
                                  ))}
                                </div>
                              ) : race.placement || '-'}
                            </td>
                            <td style={{padding: '1rem', fontSize: '0.875rem', color: '#059669', fontWeight: '600'}}>
                              {race.placements && race.placements.length > 0 ? (
                                `$${race.placements.reduce((sum, p) => sum + (parseFloat(p.payout) || 0), 0).toFixed(2)}`
                              ) : race.prize_money ? `$${parseFloat(race.prize_money).toFixed(2)}` : '-'}
                            </td>
                            <td style={{padding: '1rem', fontSize: '0.875rem', color: '#dc2626', fontWeight: '600'}}>
                              {race.entry_fee ? `$${parseFloat(race.entry_fee).toFixed(2)}` : '-'}
                            </td>
                            <td style={{padding: '1rem', textAlign: 'right'}}>
                              <div style={{display: 'flex', gap: '0.5rem', justifyContent: 'flex-end'}}>
                                <button
                                  onClick={() => {
                                    startEditRace(race);
                                    setActiveView('add-race');
                                  }}
                                  style={{padding: '0.375rem 0.75rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => deleteRace(race.id)}
                                  style={{padding: '0.375rem 0.75rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="mobile-cards">
                {filteredRaces.map((race) => {
                  const horse = horses.find(h => h.id === race.horse_id);
                  return (
                    <div key={race.id} className="mobile-race-card">
                      <div className="mobile-race-card-row">
                        <span className="mobile-race-card-label">Date</span>
                        <span className="mobile-race-card-value">{parseLocalDate(race.date).toLocaleDateString()}</span>
                      </div>
                      <div className="mobile-race-card-row">
                        <span className="mobile-race-card-label">Horse</span>
                        <span className="mobile-race-card-value">{horse?.name || 'Unknown'}</span>
                      </div>
                      <div className="mobile-race-card-row">
                        <span className="mobile-race-card-label">Rider</span>
                        <span className="mobile-race-card-value">
                          {race.rider_name ? (
                            <span style={{fontWeight: '500', color: '#7c3aed'}}>{race.rider_name}</span>
                          ) : (
                            <span style={{fontWeight: '500'}}>{viewingAsUser ? viewingAsUser.first_name : user.first_name}</span>
                          )}
                        </span>
                      </div>
                      <div className="mobile-race-card-row">
                        <span className="mobile-race-card-label">Venue</span>
                        <span className="mobile-race-card-value">{race.venue}</span>
                      </div>
                      <div className="mobile-race-card-row">
                        <span className="mobile-race-card-label">Time</span>
                        <span className="mobile-race-card-value">
                          {race.time ? (
                            <div style={{textAlign: 'right'}}>
                              <div style={{fontWeight: '600', color: race.no_time ? '#dc2626' : '#ea580c'}}>
                                {race.time}s
                              </div>
                              {race.no_time && race.barrel_issues && race.barrel_issues.length > 0 && (
                                <div style={{fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem'}}>
                                  🛢️ {getBarrelPenaltyDescription(race.barrel_issues)}
                                </div>
                              )}
                            </div>
                          ) : '-'}
                        </span>
                      </div>
                      <div className="mobile-race-card-row">
                        <span className="mobile-race-card-label">Placement</span>
                        <span className="mobile-race-card-value">
                          {race.placements && race.placements.length > 0 ? (
                            race.placements.map((p, idx) => (
                              <div key={idx} style={{fontSize: '0.8rem'}}>
                                {[p.division, p.class, p.place ? `${p.place}${p.place === 1 ? 'st' : p.place === 2 ? 'nd' : p.place === 3 ? 'rd' : 'th'}` : null].filter(Boolean).join(' ') || '-'}
                                {p.placement_type && p.placement_type !== 'Main' && <span style={{color: '#6b7280'}}> ({p.placement_type})</span>}
                              </div>
                            ))
                          ) : race.placement || '-'}
                        </span>
                      </div>
                      <div className="mobile-race-card-row">
                        <span className="mobile-race-card-label">Prize</span>
                        <span className="mobile-race-card-value" style={{color: '#059669'}}>
                          {race.placements && race.placements.length > 0 ? (
                            `$${race.placements.reduce((sum, p) => sum + (parseFloat(p.payout) || 0), 0).toFixed(2)}`
                          ) : race.prize_money ? `$${parseFloat(race.prize_money).toFixed(2)}` : '-'}
                        </span>
                      </div>
                      <div className="mobile-race-card-row">
                        <span className="mobile-race-card-label">Entry Fee</span>
                        <span className="mobile-race-card-value" style={{color: '#dc2626'}}>
                          {race.entry_fee ? `$${parseFloat(race.entry_fee).toFixed(2)}` : '-'}
                        </span>
                      </div>
                      <div style={{marginTop: '1rem', display: 'flex', gap: '0.5rem'}}>
                        <button
                          onClick={() => {
                            startEditRace(race);
                            setActiveView('add-race');
                          }}
                          style={{flex: 1, padding: '0.75rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteRace(race.id)}
                          style={{flex: 1, padding: '0.75rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
            )}
          </div>
        )}

        {/* Add Horse Form */}
        {activeView === 'add-horse' && (
          <div className="form-container" style={{maxWidth: '600px', margin: '0 auto', background: 'white', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>Add New Horse</h2>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Horse Name <span style={{color: '#dc2626'}}>*</span>
              </label>
              <input
                type="text"
                value={horseName}
                onChange={(e) => setHorseName(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                placeholder="Enter horse name"
              />
            </div>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Registered Name
              </label>
              <input
                type="text"
                value={registeredName}
                onChange={(e) => setRegisteredName(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                placeholder="Official registered name (optional)"
              />
            </div>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Foaling Date</label>
              <input
                type="date"
                value={horseFoalingDate}
                onChange={(e) => setHorseFoalingDate(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
              />
              <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>Age will be calculated automatically</p>
            </div>

            <button
              onClick={addHorse}
              disabled={loading || !horseName.trim()}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: loading || !horseName.trim() ? '#9ca3af' : '#ea580c',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontWeight: '600',
                cursor: loading || !horseName.trim() ? 'not-allowed' : 'pointer',
                fontSize: '1rem'
              }}
            >
              {loading ? 'Adding Horse...' : 'Add Horse'}
            </button>
          </div>
        )}

        {/* Add Race Form */}
        {activeView === 'add-race' && (
          <div className="form-container" style={{maxWidth: '600px', margin: '0 auto', background: 'white', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>
              {editingRace ? 'Edit Race' : 'Add New Race'}
            </h2>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Horse <span style={{color: '#dc2626'}}>*</span>
              </label>
              <select
                value={selectedHorse || ''}
                onChange={(e) => setSelectedHorse(e.target.value ? parseInt(e.target.value) : null)}
                disabled={editingRace}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
              >
                <option value="">Select a horse</option>
                {horses.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </div>

            <div className="form-grid-2" style={{marginBottom: '1rem'}}>
              <div>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Date <span style={{color: '#dc2626'}}>*</span>
                </label>
                <input
                  type="date"
                  value={editingRace ? editRaceDate : raceDate}
                  onChange={(e) => editingRace ? setEditRaceDate(e.target.value) : setRaceDate(e.target.value)}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                />
              </div>
              <div>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Venue <span style={{color: '#dc2626'}}>*</span>
                </label>
                <input
                  type="text"
                  value={editingRace ? editRaceVenue : raceVenue}
                  onChange={(e) => editingRace ? setEditRaceVenue(e.target.value) : setRaceVenue(e.target.value)}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  placeholder="Venue name"
                />
              </div>
            </div>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Time (seconds) {(editingRace ? !editNoTime : !noTime) && <span style={{color: '#dc2626'}}>*</span>}
                {(editingRace ? editNoTime : noTime) && <span style={{color: '#6b7280', fontWeight: '400'}}> (optional - for reference)</span>}
              </label>
              <input
                type="number"
                step="0.001"
                value={editingRace ? editRaceTime : raceTime}
                onChange={(e) => editingRace ? setEditRaceTime(e.target.value) : setRaceTime(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                placeholder="Enter time (e.g., 15.234)"
              />
            </div>

            <div style={{marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '0.375rem'}}>
              <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
                <input
                  type="checkbox"
                  checked={editingRace ? editNoTime : noTime}
                  onChange={(e) => {
                    if (editingRace) {
                      setEditNoTime(e.target.checked);
                    } else {
                      setNoTime(e.target.checked);
                    }
                  }}
                  style={{width: '1rem', height: '1rem'}}
                />
                <span style={{fontSize: '0.875rem', fontWeight: '500', color: '#374151'}}>No Time (barrel hit or off-pattern)</span>
              </label>
            </div>

            {(editingRace ? editNoTime : noTime) && (
              <div style={{marginBottom: '1rem', padding: '1rem', background: '#fef3c7', borderRadius: '0.375rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#92400e', marginBottom: '0.5rem'}}>
                  What happened? <span style={{color: '#dc2626'}}>*</span>
                </label>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                  {['1', '2', '3', 'off-pattern'].map(issue => (
                    <label key={issue} style={{display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', padding: '0.5rem', background: 'white', borderRadius: '0.25rem'}}>
                      <input
                        type="checkbox"
                        checked={editingRace ? editBarrelIssues.includes(issue) : barrelIssues.includes(issue)}
                        onChange={(e) => {
                          const issues = editingRace ? editBarrelIssues : barrelIssues;
                          const setIssues = editingRace ? setEditBarrelIssues : setBarrelIssues;
                          if (e.target.checked) {
                            setIssues([...issues, issue]);
                          } else {
                            setIssues(issues.filter(i => i !== issue));
                          }
                        }}
                        style={{width: '1rem', height: '1rem'}}
                      />
                      <span style={{fontSize: '0.875rem', color: '#92400e'}}>
                        {issue === 'off-pattern' ? 'Off Pattern' : `Barrel ${issue}`}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Entry Fee</label>
              <input
                type="number"
                step="0.01"
                value={editingRace ? editEntryFee : entryFee}
                onChange={(e) => editingRace ? setEditEntryFee(e.target.value) : setEntryFee(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                placeholder="Total entry fees for this race"
              />
            </div>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Rider Name
              </label>
              <input
                type="text"
                value={editingRace ? editRiderName : riderName}
                onChange={(e) => editingRace ? setEditRiderName(e.target.value) : setRiderName(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                placeholder="Leave blank if you are the rider"
              />
              <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>Optional - defaults to horse owner if left blank</p>
            </div>

            <div style={{marginBottom: '1.5rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Notes</label>
              <textarea
                value={editingRace ? editNotes : notes}
                onChange={(e) => editingRace ? setEditNotes(e.target.value) : setNotes(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem', minHeight: '80px', fontFamily: 'inherit'}}
                placeholder="Additional notes..."
              />
            </div>

            {/* Placements Section */}
            <div style={{marginBottom: '1.5rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', overflow: 'hidden'}}>
              <div style={{
                padding: '1rem',
                background: '#f9fafb',
                borderBottom: '1px solid #d1d5db',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151'
              }}>
                Placements & Winnings {(editingRace ? editPlacements : placements).length > 0 && `(${(editingRace ? editPlacements : placements).length})`}
              </div>

              <div style={{padding: '1rem'}}>
                <p style={{fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem'}}>
                  Add your placements and winnings for this race. You can add multiple if you placed in different divisions (e.g., Open 1D-1st, Youth 2D-3rd, Sidepot).
                </p>

                  {(editingRace ? editPlacements : placements).map((p, index) => {
                    const updatePlacement = (field, value) => {
                      const currentPlacements = editingRace ? editPlacements : placements;
                      const updated = [...currentPlacements];
                      updated[index] = {...updated[index], [field]: value};
                      if (editingRace) {
                        setEditPlacements(updated);
                      } else {
                        setPlacements(updated);
                      }
                    };
                    const deletePlacement = () => {
                      const currentPlacements = editingRace ? editPlacements : placements;
                      if (editingRace) {
                        setEditPlacements(currentPlacements.filter((_, i) => i !== index));
                      } else {
                        setPlacements(currentPlacements.filter((_, i) => i !== index));
                      }
                    };
                    return (
                    <div key={index} style={{marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem', position: 'relative'}}>
                      <button
                        type="button"
                        onClick={deletePlacement}
                        style={{
                          position: 'absolute',
                          top: '0.5rem',
                          right: '0.5rem',
                          padding: '0.25rem 0.5rem',
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}
                      >
                        Delete
                      </button>

                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', marginBottom: '0.5rem', marginTop: '1.5rem'}}>
                        <div>
                          <label style={{display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem'}}>Class</label>
                          {['Open', 'Youth', 'Senior', '', null].includes(p.division) ? (
                            <select
                              value={p.division || ''}
                              onChange={(e) => {
                                if (e.target.value === '__other__') {
                                  updatePlacement('division', ' ');
                                } else {
                                  updatePlacement('division', e.target.value || null);
                                }
                              }}
                              style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.875rem'}}
                            >
                              <option value="">-- Select --</option>
                              <option value="Open">Open</option>
                              <option value="Youth">Youth</option>
                              <option value="Senior">Senior</option>
                              <option value="__other__">Other...</option>
                            </select>
                          ) : (
                            <div style={{display: 'flex', gap: '0.25rem', width: '100%'}}>
                              <input
                                type="text"
                                value={p.division === ' ' ? '' : (p.division || '')}
                                onChange={(e) => updatePlacement('division', e.target.value || ' ')}
                                style={{width: 'calc(100% - 32px)', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.875rem'}}
                                placeholder="Class name"
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={() => updatePlacement('division', null)}
                                style={{width: '28px', padding: '0.5rem', background: '#e5e7eb', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.75rem', flexShrink: 0}}
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                        <div>
                          <label style={{display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem'}}>Division</label>
                          <select
                            value={p.class || ''}
                            onChange={(e) => updatePlacement('class', e.target.value || null)}
                            style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.875rem'}}
                          >
                            <option value="">-- Select --</option>
                            <option value="1D">1D</option>
                            <option value="2D">2D</option>
                            <option value="3D">3D</option>
                            <option value="4D">4D</option>
                            <option value="5D">5D</option>
                          </select>
                        </div>
                      </div>

                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem'}}>
                        <div>
                          <label style={{display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem'}}>Place</label>
                          <input
                            type="number"
                            value={p.place || ''}
                            onChange={(e) => updatePlacement('place', e.target.value ? parseInt(e.target.value) : null)}
                            style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.875rem'}}
                            placeholder="1, 2, 3..."
                          />
                        </div>
                        <div>
                          <label style={{display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem'}}>Payout ($)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={p.payout || ''}
                            onChange={(e) => updatePlacement('payout', e.target.value ? parseFloat(e.target.value) : null)}
                            style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.875rem'}}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label style={{display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem'}}>Type</label>
                          <select
                            value={p.placement_type || 'Main'}
                            onChange={(e) => updatePlacement('placement_type', e.target.value)}
                            style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.25rem', fontSize: '0.875rem'}}
                          >
                            <option value="Main">Main</option>
                            <option value="Sidepot">Sidepot</option>
                            <option value="Rollover">Rollover</option>
                            <option value="Average">Average</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => {
                      const currentPlacements = editingRace ? editPlacements : placements;
                      const newPlacement = {
                        division: null,
                        class: null,
                        place: null,
                        payout: null,
                        placement_type: 'Main'
                      };
                      if (editingRace) {
                        setEditPlacements([...currentPlacements, newPlacement]);
                      } else {
                        setPlacements([...currentPlacements, newPlacement]);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    + Add Placement
                  </button>
              </div>
            </div>

            <div style={{display: 'flex', gap: '1rem'}}>
              {editingRace ? (
                <>
                  <button
                    onClick={() => updateRace(editingRace)}
                    disabled={loading}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: loading ? '#9ca3af' : '#ea580c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    {loading ? 'Updating...' : 'Update Race'}
                  </button>
                  <button
                    onClick={cancelEditRace}
                    disabled={loading}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={addRace}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: loading ? '#9ca3af' : '#ea580c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  {loading ? 'Adding Race...' : 'Add Race'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Add Expense Form */}
        {activeView === 'add-expense' && (
          <div className="form-container" style={{maxWidth: '600px', margin: '0 auto', background: 'white', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>Add New Expense</h2>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Horse <span style={{color: '#dc2626'}}>*</span>
              </label>
              <select
                value={selectedHorse || ''}
                onChange={(e) => setSelectedHorse(e.target.value ? parseInt(e.target.value) : null)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
              >
                <option value="">Select a horse</option>
                {horses.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </div>

            <div className="form-grid-2" style={{marginBottom: '1rem'}}>
              <div>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Date <span style={{color: '#dc2626'}}>*</span>
                </label>
                <input
                  type="date"
                  value={expenseDate}
                  onChange={(e) => setExpenseDate(e.target.value)}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                />
              </div>
              <div>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Amount <span style={{color: '#dc2626'}}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Category <span style={{color: '#dc2626'}}>*</span>
              </label>
              <select
                value={expenseCategory}
                onChange={(e) => setExpenseCategory(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
              >
                <option value="">Select category</option>
                <option value="Veterinary">Veterinary</option>
                <option value="Farrier">Farrier</option>
                <option value="Feed">Feed</option>
                <option value="Supplies">Supplies</option>
                <option value="Training">Training</option>
                <option value="Equipment">Equipment</option>
                <option value="Transportation">Transportation</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div style={{marginBottom: '1.5rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>Description</label>
              <textarea
                value={expenseDescription}
                onChange={(e) => setExpenseDescription(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem', minHeight: '80px', fontFamily: 'inherit'}}
                placeholder="Expense details..."
              />
            </div>

            <button
              onClick={addExpense}
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: loading ? '#9ca3af' : '#ea580c',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem'
              }}
            >
              {loading ? 'Adding Expense...' : 'Add Expense'}
            </button>
          </div>
        )}

        {/* Admin Panel View */}
        {activeView === 'admin-panel' && user?.is_admin && (
          <div>
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>
              Admin Panel
            </h2>

            {/* System Stats */}
            {adminStats && (
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
                <div style={{background: 'white', padding: '1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                  <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Total Users</p>
                  <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#111827'}}>{adminStats.totalUsers}</p>
                </div>
                <div style={{background: 'white', padding: '1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                  <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Total Horses</p>
                  <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#111827'}}>{adminStats.totalHorses}</p>
                </div>
                <div style={{background: 'white', padding: '1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                  <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Total Races</p>
                  <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#111827'}}>{adminStats.totalRaces}</p>
                </div>
                <div style={{background: 'white', padding: '1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                  <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Active Trainers</p>
                  <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#111827'}}>{adminStats.activeTrainers}</p>
                </div>
              </div>
            )}

            {/* User Search */}
            <div style={{marginBottom: '1.5rem'}}>
              <div style={{position: 'relative', maxWidth: '400px'}}>
                <Search size={20} style={{position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280'}} />
                <input
                  type="text"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  placeholder="Search users by name or email..."
                  style={{width: '100%', padding: '0.75rem 0.75rem 0.75rem 2.5rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '1rem'}}
                />
              </div>
            </div>

            {/* Users List */}
            <div style={{background: 'white', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden'}}>
              <div className="desktop-table">
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead style={{background: '#f9fafb', borderBottom: '2px solid #e5e7eb'}}>
                    <tr>
                      <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>User</th>
                      <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Email</th>
                      <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Horses</th>
                      <th style={{padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Type</th>
                      <th style={{padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers
                      .filter(u => {
                        if (!userSearchQuery) return true;
                        const query = userSearchQuery.toLowerCase();
                        return u.first_name.toLowerCase().includes(query) ||
                               u.last_name.toLowerCase().includes(query) ||
                               u.email.toLowerCase().includes(query);
                      })
                      .map((user, index) => (
                      <tr key={user.id} style={{borderBottom: index < allUsers.length - 1 ? '1px solid #e5e7eb' : 'none'}}>
                        <td style={{padding: '1rem', fontSize: '0.875rem', fontWeight: '500', color: '#111827'}}>
                          {user.first_name} {user.last_name}
                          {user.is_admin && <span style={{marginLeft: '0.5rem', padding: '0.25rem 0.5rem', background: '#fef3c7', color: '#92400e', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '600'}}>ADMIN</span>}
                        </td>
                        <td style={{padding: '1rem', fontSize: '0.875rem', color: '#6b7280'}}>{user.email}</td>
                        <td style={{padding: '1rem', fontSize: '0.875rem', color: '#111827'}}>{user.horse_count}</td>
                        <td style={{padding: '1rem', fontSize: '0.875rem'}}>
                          {user.barn_code && <span style={{padding: '0.25rem 0.5rem', background: '#dbeafe', color: '#1e40af', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500', marginRight: '0.25rem'}}>Trainer</span>}
                          {user.trainer_barn_code && <span style={{padding: '0.25rem 0.5rem', background: '#f3e8ff', color: '#6b21a8', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500'}}>Client</span>}
                        </td>
                        <td style={{padding: '1rem', textAlign: 'right'}}>
                          <button
                            onClick={() => viewAsUser(user.id)}
                            style={{padding: '0.5rem 1rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
                          >
                            View Dashboard
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card view */}
              <div className="mobile-cards">
                {allUsers
                  .filter(u => {
                    if (!userSearchQuery) return true;
                    const query = userSearchQuery.toLowerCase();
                    return u.first_name.toLowerCase().includes(query) ||
                           u.last_name.toLowerCase().includes(query) ||
                           u.email.toLowerCase().includes(query);
                  })
                  .map((user) => (
                  <div key={user.id} style={{background: 'white', padding: '1rem', marginBottom: '1rem', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                    <div style={{marginBottom: '0.75rem'}}>
                      <p style={{fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem'}}>
                        {user.first_name} {user.last_name}
                        {user.is_admin && <span style={{marginLeft: '0.5rem', padding: '0.25rem 0.5rem', background: '#fef3c7', color: '#92400e', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '600'}}>ADMIN</span>}
                      </p>
                      <p style={{fontSize: '0.875rem', color: '#6b7280', margin: 0}}>{user.email}</p>
                    </div>
                    <div style={{display: 'flex', gap: '0.5rem', marginBottom: '0.75rem'}}>
                      <span style={{fontSize: '0.75rem', color: '#6b7280'}}>Horses: {user.horse_count}</span>
                      {user.barn_code && <span style={{padding: '0.25rem 0.5rem', background: '#dbeafe', color: '#1e40af', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500'}}>Trainer</span>}
                      {user.trainer_barn_code && <span style={{padding: '0.25rem 0.5rem', background: '#f3e8ff', color: '#6b21a8', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: '500'}}>Client</span>}
                    </div>
                    <button
                      onClick={() => viewAsUser(user.id)}
                      style={{width: '100%', padding: '0.5rem 1rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
                    >
                      View Dashboard
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}


        {/* Calendar View */}
        {activeView === 'calendar' && (
          <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'}}>
              <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', margin: 0}}>
                Calendar
              </h2>
              <button
                onClick={() => setShowAddEvent(true)}
                style={{padding: '0.75rem 1.5rem', background: '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem'}}
              >
                <PlusCircle size={18} />
                Add Event
              </button>
            </div>

            {/* Filters */}
            <div style={{marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                style={{padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem'}}
              >
                <option value="all">All Events</option>
                {user?.barn_code && <option value="barn">Barn Events Only</option>}
                <option value="personal">Personal Events Only</option>
              </select>

              <select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                style={{padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem'}}
              >
                <option value="all">All Types</option>
                <option value="competition">Competitions</option>
                <option value="practice">Practice</option>
                <option value="vet">Vet Appointments</option>
                <option value="farrier">Farrier</option>
                <option value="training">Training</option>
                <option value="other">Other</option>
              </select>

              <select
                value={calendarHorseFilter}
                onChange={(e) => setCalendarHorseFilter(e.target.value)}
                style={{padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem'}}
              >
                <option value="all">All Horses</option>
                {horses.map(horse => (
                  <option key={horse.id} value={horse.id}>{horse.name}</option>
                ))}
              </select>
            </div>

            {/* FullCalendar */}
            <div style={{background: 'white', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '2rem'}}>
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                events={(() => {
                  // Filter events based on current filters
                  const filteredEvents = events.filter(event => {
                    if (eventFilter === 'barn' && event.visibility !== 'barn') return false;
                    if (eventFilter === 'personal' && event.created_by !== user.id) return false;
                    if (eventTypeFilter !== 'all' && event.event_type !== eventTypeFilter) return false;
                    if (calendarHorseFilter !== 'all' && event.horse_id !== parseInt(calendarHorseFilter)) return false;
                    return true;
                  });

                  // Convert to FullCalendar format
                  return filteredEvents.map(event => ({
                    id: event.id.toString(),
                    title: event.title,
                    start: event.start_date,
                    end: event.end_date || event.start_date,
                    allDay: event.all_day,
                    backgroundColor: getEventColor(event.event_type),
                    borderColor: getEventColor(event.event_type),
                    extendedProps: {
                      event_type: event.event_type,
                      location: event.location,
                      description: event.description,
                      horse_name: event.horse_name,
                      visibility: event.visibility,
                      creator_name: event.creator_name,
                      notes: event.notes,
                      horse_id: event.horse_id
                    }
                  }));
                })()}
                dateClick={(info) => {
                  // Pre-fill the date when clicking on a calendar date
                  setEventStartDate(info.dateStr);
                  setShowAddEvent(true);
                }}
                eventClick={(info) => {
                  // Find the original event and open modal
                  const clickedEvent = events.find(e => e.id.toString() === info.event.id);
                  if (clickedEvent) {
                    openEventModal(clickedEvent);
                  }
                }}
                height="auto"
                eventDisplay="block"
                dayMaxEvents={3}
                moreLinkClick="popover"
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
              />
            </div>

            {/* Events for selected date OR next upcoming event */}
            {(() => {
              // If no date selected, show next upcoming event
              if (!selectedDate) {
                const now = new Date();
                console.log('Looking for upcoming events. Total events:', events.length);
                console.log('Current filters - eventFilter:', eventFilter, 'eventTypeFilter:', eventTypeFilter);
                const upcomingEvents = events
                  .filter(event => {
                    const eventStart = new Date(event.start_date);
                    // Apply filters
                    if (eventFilter === 'barn' && event.visibility !== 'barn') return false;
                    if (eventFilter === 'personal' && event.created_by !== user.id) return false;
                    if (eventTypeFilter !== 'all' && event.event_type !== eventTypeFilter) return false;
                    const isFuture = eventStart >= now;
                    console.log('Event:', event.title, 'Date:', eventStart, 'Future?', isFuture);
                    return isFuture;
                  })
                  .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                console.log('Upcoming events found:', upcomingEvents.length);

                if (upcomingEvents.length > 0) {
                  const nextEvent = upcomingEvents[0];
                  const eventDate = new Date(nextEvent.start_date);
                  return (
                    <div>
                      <h3 style={{fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem'}}>
                        Upcoming - {eventDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </h3>
                      <div style={{display: 'grid', gap: '1rem'}}>
                        <div
                          onClick={() => openEventModal(nextEvent)}
                          style={{
                            background: 'white',
                            borderLeft: `4px solid ${getEventColor(nextEvent.event_type)}`,
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)'}
                          onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
                        >
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem'}}>
                            <h4 style={{fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0}}>
                              {nextEvent.title}
                            </h4>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              background: getEventColor(nextEvent.event_type),
                              color: 'white',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              textTransform: 'capitalize'
                            }}>
                              {nextEvent.event_type}
                            </span>
                          </div>

                          <div style={{display: 'flex', gap: '1rem', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem'}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                              <Clock size={14} />
                              {nextEvent.all_day ? 'All Day' : new Date(nextEvent.start_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </div>
                            {nextEvent.location && (
                              <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                                <MapPin size={14} />
                                {nextEvent.location}
                              </div>
                            )}
                          </div>

                          {nextEvent.description && (
                            <p style={{fontSize: '0.875rem', color: '#6b7280', margin: 0}}>
                              {nextEvent.description.length > 100 ? nextEvent.description.substring(0, 100) + '...' : nextEvent.description}
                            </p>
                          )}

                          <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap'}}>
                            {nextEvent.visibility === 'barn' && (
                              <span style={{fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#dbeafe', color: '#1e40af', borderRadius: '0.25rem'}}>
                                Barn Event
                              </span>
                            )}
                            {nextEvent.horse_name && (
                              <span style={{fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#f3e8ff', color: '#6b21a8', borderRadius: '0.25rem'}}>
                                {nextEvent.horse_name}
                              </span>
                            )}
                            <span style={{fontSize: '0.75rem', color: '#6b7280'}}>
                              by {nextEvent.creator_name}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              }

              // If date is selected, show events for that date
              return selectedDate && (() => {
              const dateStr = selectedDate.toISOString().split('T')[0];
              const dayEvents = events.filter(event => {
                const eventDate = new Date(event.start_date).toISOString().split('T')[0];

                // Apply filters
                if (eventFilter === 'barn' && event.visibility !== 'barn') return false;
                if (eventFilter === 'personal' && event.created_by !== user.id) return false;
                if (eventTypeFilter !== 'all' && event.event_type !== eventTypeFilter) return false;

                return eventDate === dateStr;
              });

              if (dayEvents.length > 0) {
                return (
                  <div>
                    <h3 style={{fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem'}}>
                      Events on {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </h3>
                    <div style={{display: 'grid', gap: '1rem'}}>
                      {dayEvents.map(event => (
                        <div
                          key={event.id}
                          onClick={() => openEventModal(event)}
                          style={{
                            background: 'white',
                            borderLeft: `4px solid ${getEventColor(event.event_type)}`,
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)'}
                          onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
                        >
                          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem'}}>
                            <h4 style={{fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0}}>
                              {event.title}
                            </h4>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              background: getEventColor(event.event_type),
                              color: 'white',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              textTransform: 'capitalize'
                            }}>
                              {event.event_type}
                            </span>
                          </div>

                          <div style={{display: 'flex', gap: '1rem', fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem'}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                              <Clock size={14} />
                              {event.all_day ? 'All Day' : new Date(event.start_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </div>
                            {event.location && (
                              <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                                <MapPin size={14} />
                                {event.location}
                              </div>
                            )}
                          </div>

                          {event.description && (
                            <p style={{fontSize: '0.875rem', color: '#6b7280', margin: 0}}>
                              {event.description.length > 100 ? event.description.substring(0, 100) + '...' : event.description}
                            </p>
                          )}

                          <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap'}}>
                            {event.visibility === 'barn' && (
                              <span style={{fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#dbeafe', color: '#1e40af', borderRadius: '0.25rem'}}>
                                Barn Event
                              </span>
                            )}
                            {event.horse_name && (
                              <span style={{fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: '#f3e8ff', color: '#6b21a8', borderRadius: '0.25rem'}}>
                                {event.horse_name}
                              </span>
                            )}
                            <span style={{fontSize: '0.75rem', color: '#6b7280'}}>
                              by {event.creator_name}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div style={{textAlign: 'center', padding: '2rem', color: '#6b7280'}}>
                    <p>No events on this date</p>
                  </div>
                );
              }
            })();
            })()}

            {/* Event color legend */}
            <div style={{marginTop: '2rem', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem'}}>
              <h4 style={{fontSize: '0.875rem', fontWeight: '600', color: '#111827', marginBottom: '0.75rem'}}>
                Event Types:
              </h4>
              <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
                {[
                  { type: 'competition', label: 'Competition' },
                  { type: 'practice', label: 'Practice' },
                  { type: 'vet', label: 'Vet' },
                  { type: 'farrier', label: 'Farrier' },
                  { type: 'training', label: 'Training' },
                  { type: 'other', label: 'Other' }
                ].map(({ type, label }) => (
                  <div key={type} style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <div style={{width: '12px', height: '12px', borderRadius: '50%', background: getEventColor(type)}} />
                    <span style={{fontSize: '0.75rem', color: '#6b7280'}}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Add Event Modal */}
        {showAddEvent && (
          <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'}}>
            <div style={{background: 'white', borderRadius: '0.75rem', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto', padding: '2rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: '700', color: '#111827', margin: 0}}>
                  Add New Event
                </h3>
                <button
                  onClick={() => {
                    setShowAddEvent(false);
                    setEventTitle('');
                    setEventDescription('');
                    setEventType('other');
                    setEventStartDate('');
                    setEventStartTime('');
                    setEventEndDate('');
                    setEventEndTime('');
                    setEventLocation('');
                    setEventAllDay(false);
                    setEventHorseId('');
                    setEventVisibility('private');
                    setEventNotes('');
                  }}
                  style={{background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280'}}
                >
                  <X size={24} />
                </button>
              </div>

              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Event Title *
                </label>
                <input
                  type="text"
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  placeholder="e.g., Houston Rodeo"
                />
              </div>

              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Event Type
                </label>
                <select
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                >
                  <option value="other">Other</option>
                  <option value="competition">Competition</option>
                  <option value="practice">Practice</option>
                  <option value="vet">Vet Appointment</option>
                  <option value="farrier">Farrier</option>
                  <option value="training">Training</option>
                </select>
              </div>

              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={eventAllDay}
                    onChange={(e) => setEventAllDay(e.target.checked)}
                  />
                  <span style={{fontSize: '0.875rem', fontWeight: '500', color: '#374151'}}>
                    All Day Event
                  </span>
                </label>
              </div>

              <div style={{display: 'grid', gridTemplateColumns: eventAllDay ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                <div>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={eventStartDate}
                    onChange={(e) => setEventStartDate(e.target.value)}
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  />
                </div>
                {!eventAllDay && (
                  <div>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={eventStartTime}
                      onChange={(e) => setEventStartTime(e.target.value)}
                      style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                    />
                  </div>
                )}
              </div>

              <div style={{display: 'grid', gridTemplateColumns: eventAllDay ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                <div>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={eventEndDate}
                    onChange={(e) => setEventEndDate(e.target.value)}
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  />
                </div>
                {!eventAllDay && (
                  <div>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                      End Time
                    </label>
                    <input
                      type="time"
                      value={eventEndTime}
                      onChange={(e) => setEventEndTime(e.target.value)}
                      style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                    />
                  </div>
                )}
              </div>

              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Location
                </label>
                <input
                  type="text"
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  placeholder="e.g., Houston Arena"
                />
              </div>

              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Description
                </label>
                <textarea
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem', minHeight: '80px', fontFamily: 'inherit'}}
                  placeholder="Event details..."
                />
              </div>

              {horses.length > 0 && (
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                    Assign to Horse (Optional)
                  </label>
                  <select
                    value={eventHorseId}
                    onChange={(e) => setEventHorseId(e.target.value)}
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  >
                    <option value="">No specific horse</option>
                    {horses.map(horse => (
                      <option key={horse.id} value={horse.id}>{horse.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {user?.barn_code && (
                <div style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                    Visibility
                  </label>
                  <select
                    value={eventVisibility}
                    onChange={(e) => setEventVisibility(e.target.value)}
                    style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  >
                    <option value="private">Private (Only Me)</option>
                    <option value="barn">Share with Barn</option>
                  </select>
                </div>
              )}

              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Notes
                </label>
                <textarea
                  value={eventNotes}
                  onChange={(e) => setEventNotes(e.target.value)}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem', minHeight: '60px', fontFamily: 'inherit'}}
                  placeholder="Additional notes..."
                />
              </div>

              {error && (
                <div style={{marginBottom: '1rem', padding: '0.75rem', background: '#fee2e2', color: '#991b1b', borderRadius: '0.375rem', fontSize: '0.875rem'}}>
                  {error}
                </div>
              )}

              <div style={{display: 'flex', gap: '1rem'}}>
                <button
                  onClick={handleAddEvent}
                  disabled={loading}
                  style={{flex: 1, padding: '0.75rem', background: loading ? '#9ca3af' : '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '1rem'}}
                >
                  {loading ? 'Creating...' : 'Create Event'}
                </button>
                <button
                  onClick={() => setShowAddEvent(false)}
                  style={{padding: '0.75rem 1.5rem', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer', fontSize: '0.875rem'}}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Event Detail Modal */}
        {showEventModal && selectedEvent && (
          <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'}}>
            <div style={{background: 'white', borderRadius: '0.75rem', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto', padding: '2rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: '700', color: '#111827', margin: 0}}>
                  {selectedEvent.title}
                </h3>
                <button
                  onClick={() => {
                    setShowEventModal(false);
                    setSelectedEvent(null);
                    setIsEditingEvent(false);
                    setShowLogRaceForm(false);
                    resetLogRaceForm();
                  }}
                  style={{background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280'}}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Edit Mode Form */}
              {isEditingEvent ? (
                <>
                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                      Event Title <span style={{color: '#dc2626'}}>*</span>
                    </label>
                    <input
                      type="text"
                      value={eventTitle}
                      onChange={(e) => setEventTitle(e.target.value)}
                      style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                      placeholder="Event title"
                    />
                  </div>

                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                      Description
                    </label>
                    <textarea
                      value={eventDescription}
                      onChange={(e) => setEventDescription(e.target.value)}
                      style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem', minHeight: '80px', fontFamily: 'inherit'}}
                      placeholder="Event description (optional)"
                    />
                  </div>

                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                      Event Type <span style={{color: '#dc2626'}}>*</span>
                    </label>
                    <select
                      value={eventType}
                      onChange={(e) => setEventType(e.target.value)}
                      style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                    >
                      <option value="competition">Competition</option>
                      <option value="vet">Vet Appointment</option>
                      <option value="farrier">Farrier</option>
                      <option value="training">Training</option>
                      <option value="meeting">Meeting</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'flex', alignItems: 'center', fontSize: '0.875rem', fontWeight: '500', color: '#374151', gap: '0.5rem'}}>
                      <input
                        type="checkbox"
                        checked={eventAllDay}
                        onChange={(e) => setEventAllDay(e.target.checked)}
                        style={{width: '18px', height: '18px'}}
                      />
                      All Day Event
                    </label>
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: eventAllDay ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                    <div>
                      <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                        Start Date <span style={{color: '#dc2626'}}>*</span>
                      </label>
                      <input
                        type="date"
                        value={eventStartDate}
                        onChange={(e) => setEventStartDate(e.target.value)}
                        style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                      />
                    </div>
                    {!eventAllDay && (
                      <div>
                        <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={eventStartTime}
                          onChange={(e) => setEventStartTime(e.target.value)}
                          style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                        />
                      </div>
                    )}
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: eventAllDay ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                    <div>
                      <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                        End Date (optional)
                      </label>
                      <input
                        type="date"
                        value={eventEndDate}
                        onChange={(e) => setEventEndDate(e.target.value)}
                        style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                      />
                    </div>
                    {!eventAllDay && (
                      <div>
                        <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                          End Time
                        </label>
                        <input
                          type="time"
                          value={eventEndTime}
                          onChange={(e) => setEventEndTime(e.target.value)}
                          style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                        />
                      </div>
                    )}
                  </div>

                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                      Location
                    </label>
                    <input
                      type="text"
                      value={eventLocation}
                      onChange={(e) => setEventLocation(e.target.value)}
                      style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                      placeholder="Event location (optional)"
                    />
                  </div>

                  {horses.length > 0 && (
                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                        Associated Horse
                      </label>
                      <select
                        value={eventHorseId}
                        onChange={(e) => setEventHorseId(e.target.value)}
                        style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                      >
                        <option value="">No specific horse</option>
                        {horses.map(horse => (
                          <option key={horse.id} value={horse.id}>{horse.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {user?.barn_code && (
                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                        Visibility
                      </label>
                      <select
                        value={eventVisibility}
                        onChange={(e) => setEventVisibility(e.target.value)}
                        style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                      >
                        <option value="private">Private (Only Me)</option>
                        <option value="barn">Share with Barn</option>
                      </select>
                    </div>
                  )}

                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                      Notes
                    </label>
                    <textarea
                      value={eventNotes}
                      onChange={(e) => setEventNotes(e.target.value)}
                      style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem', minHeight: '60px', fontFamily: 'inherit'}}
                      placeholder="Additional notes..."
                    />
                  </div>

                  <div style={{display: 'flex', gap: '1rem'}}>
                    <button
                      onClick={async () => {
                        await handleEditEvent();
                        setIsEditingEvent(false);
                      }}
                      disabled={loading || !eventTitle || !eventStartDate}
                      style={{flex: 1, padding: '0.75rem', background: loading || !eventTitle || !eventStartDate ? '#9ca3af' : '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: loading || !eventTitle || !eventStartDate ? 'not-allowed' : 'pointer', fontSize: '1rem'}}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={() => setIsEditingEvent(false)}
                      style={{padding: '0.75rem 1.5rem', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer', fontSize: '0.875rem'}}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Read-Only View */}
                  <div style={{marginBottom: '1.5rem'}}>
                    <span style={{
                      padding: '0.375rem 0.75rem',
                      background: getEventColor(selectedEvent.event_type),
                      color: 'white',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {selectedEvent.event_type}
                    </span>
                  </div>

                  {selectedEvent.description && (
                <div style={{marginBottom: '1rem'}}>
                  <h4 style={{fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem'}}>
                    Description
                  </h4>
                  <p style={{fontSize: '0.875rem', color: '#111827', margin: 0}}>
                    {selectedEvent.description}
                  </p>
                </div>
              )}

              <div style={{marginBottom: '1rem'}}>
                <h4 style={{fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem'}}>
                  Date & Time
                </h4>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#111827'}}>
                  <Clock size={16} />
                  {selectedEvent.all_day
                    ? `${new Date(selectedEvent.start_date).toLocaleDateString()} - All Day`
                    : `${new Date(selectedEvent.start_date).toLocaleString()}`
                  }
                  {selectedEvent.end_date && ` - ${new Date(selectedEvent.end_date).toLocaleString()}`}
                </div>
              </div>

              {selectedEvent.location && (
                <div style={{marginBottom: '1rem'}}>
                  <h4 style={{fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem'}}>
                    Location
                  </h4>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#111827'}}>
                    <MapPin size={16} />
                    {selectedEvent.location}
                  </div>
                </div>
              )}

              {selectedEvent.horse_name && (
                <div style={{marginBottom: '1rem'}}>
                  <h4 style={{fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem'}}>
                    Horse
                  </h4>
                  <p style={{fontSize: '0.875rem', color: '#111827', margin: 0}}>
                    {selectedEvent.horse_name}
                  </p>
                </div>
              )}

              {selectedEvent.notes && (
                <div style={{marginBottom: '1rem'}}>
                  <h4 style={{fontSize: '0.875rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem'}}>
                    Notes
                  </h4>
                  <p style={{fontSize: '0.875rem', color: '#111827', margin: 0}}>
                    {selectedEvent.notes}
                  </p>
                </div>
              )}

              <div style={{marginBottom: '1rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '0.5rem'}}>
                <p style={{fontSize: '0.75rem', color: '#6b7280', margin: 0}}>
                  Created by {selectedEvent.creator_name}
                  {selectedEvent.visibility === 'barn' && ' • Shared with barn'}
                </p>
              </div>

              {/* Log Race Result button for competition events */}
              {selectedEvent.created_by === user.id && selectedEvent.event_type === 'competition' && !isEditingEvent && !showLogRaceForm && (
                <button
                  onClick={() => {
                    setShowLogRaceForm(true);
                    // Pre-fill entry fee from description if available
                    const costMatch = selectedEvent.description?.match(/Estimated cost: \$(\d+)/);
                    if (costMatch) {
                      setLogRaceEntryFee(costMatch[1]);
                    }
                  }}
                  style={{width: '100%', padding: '0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem'}}
                >
                  <Flag size={16} />
                  Log Race Result
                </button>
              )}

              {/* Log Race Result Form */}
              {showLogRaceForm && (
                <form onSubmit={handleLogRaceResult} style={{marginBottom: '1rem', padding: '1rem', background: '#f0fdf4', borderRadius: '0.5rem', border: '1px solid #86efac'}}>
                  <h4 style={{margin: '0 0 1rem 0', color: '#166534', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <Flag size={18} />
                    Log Race Result
                  </h4>

                  {/* Horse Selection */}
                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem'}}>Horse *</label>
                    <select
                      value={logRaceHorseId}
                      onChange={(e) => setLogRaceHorseId(e.target.value)}
                      required
                      style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem'}}
                    >
                      <option value="">Select a horse</option>
                      {horses.map(horse => (
                        <option key={horse.id} value={horse.id}>{horse.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Time Input */}
                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem'}}>Time (seconds)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={logRaceTime}
                      onChange={(e) => setLogRaceTime(e.target.value)}
                      disabled={logRaceNoTime}
                      placeholder="e.g., 17.456"
                      style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', background: logRaceNoTime ? '#f3f4f6' : 'white'}}
                    />
                    <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.875rem'}}>
                      <input
                        type="checkbox"
                        checked={logRaceNoTime}
                        onChange={(e) => {
                          setLogRaceNoTime(e.target.checked);
                          if (e.target.checked) setLogRaceTime('');
                        }}
                      />
                      No time (knocked barrel, off-pattern, etc.)
                    </label>
                  </div>

                  {/* Barrel Issues (shown when no-time is checked) */}
                  {logRaceNoTime && (
                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem'}}>What happened?</label>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                        {['1', '2', '3', 'off-pattern'].map(issue => (
                          <label key={issue} style={{display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', padding: '0.25rem 0.5rem', background: logRaceBarrelIssues.includes(issue) ? '#fef3c7' : '#f3f4f6', borderRadius: '0.25rem', cursor: 'pointer'}}>
                            <input
                              type="checkbox"
                              checked={logRaceBarrelIssues.includes(issue)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setLogRaceBarrelIssues([...logRaceBarrelIssues, issue]);
                                } else {
                                  setLogRaceBarrelIssues(logRaceBarrelIssues.filter(i => i !== issue));
                                }
                              }}
                            />
                            {issue === 'off-pattern' ? 'Off Pattern' : `Barrel ${issue}`}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Placement */}
                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem'}}>Placement</label>
                    <input
                      type="text"
                      value={logRacePlacement}
                      onChange={(e) => setLogRacePlacement(e.target.value)}
                      placeholder="e.g., 1st 1D, 4th 3D"
                      style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem'}}
                    />
                  </div>

                  {/* Prize Money & Entry Fee */}
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                    <div>
                      <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem'}}>Prize Money ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={logRacePrizeMoney}
                        onChange={(e) => setLogRacePrizeMoney(e.target.value)}
                        placeholder="0.00"
                        style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem'}}
                      />
                    </div>
                    <div>
                      <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem'}}>Entry Fee ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={logRaceEntryFee}
                        onChange={(e) => setLogRaceEntryFee(e.target.value)}
                        placeholder="0.00"
                        style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem'}}
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div style={{marginBottom: '1rem'}}>
                    <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem'}}>Notes</label>
                    <textarea
                      value={logRaceNotes}
                      onChange={(e) => setLogRaceNotes(e.target.value)}
                      placeholder="Any notes about this race..."
                      rows={2}
                      style={{width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', resize: 'vertical'}}
                    />
                  </div>

                  {/* Delete Event Option */}
                  <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem'}}>
                    <input
                      type="checkbox"
                      checked={logRaceDeleteEvent}
                      onChange={(e) => setLogRaceDeleteEvent(e.target.checked)}
                    />
                    Remove this event from calendar after logging
                  </label>

                  {/* Submit Buttons */}
                  <div style={{display: 'flex', gap: '0.5rem'}}>
                    <button
                      type="submit"
                      disabled={loading}
                      style={{flex: 1, padding: '0.75rem', background: loading ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '0.875rem'}}
                    >
                      {loading ? 'Saving...' : 'Save Race Result'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowLogRaceForm(false);
                        resetLogRaceForm();
                      }}
                      style={{padding: '0.75rem 1rem', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontWeight: '500', cursor: 'pointer', fontSize: '0.875rem'}}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {selectedEvent.created_by === user.id && !isEditingEvent && !showLogRaceForm && (
                <div style={{display: 'flex', gap: '1rem', marginTop: '1.5rem'}}>
                  <button
                    onClick={() => {
                      setIsEditingEvent(true);
                      // Populate form with current event data
                      setEventTitle(selectedEvent.title);
                      setEventDescription(selectedEvent.description || '');
                      setEventType(selectedEvent.event_type);
                      setEventLocation(selectedEvent.location || '');
                      setEventAllDay(selectedEvent.all_day);
                      setEventHorseId(selectedEvent.horse_id || '');
                      setEventVisibility(selectedEvent.visibility);
                      setEventNotes(selectedEvent.notes || '');

                      // Parse dates
                      const startDate = new Date(selectedEvent.start_date);
                      setEventStartDate(startDate.toISOString().split('T')[0]);
                      if (!selectedEvent.all_day) {
                        setEventStartTime(startDate.toTimeString().slice(0, 5));
                      }

                      if (selectedEvent.end_date) {
                        const endDate = new Date(selectedEvent.end_date);
                        setEventEndDate(endDate.toISOString().split('T')[0]);
                        if (!selectedEvent.all_day) {
                          setEventEndTime(endDate.toTimeString().slice(0, 5));
                        }
                      }
                    }}
                    style={{flex: 1, padding: '0.75rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'}}
                  >
                    <Edit2 size={16} />
                    Edit Event
                  </button>
                  <button
                    onClick={() => handleDeleteEvent(selectedEvent.id)}
                    disabled={loading}
                    style={{padding: '0.75rem 1.5rem', background: loading ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Find Events View */}
        {activeView === 'find-events' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem'}}>Find Barrel Racing Events</h2>
                <p style={{color: '#6b7280', margin: 0}}>
                  Browse upcoming barrel races and rodeos from NBHA, BBR, WPRA, and more.
                </p>
              </div>
              <button
                onClick={() => setShowSubmitEventModal(true)}
                style={{
                  padding: '0.625rem 1rem',
                  background: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  whiteSpace: 'nowrap'
                }}
              >
                <PlusCircle size={16} />
                Submit Event
              </button>
            </div>

            {/* Filter Bar */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
              marginBottom: '1.5rem',
              padding: '1rem',
              background: '#f9fafb',
              borderRadius: '0.5rem'
            }}>
              {/* Search */}
              <input
                type="text"
                placeholder="Search events..."
                value={externalEventsSearch}
                onChange={(e) => setExternalEventsSearch(e.target.value)}
                style={{
                  flex: '1',
                  minWidth: '200px',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />

              {/* State Filter */}
              <select
                value={externalEventsStateFilter}
                onChange={(e) => setExternalEventsStateFilter(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  background: 'white',
                  minWidth: '120px'
                }}
              >
                <option value="">All States</option>
                {externalEventsFilters.states.map(s => (
                  <option key={s.state} value={s.state}>{s.state} ({s.count})</option>
                ))}
              </select>

              {/* Association Filter */}
              <select
                value={externalEventsAssociationFilter}
                onChange={(e) => setExternalEventsAssociationFilter(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  background: 'white',
                  minWidth: '140px'
                }}
              >
                <option value="">All Associations</option>
                {externalEventsFilters.associations.map(a => (
                  <option key={a.association} value={a.association}>{a.association} ({a.count})</option>
                ))}
              </select>

              {/* Search Button */}
              <button
                onClick={() => loadExternalEvents(1)}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#ea580c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <Search size={16} />
                Search
              </button>

              {/* Clear Filters */}
              {(externalEventsStateFilter || externalEventsAssociationFilter || externalEventsSearch) && (
                <button
                  onClick={() => {
                    setExternalEventsStateFilter('');
                    setExternalEventsAssociationFilter('');
                    setExternalEventsSearch('');
                    loadExternalEvents(1);
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: '#f3f4f6',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Location Filter Section */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: locationFilterEnabled ? '#fef3c7' : '#f9fafb',
              borderRadius: '0.5rem',
              border: locationFilterEnabled ? '1px solid #fcd34d' : '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <MapPin size={18} color={locationFilterEnabled ? '#d97706' : '#6b7280'} />
                <span style={{ fontWeight: '600', color: locationFilterEnabled ? '#92400e' : '#374151' }}>
                  Filter by Distance
                </span>
                {locationFilterEnabled && (
                  <span style={{
                    fontSize: '0.75rem',
                    background: '#f59e0b',
                    color: 'white',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '9999px',
                    fontWeight: '500'
                  }}>
                    Active
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
                <div style={{ flex: '1', minWidth: '150px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Your Zipcode
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 73034"
                    value={userZipcode}
                    onChange={(e) => setUserZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    maxLength={5}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                <div style={{ minWidth: '140px' }}>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                    Search Radius
                  </label>
                  <select
                    value={userSearchRadius}
                    onChange={(e) => setUserSearchRadius(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      background: 'white'
                    }}
                  >
                    <option value={25}>25 miles</option>
                    <option value={50}>50 miles</option>
                    <option value={100}>100 miles</option>
                    <option value={150}>150 miles</option>
                    <option value={200}>200 miles</option>
                    <option value={300}>300 miles</option>
                    <option value={500}>500 miles</option>
                  </select>
                </div>
                <button
                  onClick={() => {
                    if (userZipcode.length === 5) {
                      saveUserLocation(userZipcode, userSearchRadius);
                    } else {
                      setError('Please enter a valid 5-digit zipcode');
                    }
                  }}
                  disabled={userZipcode.length !== 5}
                  style={{
                    padding: '0.5rem 1rem',
                    background: userZipcode.length === 5 ? '#d97706' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: userZipcode.length === 5 ? 'pointer' : 'not-allowed',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <MapPin size={16} />
                  {locationFilterEnabled ? 'Update' : 'Apply'} Location
                </button>
                {locationFilterEnabled && (
                  <button
                    onClick={() => {
                      setLocationFilterEnabled(false);
                      setUserZipcode('');
                      loadExternalEvents(1);
                    }}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: 'white',
                      color: '#6b7280',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Clear Location
                  </button>
                )}
              </div>
              {locationFilterEnabled && (
                <p style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.5rem' }}>
                  Showing events within {userSearchRadius} miles of {userZipcode}
                </p>
              )}
            </div>

            {/* View Toggle & Sort Controls */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}>
              {/* Results Count */}
              <p style={{color: '#6b7280', margin: 0, fontSize: '0.875rem'}}>
                {externalEventsLoading ? 'Loading...' : `${externalEventsTotal} events found`}
              </p>

              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                {/* Sort Dropdown */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Sort:</span>
                  <select
                    value={externalEventsSortBy}
                    onChange={(e) => {
                      setExternalEventsSortBy(e.target.value);
                      loadExternalEvents(1);
                    }}
                    style={{
                      padding: '0.375rem 0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.75rem',
                      background: 'white'
                    }}
                  >
                    <option value="date">Date (Soonest)</option>
                    {locationFilterEnabled && <option value="distance">Distance (Closest)</option>}
                  </select>
                </div>

                {/* View Toggle */}
                <div style={{
                  display: 'flex',
                  background: '#f3f4f6',
                  borderRadius: '0.375rem',
                  padding: '0.125rem'
                }}>
                  <button
                    onClick={() => {
                      setExternalEventsViewMode('cards');
                      loadExternalEvents(1, false);
                    }}
                    style={{
                      padding: '0.375rem 0.625rem',
                      background: externalEventsViewMode === 'cards' ? 'white' : 'transparent',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.75rem',
                      color: externalEventsViewMode === 'cards' ? '#111827' : '#6b7280',
                      boxShadow: externalEventsViewMode === 'cards' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                    }}
                    title="Card View"
                  >
                    <LayoutGrid size={14} />
                    Cards
                  </button>
                  <button
                    onClick={() => {
                      setExternalEventsViewMode('calendar');
                      loadExternalEvents(1, true);  // Load more events for calendar
                    }}
                    style={{
                      padding: '0.375rem 0.625rem',
                      background: externalEventsViewMode === 'calendar' ? 'white' : 'transparent',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.75rem',
                      color: externalEventsViewMode === 'calendar' ? '#111827' : '#6b7280',
                      boxShadow: externalEventsViewMode === 'calendar' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                    }}
                    title="Calendar View"
                  >
                    <CalendarDays size={14} />
                    Calendar
                  </button>
                </div>
              </div>
            </div>

            {/* Events Display - Cards or Calendar View */}
            {externalEventsLoading ? (
              <div style={{textAlign: 'center', padding: '3rem', color: '#6b7280'}}>
                Loading events...
              </div>
            ) : externalEvents.length === 0 ? (
              <div style={{textAlign: 'center', padding: '3rem', color: '#6b7280'}}>
                <Search size={48} style={{margin: '0 auto 1rem', opacity: 0.5}} />
                <p>No events found matching your criteria.</p>
                <p style={{fontSize: '0.875rem', marginTop: '0.5rem'}}>Try adjusting your filters or search terms.</p>
              </div>
            ) : externalEventsViewMode === 'calendar' ? (
              /* Calendar View */
              <div style={{ background: 'white', borderRadius: '0.75rem', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  events={externalEvents.map(event => ({
                    id: event.id.toString(),
                    title: event.name,
                    start: event.event_date.split('T')[0],  // Extract YYYY-MM-DD to avoid timezone issues
                    end: event.end_date ? new Date(new Date(event.end_date.split('T')[0] + 'T00:00:00').getTime() + 86400000).toISOString().split('T')[0] : undefined,
                    allDay: true,  // Hide time display
                    backgroundColor: savedExternalEvents.some(s => s.external_event_id === event.id) ? '#059669' : '#ea580c',
                    borderColor: savedExternalEvents.some(s => s.external_event_id === event.id) ? '#059669' : '#ea580c',
                    extendedProps: event
                  }))}
                  eventClick={(info) => {
                    const event = info.event.extendedProps;
                    openAddExternalEventModal(event);
                  }}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: ''
                  }}
                  height="auto"
                  dayMaxEvents={10}
                  moreLinkClick="popover"
                  eventDisplay="block"
                  displayEventTime={false}
                />
                <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', justifyContent: 'center', fontSize: '0.75rem', color: '#6b7280' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#ea580c' }}></div>
                    Available Events
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: '#059669' }}></div>
                    Added to Calendar
                  </div>
                </div>
              </div>
            ) : (
              /* Cards View */
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '1rem'
              }}>
                {externalEvents.map(event => {
                  const isSaved = savedExternalEvents.some(s => s.external_event_id === event.id);
                  return (
                    <div
                      key={event.id}
                      style={{
                        background: 'white',
                        borderRadius: '0.75rem',
                        padding: '1rem',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        borderLeft: '4px solid #ea580c'
                      }}
                    >
                      {/* Event Name */}
                      <h3 style={{fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem', lineHeight: '1.3'}}>
                        {event.name}
                      </h3>

                      {/* Date */}
                      <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.25rem'}}>
                        <CalendarDays size={14} />
                        <span>
                          {parseLocalDate(event.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                          {event.end_date && event.end_date !== event.event_date && (
                            <> - {parseLocalDate(event.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>
                          )}
                        </span>
                      </div>

                      {/* Location */}
                      <div style={{display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.5rem'}}>
                        <MapPin size={14} />
                        <span>{event.city ? `${event.city}, ${event.state}` : event.location || event.state || 'Location TBD'}</span>
                      </div>

                      {/* Distance Badge (when location filtering is active) */}
                      {locationFilterEnabled && event.distance != null && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          background: event.distance <= 50 ? '#d1fae5' : event.distance <= 100 ? '#fef3c7' : '#f3f4f6',
                          color: event.distance <= 50 ? '#065f46' : event.distance <= 100 ? '#92400e' : '#374151',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          marginBottom: '0.5rem'
                        }}>
                          <MapPin size={12} />
                          {event.distance} miles away
                        </div>
                      )}

                      {/* Association Badge */}
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.5rem',
                        background: '#f3f4f6',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.75rem'
                      }}>
                        {event.association}
                      </span>

                      {/* Actions */}
                      <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.5rem'}}>
                        {isSaved ? (
                          <button
                            onClick={() => {
                              const saved = savedExternalEvents.find(s => s.external_event_id === event.id);
                              if (saved) handleRemoveSavedExternalEvent(saved.id);
                            }}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: '#d1fae5',
                              color: '#065f46',
                              border: 'none',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            <span style={{fontSize: '1rem'}}>✓</span> Added
                          </button>
                        ) : (
                          <button
                            onClick={() => openAddExternalEventModal(event)}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: '#ea580c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            <PlusCircle size={14} /> Add to Calendar
                          </button>
                        )}

                        {event.registration_url && (
                          <a
                            href={event.registration_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: 'white',
                              color: '#ea580c',
                              border: '1px solid #ea580c',
                              borderRadius: '0.375rem',
                              textDecoration: 'none',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}
                          >
                            Register
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination - Only show in cards view */}
            {externalEventsViewMode === 'cards' && externalEventsTotalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem',
                marginTop: '2rem'
              }}>
                <button
                  onClick={() => loadExternalEvents(externalEventsPage - 1)}
                  disabled={externalEventsPage <= 1}
                  style={{
                    padding: '0.5rem 1rem',
                    background: externalEventsPage <= 1 ? '#f3f4f6' : '#ea580c',
                    color: externalEventsPage <= 1 ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: externalEventsPage <= 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                <span style={{color: '#6b7280'}}>
                  Page {externalEventsPage} of {externalEventsTotalPages}
                </span>
                <button
                  onClick={() => loadExternalEvents(externalEventsPage + 1)}
                  disabled={externalEventsPage >= externalEventsTotalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    background: externalEventsPage >= externalEventsTotalPages ? '#f3f4f6' : '#ea580c',
                    color: externalEventsPage >= externalEventsTotalPages ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: externalEventsPage >= externalEventsTotalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            )}

            {/* My Saved Events Section */}
            {savedExternalEvents.length > 0 && (
              <div style={{marginTop: '3rem'}}>
                <h3 style={{fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem'}}>
                  My Saved Events ({savedExternalEvents.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '1rem'
                }}>
                  {savedExternalEvents.map(saved => (
                    <div
                      key={saved.id}
                      style={{
                        background: '#f0fdf4',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        border: '1px solid #86efac'
                      }}
                    >
                      <h4 style={{fontWeight: '600', marginBottom: '0.25rem'}}>{saved.name}</h4>
                      <p style={{fontSize: '0.875rem', color: '#6b7280'}}>
                        {parseLocalDate(saved.event_date).toLocaleDateString()} • {saved.city}, {saved.state}
                      </p>
                      {saved.notes && (
                        <p style={{fontSize: '0.75rem', color: '#059669', marginTop: '0.5rem', fontStyle: 'italic'}}>
                          Note: {saved.notes}
                        </p>
                      )}
                      <div style={{display: 'flex', gap: '0.5rem', marginTop: '0.75rem'}}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          background: saved.entry_status === 'confirmed' ? '#059669' :
                                      saved.entry_status === 'entered' ? '#0284c7' : '#f59e0b',
                          color: 'white',
                          borderRadius: '0.25rem',
                          fontSize: '0.625rem',
                          fontWeight: '500',
                          textTransform: 'uppercase'
                        }}>
                          {saved.entry_status}
                        </span>
                        <button
                          onClick={() => handleRemoveSavedExternalEvent(saved.id)}
                          style={{
                            marginLeft: 'auto',
                            padding: '0.25rem 0.5rem',
                            background: 'white',
                            color: '#dc2626',
                            border: '1px solid #dc2626',
                            borderRadius: '0.25rem',
                            fontSize: '0.625rem',
                            cursor: 'pointer'
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add External Event to Calendar Modal */}
        {showAddExternalEventModal && selectedExternalEvent && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h3 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem'}}>
                Add to Calendar
              </h3>

              {/* Event Info */}
              <div style={{
                background: '#f9fafb',
                padding: '1rem',
                borderRadius: '0.5rem',
                marginBottom: '1rem'
              }}>
                <p style={{fontWeight: '600'}}>{selectedExternalEvent.name}</p>
                <p style={{fontSize: '0.875rem', color: '#6b7280'}}>
                  {parseLocalDate(selectedExternalEvent.event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <p style={{fontSize: '0.875rem', color: '#6b7280'}}>
                  {selectedExternalEvent.city}, {selectedExternalEvent.state}
                </p>
              </div>

              {/* Personal Notes */}
              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontWeight: '500', marginBottom: '0.25rem', fontSize: '0.875rem'}}>
                  Personal Notes
                </label>
                <textarea
                  value={externalEventNotes}
                  onChange={(e) => setExternalEventNotes(e.target.value)}
                  placeholder="e.g., Need to arrive by 8 AM, bring extra hay..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Horses Entered */}
              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontWeight: '500', marginBottom: '0.25rem', fontSize: '0.875rem'}}>
                  Horses to Enter
                </label>
                <select
                  multiple
                  value={externalEventHorsesEntered}
                  onChange={(e) => setExternalEventHorsesEntered(Array.from(e.target.selectedOptions, opt => opt.value))}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    minHeight: '80px'
                  }}
                >
                  {horses.map(h => (
                    <option key={h.id} value={h.name}>{h.name}</option>
                  ))}
                </select>
                <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>Hold Ctrl/Cmd to select multiple</p>
              </div>

              {/* Stall Reservation */}
              <div style={{marginBottom: '1rem'}}>
                <label style={{display: 'block', fontWeight: '500', marginBottom: '0.25rem', fontSize: '0.875rem'}}>
                  Stall Reservation
                </label>
                <input
                  type="text"
                  value={externalEventStallReservation}
                  onChange={(e) => setExternalEventStallReservation(e.target.value)}
                  placeholder="e.g., Barn A, Stall 12"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              {/* Entry Status */}
              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontWeight: '500', marginBottom: '0.25rem', fontSize: '0.875rem'}}>
                  Entry Status
                </label>
                <select
                  value={externalEventEntryStatus}
                  onChange={(e) => setExternalEventEntryStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="planning">Planning to Enter</option>
                  <option value="entered">Entry Submitted</option>
                  <option value="confirmed">Entry Confirmed</option>
                </select>
              </div>

              {/* Buttons */}
              <div style={{display: 'flex', gap: '1rem'}}>
                <button
                  onClick={handleAddExternalEventToCalendar}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#ea580c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? 'Adding...' : 'Add to Calendar'}
                </button>
                <button
                  onClick={() => {
                    setShowAddExternalEventModal(false);
                    setSelectedExternalEvent(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submit Event Modal */}
        {showSubmitEventModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>
                Submit a New Event
              </h2>
              <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem'}}>
                Know of a barrel racing event that's not in our calendar? Submit it here and help the community!
              </p>

              <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                <div>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                    Event Name *
                  </label>
                  <input
                    type="text"
                    value={submitEventName}
                    onChange={(e) => setSubmitEventName(e.target.value)}
                    placeholder="e.g., Spring Barrel Bash"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                  <div>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={submitEventDate}
                      onChange={(e) => setSubmitEventDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                      End Date
                    </label>
                    <input
                      type="date"
                      value={submitEventEndDate}
                      onChange={(e) => setSubmitEventEndDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem'}}>
                  <div>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                      City
                    </label>
                    <input
                      type="text"
                      value={submitEventCity}
                      onChange={(e) => setSubmitEventCity(e.target.value)}
                      placeholder="e.g., Oklahoma City"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                      State
                    </label>
                    <select
                      value={submitEventState}
                      onChange={(e) => setSubmitEventState(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        fontSize: '1rem',
                        background: 'white'
                      }}
                    >
                      <option value="">Select</option>
                      <option value="AL">AL</option>
                      <option value="AK">AK</option>
                      <option value="AZ">AZ</option>
                      <option value="AR">AR</option>
                      <option value="CA">CA</option>
                      <option value="CO">CO</option>
                      <option value="CT">CT</option>
                      <option value="DE">DE</option>
                      <option value="FL">FL</option>
                      <option value="GA">GA</option>
                      <option value="HI">HI</option>
                      <option value="ID">ID</option>
                      <option value="IL">IL</option>
                      <option value="IN">IN</option>
                      <option value="IA">IA</option>
                      <option value="KS">KS</option>
                      <option value="KY">KY</option>
                      <option value="LA">LA</option>
                      <option value="ME">ME</option>
                      <option value="MD">MD</option>
                      <option value="MA">MA</option>
                      <option value="MI">MI</option>
                      <option value="MN">MN</option>
                      <option value="MS">MS</option>
                      <option value="MO">MO</option>
                      <option value="MT">MT</option>
                      <option value="NE">NE</option>
                      <option value="NV">NV</option>
                      <option value="NH">NH</option>
                      <option value="NJ">NJ</option>
                      <option value="NM">NM</option>
                      <option value="NY">NY</option>
                      <option value="NC">NC</option>
                      <option value="ND">ND</option>
                      <option value="OH">OH</option>
                      <option value="OK">OK</option>
                      <option value="OR">OR</option>
                      <option value="PA">PA</option>
                      <option value="RI">RI</option>
                      <option value="SC">SC</option>
                      <option value="SD">SD</option>
                      <option value="TN">TN</option>
                      <option value="TX">TX</option>
                      <option value="UT">UT</option>
                      <option value="VT">VT</option>
                      <option value="VA">VA</option>
                      <option value="WA">WA</option>
                      <option value="WV">WV</option>
                      <option value="WI">WI</option>
                      <option value="WY">WY</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                    Venue / Location
                  </label>
                  <input
                    type="text"
                    value={submitEventLocation}
                    onChange={(e) => setSubmitEventLocation(e.target.value)}
                    placeholder="e.g., Lazy E Arena"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                    Association
                  </label>
                  <input
                    type="text"
                    value={submitEventAssociation}
                    onChange={(e) => setSubmitEventAssociation(e.target.value)}
                    placeholder="e.g., NBHA, WPRA, Open"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                    Registration URL
                  </label>
                  <input
                    type="url"
                    value={submitEventUrl}
                    onChange={(e) => setSubmitEventUrl(e.target.value)}
                    placeholder="https://..."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div style={{display: 'flex', gap: '1rem', marginTop: '1.5rem', justifyContent: 'flex-end'}}>
                <button
                  onClick={() => {
                    setShowSubmitEventModal(false);
                    setSubmitEventName('');
                    setSubmitEventDate('');
                    setSubmitEventEndDate('');
                    setSubmitEventCity('');
                    setSubmitEventState('');
                    setSubmitEventLocation('');
                    setSubmitEventAssociation('');
                    setSubmitEventUrl('');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitEvent}
                  disabled={submitEventLoading || !submitEventName || !submitEventDate}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: submitEventLoading || !submitEventName || !submitEventDate ? '#9ca3af' : '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontWeight: '600',
                    cursor: submitEventLoading || !submitEventName || !submitEventDate ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {submitEventLoading ? 'Submitting...' : 'Submit Event'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Horse Care View */}
        {activeView === 'horse-care' && (
          <HorseCare
            horses={horses}
            user={user}
            API_URL={API_URL}
            isDemoMode={isDemoMode}
            setError={setError}
            setSuccess={setSuccess}
          />
        )}

        {/* Settings View */}
        {activeView === 'settings' && (
          <div>
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem'}}>Settings</h2>
            {/* Subscription Information */}
            <div style={{marginBottom: '2rem', padding: '1.5rem', background: '#d1fae5', borderRadius: '0.5rem', border: '1px solid #059669'}}>
              <h2 style={{fontSize: '1.25rem', fontWeight: '600', color: '#065f46', marginBottom: '1rem'}}>
                Subscription Status
              </h2>
              <div style={{marginBottom: '0.75rem'}}>
                <span style={{fontWeight: '600', color: '#047857'}}>Status: </span>
                <span style={{color: '#059669', fontWeight: '600', textTransform: 'capitalize'}}>
                  {subscriptionStatus === 'trialing' ? 'Free Trial' : subscriptionStatus}
                </span>
              </div>

              {subscriptionEndsAt && (
                <div style={{marginBottom: '0.75rem', fontSize: '0.875rem', color: '#047857'}}>
                  <span style={{fontWeight: '600'}}>Next Billing: </span>
                  {new Date(subscriptionEndsAt).toLocaleDateString()}
                </div>
              )}

              {hasStripeCustomer && (
                <button
                  onClick={handleManageSubscription}
                  disabled={loading}
                  style={{marginTop: '1rem', padding: '0.75rem 1.5rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: 'pointer'}}
                >
                  Manage Subscription
                </button>
              )}
            </div>
            {/* Change Password */}
            <div style={{background: 'white', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', marginBottom: '1.5rem'}}>
              <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>Change Password</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (isDemoMode) {
                  setError('Demo mode: Cannot change password');
                  return;
                }
                const form = e.target;
                const currentPassword = form.currentPassword.value;
                const newPassword = form.newPassword.value;
                const confirmPassword = form.confirmPassword.value;

                if (newPassword !== confirmPassword) {
                  setError('New passwords do not match');
                  return;
                }
                if (newPassword.length < 8) {
                  setError('New password must be at least 8 characters');
                  return;
                }

                setLoading(true);
                setError('');
                try {
                  const res = await fetch(`${API_URL}/auth/change-password`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ currentPassword, newPassword })
                  });
                  const data = await res.json();
                  if (res.ok) {
                    setSuccess('Password changed successfully!');
                    form.reset();
                  } else {
                    setError(data.message || 'Failed to change password');
                  }
                } catch (err) {
                  setError('Failed to change password');
                } finally {
                  setLoading(false);
                }
              }} style={{display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '400px'}}>
                <div>
                  <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem', color: '#374151'}}>Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    required
                    style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem', color: '#374151'}}>New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    required
                    minLength={8}
                    style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem', color: '#374151'}}>Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    required
                    minLength={8}
                    style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{padding: '0.75rem 1.5rem', background: loading ? '#9ca3af' : '#ea580c', color: 'white', border: 'none', borderRadius: '0.375rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '1rem'}}
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>

            {/* Horse Management */}
            <div style={{background: 'white', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', marginBottom: '1.5rem'}}>
              <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>Horse Management</h3>
              <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem'}}>
                Manage your horses - transfer ownership or delete horses and all associated data
              </p>

              {horses.length === 0 ? (
                <p style={{color: '#6b7280', fontStyle: 'italic'}}>No horses to manage. Add a horse from the dashboard.</p>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                  {horses.map(horse => (
                    <div key={horse.id} style={{padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div>
                        <p style={{fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem'}}>{horse.name}</p>
                        {getHorseAge(horse) && <p style={{fontSize: '0.75rem', color: '#6b7280'}}>Age: {getHorseAge(horse)}</p>}
                        {horse.registered_name && <p style={{fontSize: '0.75rem', color: '#6b7280'}}>Registered: {horse.registered_name}</p>}
                      </div>
                      <div style={{display: 'flex', gap: '0.5rem'}}>
                        <button
                          onClick={() => {
                            setHorseToEdit(horse);
                            setEditHorseName(horse.name);
                            setEditHorseFoalingDate(horse.foaling_date || '');
                            setEditHorseRegisteredName(horse.registered_name || '');
                            setShowEditHorseModal(true);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setHorseToTransfer(horse);
                            setShowTransferHorseModal(true);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          Transfer
                        </button>
                        <button
                          onClick={() => {
                            setHorseToDelete(horse);
                            setShowDeleteHorseModal(true);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Barn Code Management */}
            <div style={{background: 'white', padding: '2rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', marginBottom: '1.5rem'}}>
              <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>Barn Management</h3>

              {/* If user is a trainer (has barn_code) */}
              {user?.barn_code && (
                <div style={{padding: '1rem', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '0.5rem', marginBottom: '1rem'}}>
                  <p style={{fontSize: '0.875rem', color: '#166534', fontWeight: '600', marginBottom: '0.5rem'}}>Your Barn Code</p>
                  <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#15803d', fontFamily: 'monospace'}}>{user.barn_code}</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(user.barn_code);
                        setSuccess('Barn code copied to clipboard!');
                        setTimeout(() => setSuccess(''), 2000);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Copy Code
                    </button>
                  </div>
                  <p style={{fontSize: '0.875rem', color: '#166534', marginTop: '0.5rem'}}>
                    Share this code with clients so they can join your barn
                  </p>
                </div>
              )}

              {/* If user is part of a barn (has trainer_barn_code) */}
              {user?.trainer_barn_code && (
                <div style={{padding: '1rem', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '0.5rem', marginBottom: '1rem'}}>
                  <p style={{fontSize: '0.875rem', color: '#1e40af', fontWeight: '600', marginBottom: '0.5rem'}}>Current Barn</p>
                  <p style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#1e3a8a', fontFamily: 'monospace', marginBottom: '1rem'}}>{user.trainer_barn_code}</p>
                  <button
                    onClick={leaveBarn}
                    disabled={loading}
                    style={{
                      padding: '0.5rem 1rem',
                      background: loading ? '#9ca3af' : '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontWeight: '500',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    {loading ? 'Leaving...' : 'Leave Barn'}
                  </button>
                </div>
              )}

              {/* If user is not a trainer and not part of a barn */}
              {!user?.barn_code && !user?.trainer_barn_code && (
                <div>
                  {/* Create Barn Section */}
                  <div style={{padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem', marginBottom: '1.5rem'}}>
                    <h4 style={{fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem'}}>Become a Trainer</h4>
                    <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem'}}>
                      Create your own barn code and manage your clients' horses in one dashboard
                    </p>
                    <button
                      onClick={createBarn}
                      disabled={loading}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: loading ? '#9ca3af' : '#ea580c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      {loading ? 'Creating Barn...' : 'Create Barn Code'}
                    </button>
                  </div>

                  {/* Join Barn Section */}
                  <div style={{padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem'}}>
                    <h4 style={{fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem'}}>Join a Barn</h4>
                    <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem'}}>
                      Enter your trainer's barn code to give them access to view your horses
                    </p>
                    <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap'}}>
                      <input
                        type="text"
                        value={barnCode}
                        onChange={(e) => setBarnCode(e.target.value.toUpperCase())}
                        placeholder="BARN-XXXXX"
                        style={{
                          flex: '1',
                          minWidth: '200px',
                          padding: '0.625rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          fontSize: '1rem',
                          fontFamily: 'monospace',
                          textTransform: 'uppercase'
                        }}
                      />
                      <button
                        onClick={joinBarn}
                        disabled={loading}
                        style={{
                          padding: '0.625rem 1.5rem',
                          background: loading ? '#9ca3af' : '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          fontSize: '1rem'
                        }}
                      >
                        {loading ? 'Joining...' : 'Join Barn'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Business rule info */}
              <div style={{marginTop: '1.5rem', padding: '1rem', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '0.5rem'}}>
                <p style={{fontSize: '0.875rem', color: '#92400e', fontWeight: '600', marginBottom: '0.5rem'}}>Important Notes:</p>
                <ul style={{fontSize: '0.875rem', color: '#92400e', marginLeft: '1.25rem', lineHeight: '1.5'}}>
                  <li>Trainers cannot join other barns</li>
                  <li>Clients cannot become trainers while in a barn</li>
                  <li>You must leave your current barn before becoming a trainer</li>
                  <li>Trainers can view (but not edit) client horses and data</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* My Barn View (Trainers Only) */}
        {activeView === 'my-barn' && user?.barn_code && (
          <div>
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>My Barn Dashboard</h2>
            <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem'}}>
              Viewing all horses and data from clients in your barn
            </p>

            {/* Barn Summary */}
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Total Clients</p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#111827'}}>{barnClients.length}</p>
              </div>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Total Horses</p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#111827'}}>{barnHorses.length}</p>
              </div>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Total Races</p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#111827'}}>{barnRaces.length}</p>
              </div>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}>
                <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Total Earnings</p>
                <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#10b981'}}>
                  ${barnRaces.reduce((sum, r) => {
                    if (r.placements && r.placements.length > 0) {
                      return sum + r.placements.reduce((pSum, p) => pSum + (parseFloat(p.payout) || 0), 0);
                    }
                    return sum + (parseFloat(r.prize_money) || 0);
                  }, 0).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Client Filter */}
            {barnClients.length > 0 && (
              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Filter by Client
                </label>
                <select
                  value={selectedClient || ''}
                  onChange={(e) => setSelectedClient(e.target.value ? parseInt(e.target.value) : null)}
                  style={{padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem', maxWidth: '300px'}}
                >
                  <option value="">All Clients</option>
                  {barnClients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.first_name} {client.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Barn Horses */}
            {barnHorses.length === 0 ? (
              <div style={{background: 'white', padding: '3rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', textAlign: 'center'}}>
                <p style={{fontSize: '1.125rem', color: '#6b7280'}}>No clients have joined your barn yet.</p>
                <p style={{fontSize: '0.875rem', color: '#9ca3af', marginTop: '0.5rem'}}>
                  Share your barn code with clients to get started!
                </p>
                <button
                  onClick={() => setShowCancelBarnModal(true)}
                  style={{marginTop: '1.5rem', padding: '0.625rem 1.25rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500'}}
                >
                  Cancel Barn
                </button>
              </div>
            ) : (
              <div className="horse-cards-grid" data-count={barnHorses.filter(h => !selectedClient || h.user_id === selectedClient).length}>
                {barnHorses
                  .filter(h => !selectedClient || h.user_id === selectedClient)
                  .map(horse => {
                    const horseRaces = barnRaces.filter(r => r.horse_id === horse.id);
                    const horseExpenses = barnExpenses.filter(e => e.horse_id === horse.id);
                    const totalPrizes = horseRaces.reduce((sum, r) => {
                      if (r.placements && r.placements.length > 0) {
                        return sum + r.placements.reduce((pSum, p) => pSum + (parseFloat(p.payout) || 0), 0);
                      }
                      return sum + (parseFloat(r.prize_money) || 0);
                    }, 0);
                    const totalExpenseAmount = horseExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);
                    const entryFees = horseRaces.reduce((sum, r) => sum + (parseFloat(r.entry_fee) || 0), 0);
                    const totalExpenses = totalExpenseAmount + entryFees;
                    const netProfit = totalPrizes - totalExpenses;

                    return (
                      <div
                        key={horse.id}
                        className="horse-card"
                        style={{background: 'white', padding: '1.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s'}}
                        onClick={() => setExpandedBarnHorse({...horse, owner_name: `${horse.owner_first_name} ${horse.owner_last_name}`, races: horseRaces, expenses: horseExpenses})}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                        }}
                      >
                        {/* Owner Badge */}
                        <div style={{display: 'inline-block', padding: '0.25rem 0.75rem', background: '#dbeafe', color: '#1e40af', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', marginBottom: '1rem'}}>
                          {horse.owner_first_name} {horse.owner_last_name}
                        </div>

                        <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem'}}>{horse.name}</h3>
                        {horse.age && <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem'}}>Age: {horse.age}</p>}

                        <div className="stats-grid">
                          <div style={{textAlign: 'center', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem'}}>
                            <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Races</p>
                            <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827'}}>{horseRaces.length}</p>
                          </div>
                          <div style={{textAlign: 'center', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem'}}>
                            <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Earnings</p>
                            <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981'}}>${totalPrizes.toFixed(2)}</p>
                          </div>
                          <div style={{textAlign: 'center', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem'}}>
                            <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Expenses</p>
                            <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444'}}>${totalExpenses.toFixed(2)}</p>
                          </div>
                          <div style={{textAlign: 'center', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem'}}>
                            <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem'}}>Net Profit</p>
                            <p style={{fontSize: '1.5rem', fontWeight: 'bold', color: netProfit >= 0 ? '#10b981' : '#ef4444'}}>
                              ${netProfit.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* Expanded Barn Horse Modal */}
        {expandedBarnHorse && (() => {
          const horseRaces = expandedBarnHorse.races || [];
          const horseExpenses = expandedBarnHorse.expenses || [];

          // Calculate comprehensive stats
          const validRaces = horseRaces.filter(r => r.time && !r.no_time);
          const noTimeRaces = horseRaces.filter(r => r.no_time);
          const totalRaces = horseRaces.length;
          const bestTime = validRaces.length > 0 ? Math.min(...validRaces.map(r => parseFloat(r.time))).toFixed(3) : null;
          const avgTime = validRaces.length > 0 ? (validRaces.reduce((sum, r) => sum + parseFloat(r.time), 0) / validRaces.length).toFixed(3) : null;
          const noTimePercent = totalRaces > 0 ? ((noTimeRaces.length / totalRaces) * 100).toFixed(1) : 0;

          // Barrel issues breakdown
          const barrelIssueStats = {};
          noTimeRaces.forEach(race => {
            if (race.barrel_issues && race.barrel_issues.length > 0) {
              race.barrel_issues.forEach(issue => {
                barrelIssueStats[issue] = (barrelIssueStats[issue] || 0) + 1;
              });
            }
          });
          Object.keys(barrelIssueStats).forEach(issue => {
            barrelIssueStats[issue] = ((barrelIssueStats[issue] / noTimeRaces.length) * 100).toFixed(0);
          });

          // Financial stats
          const totalPrizes = horseRaces.reduce((sum, r) => {
            if (r.placements && r.placements.length > 0) {
              return sum + r.placements.reduce((pSum, p) => pSum + (parseFloat(p.payout) || 0), 0);
            }
            return sum + (parseFloat(r.prize_money) || 0);
          }, 0);
          const entryFees = horseRaces.reduce((sum, r) => sum + (parseFloat(r.entry_fee) || 0), 0);
          const expenseTotal = horseExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);
          const totalExpenses = entryFees + expenseTotal;
          const netProfit = totalPrizes - totalExpenses;

          return (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.7)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                animation: 'fadeIn 0.3s ease-in-out'
              }}
              onClick={() => setExpandedBarnHorse(null)}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  maxWidth: '1400px',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  position: 'relative',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                  animation: 'slideUp 0.3s ease-out'
                }}
                onClick={(e) => e.stopPropagation()}
                className="expanded-horse-modal"
              >
                {/* Close Button */}
                <button
                  onClick={() => setExpandedBarnHorse(null)}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    zIndex: 10
                  }}
                >
                  ×
                </button>

                {/* Owner Badge */}
                <div style={{display: 'inline-block', padding: '0.5rem 1rem', background: '#dbeafe', color: '#1e40af', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem'}}>
                  Owner: {expandedBarnHorse.owner_name}
                </div>

                {/* Horse Header */}
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '2rem'}}>
                  <div>
                    <h2 style={{fontSize: '2rem', fontWeight: 'bold', color: '#111827', margin: 0}}>{expandedBarnHorse.name}</h2>
                    {expandedBarnHorse.age && <p style={{color: '#6b7280', fontSize: '1rem', margin: '0.5rem 0 0 0'}}>Age: {expandedBarnHorse.age}</p>}
                  </div>
                  <Trophy size={48} style={{color: '#ea580c'}} />
                </div>

                {totalRaces > 0 ? (
                  <>
                    {/* Stats Grid */}
                    <div className="stats-grid" style={{marginBottom: '2rem', gap: '1.5rem'}}>
                      <div style={{background: '#f9fafb', padding: '1.5rem', borderRadius: '0.5rem'}}>
                        <p style={{color: '#6b7280', fontSize: '0.875rem', margin: 0}}>Total Races</p>
                        <p style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', margin: '0.5rem 0 0 0'}}>{totalRaces}</p>
                      </div>
                      <div style={{background: '#f9fafb', padding: '1.5rem', borderRadius: '0.5rem'}}>
                        <p style={{color: '#6b7280', fontSize: '0.875rem', margin: 0}}>Best Time</p>
                        <p style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#ea580c', margin: '0.5rem 0 0 0'}}>
                          {bestTime || '-'}s
                        </p>
                      </div>
                      <div style={{background: '#f9fafb', padding: '1.5rem', borderRadius: '0.5rem'}}>
                        <p style={{color: '#6b7280', fontSize: '0.875rem', margin: 0}}>Avg Time</p>
                        <p style={{fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', margin: '0.5rem 0 0 0'}}>
                          {avgTime || '-'}s
                        </p>
                      </div>
                      <div style={{background: '#f9fafb', padding: '1.5rem', borderRadius: '0.5rem'}}>
                        <p style={{color: '#6b7280', fontSize: '0.875rem', margin: 0}}>No-Time %</p>
                        <p style={{fontSize: '2.5rem', fontWeight: 'bold', color: parseFloat(noTimePercent) > 20 ? '#dc2626' : '#111827', margin: '0.5rem 0 0 0'}}>
                          {noTimePercent}%
                        </p>
                      </div>
                    </div>

                    {/* Barrel Issues */}
                    {noTimeRaces.length > 0 && Object.keys(barrelIssueStats).length > 0 && (
                      <div style={{marginBottom: '2rem', padding: '1.5rem', background: '#fef3c7', borderRadius: '0.5rem', border: '1px solid #fbbf24'}}>
                        <p style={{fontSize: '1rem', fontWeight: '600', color: '#92400e', margin: '0 0 1rem 0'}}>Common Issues:</p>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: '1rem'}}>
                          {Object.entries(barrelIssueStats).map(([issue, percent]) => (
                            <span key={issue} style={{fontSize: '1rem', padding: '0.5rem 1rem', background: 'white', borderRadius: '0.5rem', color: '#92400e', fontWeight: '500'}}>
                              {issue === 'off-pattern' ? 'Off-Pattern' : `Barrel ${issue}`}: {percent}%
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Rider Breakdown */}
                    {(() => {
                      const ownerFirstName = expandedBarnHorse.owner_first_name || 'Owner';
                      const uniqueRiders = [...new Set(horseRaces.map(r => r.rider_name || ownerFirstName))];
                      if (uniqueRiders.length > 1) {
                        const riderStats = uniqueRiders.map(riderName => {
                          const riderRaces = horseRaces.filter(r => (r.rider_name || ownerFirstName) === riderName);
                          const riderValidRaces = riderRaces.filter(r => r.time && !r.no_time);
                          const riderNoTimeRaces = riderRaces.filter(r => r.no_time);
                          const riderBestTime = riderValidRaces.length > 0 ? Math.min(...riderValidRaces.map(r => parseFloat(r.time))).toFixed(3) : null;
                          const riderAvgTime = riderValidRaces.length > 0 ? (riderValidRaces.reduce((sum, r) => sum + parseFloat(r.time), 0) / riderValidRaces.length).toFixed(3) : null;
                          const riderNoTimePercent = riderRaces.length > 0 ? ((riderNoTimeRaces.length / riderRaces.length) * 100).toFixed(1) : 0;

                          return {
                            name: riderName,
                            totalRaces: riderRaces.length,
                            bestTime: riderBestTime,
                            avgTime: riderAvgTime,
                            noTimePercent: riderNoTimePercent
                          };
                        }).sort((a, b) => b.totalRaces - a.totalRaces);

                        return (
                          <div style={{marginBottom: '2rem', padding: '1.5rem', background: '#f5f3ff', borderRadius: '0.5rem', border: '1px solid #c4b5fd'}}>
                            <h3 style={{fontSize: '1.125rem', fontWeight: '600', color: '#5b21b6', margin: '0 0 1rem 0'}}>Performance by Rider</h3>
                            <div style={{display: 'grid', gap: '1rem'}}>
                              {riderStats.map(rider => (
                                <div key={rider.name} style={{background: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e9d5ff'}}>
                                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem'}}>
                                    <h4 style={{fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0}}>
                                      <span style={{color: '#7c3aed'}}>{rider.name}</span>
                                    </h4>
                                    <span style={{fontSize: '0.875rem', color: '#6b7280', fontWeight: '500'}}>
                                      {rider.totalRaces} {rider.totalRaces === 1 ? 'race' : 'races'}
                                    </span>
                                  </div>
                                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem'}}>
                                    <div>
                                      <p style={{fontSize: '0.75rem', color: '#6b7280', margin: 0}}>Best Time</p>
                                      <p style={{fontSize: '1.125rem', fontWeight: '600', color: '#ea580c', margin: '0.25rem 0 0 0'}}>
                                        {rider.bestTime ? `${rider.bestTime}s` : '-'}
                                      </p>
                                    </div>
                                    <div>
                                      <p style={{fontSize: '0.75rem', color: '#6b7280', margin: 0}}>Avg Time</p>
                                      <p style={{fontSize: '1.125rem', fontWeight: '600', color: '#111827', margin: '0.25rem 0 0 0'}}>
                                        {rider.avgTime ? `${rider.avgTime}s` : '-'}
                                      </p>
                                    </div>
                                    <div>
                                      <p style={{fontSize: '0.75rem', color: '#6b7280', margin: 0}}>No-Time %</p>
                                      <p style={{fontSize: '1.125rem', fontWeight: '600', color: parseFloat(rider.noTimePercent) > 20 ? '#dc2626' : '#111827', margin: '0.25rem 0 0 0'}}>
                                        {rider.noTimePercent}%
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Financial Summary */}
                    <div style={{padding: '1.5rem', background: '#f0fdf4', borderRadius: '0.5rem', marginBottom: '2rem'}}>
                      <div className="expanded-horse-financial-grid" style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '2rem'}}>
                        <div>
                          <p style={{color: '#065f46', fontSize: '0.875rem', margin: 0}}>Total Prizes</p>
                          <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#059669', margin: '0.5rem 0 0 0'}}>
                            ${totalPrizes.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p style={{color: '#7c2d12', fontSize: '0.875rem', margin: 0}}>Total Expenses</p>
                          <p style={{fontSize: '2rem', fontWeight: 'bold', color: '#dc2626', margin: '0.5rem 0 0 0'}}>
                            ${totalExpenses.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p style={{fontSize: '0.875rem', margin: 0, color: netProfit >= 0 ? '#065f46' : '#7c2d12', fontWeight: '600'}}>Net Profit</p>
                          <p style={{fontSize: '2rem', fontWeight: 'bold', margin: '0.5rem 0 0 0', color: netProfit >= 0 ? '#059669' : '#dc2626'}}>
                            ${netProfit.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Performance Chart */}
                    <div style={{marginBottom: '2rem'}}>
                      <h3 style={{fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem'}}>Performance Over Time</h3>
                      <PerformanceChart horseId={expandedBarnHorse.id} />
                    </div>

                    {/* Race List */}
                    <div style={{marginBottom: '2rem'}}>
                      <h3 style={{fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem'}}>Race History</h3>
                      <div style={{background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse'}}>
                          <thead style={{background: '#f9fafb'}}>
                            <tr>
                              <th style={{padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Date</th>
                              <th style={{padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Rider</th>
                              <th style={{padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Venue</th>
                              <th style={{padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Time</th>
                              <th style={{padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Prize</th>
                            </tr>
                          </thead>
                          <tbody>
                            {horseRaces.sort((a, b) => new Date(b.date) - new Date(a.date)).map((race, index) => (
                              <tr key={race.id} style={{borderBottom: index < horseRaces.length - 1 ? '1px solid #e5e7eb' : 'none'}}>
                                <td style={{padding: '0.75rem', fontSize: '0.875rem', color: '#111827'}}>
                                  {parseLocalDate(race.date).toLocaleDateString()}
                                </td>
                                <td style={{padding: '0.75rem', fontSize: '0.875rem'}}>
                                  {race.rider_name ? (
                                    <span style={{fontWeight: '500', color: '#7c3aed'}}>{race.rider_name}</span>
                                  ) : (
                                    <span style={{color: '#6b7280', fontStyle: 'italic'}}>Owner</span>
                                  )}
                                </td>
                                <td style={{padding: '0.75rem', fontSize: '0.875rem', color: '#111827'}}>{race.venue}</td>
                                <td style={{padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: race.no_time ? '#dc2626' : '#ea580c'}}>
                                  {race.time ? `${race.time}s` : '-'}
                                </td>
                                <td style={{padding: '0.75rem', fontSize: '0.875rem', color: '#059669', fontWeight: '600'}}>
                                  {race.placements && race.placements.length > 0 ? (
                                    `$${race.placements.reduce((sum, p) => sum + (parseFloat(p.payout) || 0), 0).toFixed(2)}`
                                  ) : race.prize_money ? `$${parseFloat(race.prize_money).toFixed(2)}` : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Expenses List */}
                    {horseExpenses.length > 0 && (
                      <div>
                        <h3 style={{fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem'}}>Expenses</h3>
                        <div style={{background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden'}}>
                          <table style={{width: '100%', borderCollapse: 'collapse'}}>
                            <thead style={{background: '#f9fafb'}}>
                              <tr>
                                <th style={{padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Date</th>
                                <th style={{padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Category</th>
                                <th style={{padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Description</th>
                                <th style={{padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase'}}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {horseExpenses.sort((a, b) => new Date(b.date) - new Date(a.date)).map((expense, index) => (
                                <tr key={expense.id} style={{borderBottom: index < horseExpenses.length - 1 ? '1px solid #e5e7eb' : 'none'}}>
                                  <td style={{padding: '0.75rem', fontSize: '0.875rem', color: '#111827'}}>
                                    {new Date(expense.date).toLocaleDateString()}
                                  </td>
                                  <td style={{padding: '0.75rem', fontSize: '0.875rem', color: '#111827'}}>{expense.category}</td>
                                  <td style={{padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280'}}>{expense.description || '-'}</td>
                                  <td style={{padding: '0.75rem', fontSize: '0.875rem', color: '#dc2626', fontWeight: '600'}}>
                                    ${parseFloat(expense.amount).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p style={{color: '#6b7280', fontSize: '1rem', textAlign: 'center', padding: '2rem'}}>No race data yet for this horse</p>
                )}
              </div>
            </div>
          );
        })()}

      {/* Help/Support Modal */}
      {showHelpModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem'}}>
          <div style={{background: 'white', borderRadius: '0.75rem', maxWidth: '500px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', maxHeight: '90vh', overflow: 'auto'}}>
            <div style={{padding: '2rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0}}>Help & Support</h2>
                <button
                  onClick={() => {
                    setShowHelpModal(false);
                    setHelpError('');
                    setHelpSuccess('');
                  }}
                  style={{background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.5rem'}}
                >
                  <X size={24} />
                </button>
              </div>

              {helpSuccess && (
                <div style={{background: '#d1fae5', border: '1px solid #10b981', color: '#065f46', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem'}}>
                  {helpSuccess}
                </div>
              )}

              {helpError && (
                <div style={{background: '#fee2e2', border: '1px solid #ef4444', color: '#991b1b', padding: '0.75rem', borderRadius: '0.375rem', marginBottom: '1rem'}}>
                  {helpError}
                </div>
              )}

              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem', color: '#374151'}}>
                  Subject
                </label>
                <input
                  type="text"
                  value={helpSubject}
                  onChange={(e) => setHelpSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                />
              </div>

              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontWeight: '500', marginBottom: '0.5rem', color: '#374151'}}>
                  Message
                </label>
                <textarea
                  value={helpMessage}
                  onChange={(e) => setHelpMessage(e.target.value)}
                  placeholder="Please describe your issue in detail..."
                  rows={6}
                  style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem', resize: 'vertical'}}
                />
              </div>

              <div style={{display: 'flex', gap: '0.75rem', justifyContent: 'flex-end'}}>
                <button
                  onClick={() => {
                    setShowHelpModal(false);
                    setHelpError('');
                    setHelpSuccess('');
                  }}
                  disabled={helpLoading}
                  style={{padding: '0.625rem 1.25rem', border: '1px solid #d1d5db', background: 'white', color: '#374151', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '500'}}
                >
                  Cancel
                </button>
                <button
                  onClick={handleHelpSubmit}
                  disabled={helpLoading}
                  style={{padding: '0.625rem 1.25rem', border: 'none', background: '#ea580c', color: 'white', borderRadius: '0.375rem', cursor: helpLoading ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: helpLoading ? 0.5 : 1}}
                >
                  {helpLoading ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Barn Confirmation Modal */}
      {showCancelBarnModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem'}}>
          <div style={{background: 'white', borderRadius: '0.75rem', maxWidth: '400px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'}}>
            <div style={{padding: '2rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
                <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', margin: 0}}>Cancel Barn</h2>
                <button
                  onClick={() => setShowCancelBarnModal(false)}
                  disabled={cancelBarnLoading}
                  style={{background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.5rem'}}
                >
                  <X size={24} />
                </button>
              </div>

              <p style={{color: '#6b7280', marginBottom: '1.5rem'}}>
                Are you sure you want to cancel your barn? This will permanently remove your barn code and you'll need to create a new one if you want to start a barn again.
              </p>

              <div style={{display: 'flex', gap: '0.75rem', justifyContent: 'flex-end'}}>
                <button
                  onClick={() => setShowCancelBarnModal(false)}
                  disabled={cancelBarnLoading}
                  style={{padding: '0.625rem 1.25rem', border: '1px solid #d1d5db', background: 'white', color: '#374151', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: '500'}}
                >
                  Keep Barn
                </button>
                <button
                  onClick={handleCancelBarn}
                  disabled={cancelBarnLoading}
                  style={{padding: '0.625rem 1.25rem', border: 'none', background: '#ef4444', color: 'white', borderRadius: '0.375rem', cursor: cancelBarnLoading ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: cancelBarnLoading ? 0.5 : 1}}
                >
                  {cancelBarnLoading ? 'Canceling...' : 'Cancel Barn'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Demo Email Capture Modal */}
      {showDemoEmailModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem'}}>
          <div style={{background: 'white', borderRadius: '1rem', maxWidth: '450px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)'}}>
            <div style={{padding: '2rem'}}>
              <div style={{textAlign: 'center', marginBottom: '1.5rem'}}>
                <img src="/logo.png" alt="SaddlePulse Logo" style={{height: '80px', width: 'auto', margin: '0 auto 1rem auto', display: 'block'}} />
                <h2 style={{fontSize: '1.75rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem'}}>
                  Welcome to SaddlePulse Demo
                </h2>
                <p style={{fontSize: '1rem', color: '#6b7280'}}>
                  See how SaddlePulse can help you track your barrel racing performance
                </p>
              </div>

              <div style={{marginBottom: '1.5rem'}}>
                <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                  Email (optional)
                </label>
                <input
                  type="email"
                  value={demoEmail}
                  onChange={(e) => setDemoEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleDemoEmailCapture()}
                  placeholder="your@email.com"
                  style={{width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', fontSize: '1rem'}}
                />
                <p style={{fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem'}}>
                  Optional: We'll send you tips and updates about SaddlePulse
                </p>
              </div>

              <button
                onClick={handleDemoEmailCapture}
                disabled={demoEmailLoading}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: '#ea580c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  cursor: demoEmailLoading ? 'not-allowed' : 'pointer',
                  marginBottom: '0.75rem',
                  opacity: demoEmailLoading ? 0.5 : 1
                }}
              >
                {demoEmailLoading ? 'Loading...' : 'Try Demo Now'}
              </button>

              <button
                onClick={() => {
                  setShowDemoEmailModal(false);
                  handleDemoLogin();
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'transparent',
                  color: '#6b7280',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Skip and continue to demo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Demo Modal */}
      {showDemoModal && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem'}}>
          <div style={{background: 'white', borderRadius: '1rem', maxWidth: '600px', width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)', maxHeight: '90vh', overflow: 'auto'}}>
            <div style={{padding: '2rem'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
                <h2 style={{fontSize: '1.75rem', fontWeight: 'bold', color: '#1f2937', margin: 0}}>
                  Welcome to SaddlePulse
                </h2>
                <button
                  onClick={() => {
                    setShowDemoModal(false);
                    setDemoStep(0);
                  }}
                  style={{background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.5rem'}}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Feature Card */}
              <div style={{background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', padding: '2rem', borderRadius: '0.75rem', marginBottom: '2rem', color: 'white'}}>
                <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: 'white'}}>
                  {demoFeatures[demoStep].title}
                </h3>
                <p style={{fontSize: '1rem', marginBottom: '1.5rem', color: 'rgba(255,255,255,0.95)'}}>
                  {demoFeatures[demoStep].description}
                </p>
                <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                  {demoFeatures[demoStep].features.map((feature, idx) => (
                    <li key={idx} style={{display: 'flex', alignItems: 'center', marginBottom: '0.75rem', fontSize: '0.95rem'}}>
                      <span style={{marginRight: '0.75rem'}}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Progress Dots */}
              <div style={{display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '2rem'}}>
                {demoFeatures.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setDemoStep(idx)}
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      border: 'none',
                      background: idx === demoStep ? '#f97316' : '#d1d5db',
                      cursor: 'pointer',
                      transition: 'all 0.3s'
                    }}
                  />
                ))}
              </div>

              {/* Step Counter */}
              <div style={{textAlign: 'center', marginBottom: '2rem', color: '#6b7280', fontSize: '0.875rem'}}>
                Step {demoStep + 1} of {demoFeatures.length}
              </div>

              {/* Navigation Buttons */}
              <div style={{display: 'flex', gap: '0.75rem', justifyContent: 'space-between', marginBottom: '1.5rem'}}>
                <button
                  onClick={() => setDemoStep(Math.max(0, demoStep - 1))}
                  disabled={demoStep === 0}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    color: '#374151',
                    borderRadius: '0.5rem',
                    cursor: demoStep === 0 ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: demoStep === 0 ? 0.5 : 1,
                    flex: 1
                  }}
                >
                  Previous
                </button>
                {demoStep < demoFeatures.length - 1 ? (
                  <button
                    onClick={() => setDemoStep(Math.min(demoFeatures.length - 1, demoStep + 1))}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      background: '#f97316',
                      color: 'white',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontWeight: '500',
                      flex: 1
                    }}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowDemoModal(false);
                      setDemoStep(0);
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      border: 'none',
                      background: '#10b981',
                      color: 'white',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      fontWeight: '500',
                      flex: 1
                    }}
                  >
                    Get Started
                  </button>
                )}
              </div>

              {/* Login with Demo Account */}
              <div style={{textAlign: 'center', padding: '1.5rem', background: '#f3f4f6', borderRadius: '0.5rem'}}>
                <p style={{fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem'}}>
                  Want to explore first? Try the demo account:
                </p>
                <button
                  onClick={handleDemoLogin}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  {loading ? 'Loading...' : '🐴 Try Demo Account (Read-Only)'}
                </button>
                <p style={{fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem', fontStyle: 'italic'}}>
                  View sample data without making any changes
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Horse Confirmation Modal */}
      {/* Edit Horse Modal */}
      {showEditHorseModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={() => {
            setShowEditHorseModal(false);
            setHorseToEdit(null);
            setEditHorseName('');
            setEditHorseFoalingDate('');
            setEditHorseRegisteredName('');
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>
              Edit Horse
            </h2>
            <p style={{color: '#6b7280', marginBottom: '1.5rem'}}>
              Update the details for <strong>{horseToEdit?.name}</strong>
            </p>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Horse Name *
              </label>
              <input
                type="text"
                value={editHorseName}
                onChange={(e) => setEditHorseName(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                placeholder="e.g., Thunder"
              />
            </div>

            <div style={{marginBottom: '1rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Foaling Date (Optional)
              </label>
              <input
                type="date"
                value={editHorseFoalingDate}
                onChange={(e) => setEditHorseFoalingDate(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
              />
              {horseToEdit?.age && !editHorseFoalingDate && (
                <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem'}}>
                  Currently using static age: {horseToEdit.age} years old
                </p>
              )}
            </div>

            <div style={{marginBottom: '1.5rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Registered Name (Optional)
              </label>
              <input
                type="text"
                value={editHorseRegisteredName}
                onChange={(e) => setEditHorseRegisteredName(e.target.value)}
                style={{width: '100%', padding: '0.625rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '1rem'}}
                placeholder="Official registered name"
              />
            </div>

            <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
              <button
                onClick={() => {
                  setShowEditHorseModal(false);
                  setHorseToEdit(null);
                  setEditHorseName('');
                  setEditHorseFoalingDate('');
                  setEditHorseRegisteredName('');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#111827',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                disabled={editHorseLoading}
              >
                Cancel
              </button>
              <button
                onClick={editHorse}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: editHorseLoading ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontWeight: '600',
                  cursor: editHorseLoading ? 'not-allowed' : 'pointer'
                }}
                disabled={editHorseLoading || !editHorseName.trim()}
              >
                {editHorseLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Horse Modal */}
      {showDeleteHorseModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={() => {
            setShowDeleteHorseModal(false);
            setHorseToDelete(null);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>
              Delete Horse?
            </h2>
            <p style={{color: '#6b7280', marginBottom: '1.5rem'}}>
              Are you sure you want to delete <strong>{horseToDelete?.name}</strong>?
              This will permanently delete:
            </p>
            <ul style={{color: '#6b7280', marginBottom: '1.5rem', paddingLeft: '1.5rem'}}>
              <li>All race records for this horse</li>
              <li>All expense records for this horse</li>
              <li>All horse care data (feeding, medications, supplements)</li>
              <li>All documents for this horse</li>
            </ul>
            <p style={{color: '#dc2626', fontWeight: '600', marginBottom: '1.5rem'}}>
              This action cannot be undone!
            </p>
            <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
              <button
                onClick={() => {
                  setShowDeleteHorseModal(false);
                  setHorseToDelete(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#111827',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                disabled={deleteHorseLoading}
              >
                Cancel
              </button>
              <button
                onClick={deleteHorse}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: deleteHorseLoading ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontWeight: '600',
                  cursor: deleteHorseLoading ? 'not-allowed' : 'pointer'
                }}
                disabled={deleteHorseLoading}
              >
                {deleteHorseLoading ? 'Deleting...' : 'Delete Horse'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Horse Modal */}
      {showTransferHorseModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
          onClick={() => {
            setShowTransferHorseModal(false);
            setHorseToTransfer(null);
            setTransferEmail('');
            setError('');
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '0.75rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>
              Transfer Horse
            </h2>
            <p style={{color: '#6b7280', marginBottom: '1.5rem'}}>
              Transfer <strong>{horseToTransfer?.name}</strong> to another SaddlePulse user.
              Race data and horse care records will transfer. Expenses will NOT be transferred.
            </p>
            <div style={{marginBottom: '1.5rem'}}>
              <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                Recipient Email Address <span style={{color: '#dc2626'}}>*</span>
              </label>
              <input
                type="email"
                value={transferEmail}
                onChange={(e) => setTransferEmail(e.target.value)}
                placeholder="user@example.com"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '1rem'
                }}
                disabled={transferHorseLoading}
              />
              <p style={{fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem'}}>
                The recipient must have a SaddlePulse account
              </p>
            </div>
            <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
              <button
                onClick={() => {
                  setShowTransferHorseModal(false);
                  setHorseToTransfer(null);
                  setTransferEmail('');
                  setError('');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#111827',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                disabled={transferHorseLoading}
              >
                Cancel
              </button>
              <button
                onClick={transferHorse}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: transferHorseLoading || !transferEmail.trim() ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontWeight: '600',
                  cursor: transferHorseLoading || !transferEmail.trim() ? 'not-allowed' : 'pointer'
                }}
                disabled={transferHorseLoading || !transferEmail.trim()}
              >
                {transferHorseLoading ? 'Transferring...' : 'Transfer Horse'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Continue Tutorial Floating Button */}
      {!showOnboarding && onboardingStep > 0 && onboardingStep < 6 && user && !user.onboarding_completed && (
        <button
          onClick={() => setShowOnboarding(true)}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            padding: '1rem 1.5rem',
            background: '#ea580c',
            color: 'white',
            border: 'none',
            borderRadius: '9999px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '1rem',
            boxShadow: '0 10px 25px rgba(234, 88, 12, 0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            animation: 'pulse 2s infinite'
          }}
        >
          Continue Tutorial ({onboardingStep}/5)
        </button>
      )}

      {/* Onboarding Modal */}
      {showOnboarding && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          zIndex: 3000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
          }}>
            {/* Progress indicator */}
            <div style={{display: 'flex', gap: '0.5rem', marginBottom: '2rem', justifyContent: 'center'}}>
              {[1, 2, 3, 4, 5].map(step => (
                <div key={step} style={{
                  width: '2rem',
                  height: '0.375rem',
                  borderRadius: '0.25rem',
                  background: step === onboardingStep ? '#ea580c' : step < onboardingStep ? '#10b981' : '#e5e7eb'
                }} />
              ))}
            </div>

            {onboardingStep === 1 && (
              <div>
                <h2 style={{fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem', textAlign: 'center'}}>
                  Welcome to SaddlePulse! 🎉
                </h2>
                <p style={{color: '#6b7280', fontSize: '1rem', marginBottom: '2rem', textAlign: 'center'}}>
                  Let's get you started by adding your first horse. This will take less than 2 minutes!
                </p>

                {error && (
                  <div style={{padding: '0.75rem', background: '#fee2e2', color: '#991b1b', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem', textAlign: 'center'}}>
                    {error}
                  </div>
                )}

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                    Horse Name <span style={{color: '#dc2626'}}>*</span>
                  </label>
                  <input
                    type="text"
                    value={horseName}
                    onChange={(e) => setHorseName(e.target.value)}
                    style={{width: '100%', padding: '0.75rem', border: '2px solid #d1d5db', borderRadius: '0.5rem', fontSize: '1rem'}}
                    placeholder="e.g., Thunder"
                  />
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                    Foaling Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={horseFoalingDate}
                    onChange={(e) => setHorseFoalingDate(e.target.value)}
                    style={{width: '100%', padding: '0.75rem', border: '2px solid #d1d5db', borderRadius: '0.5rem', fontSize: '1rem'}}
                  />
                </div>

                <div style={{marginBottom: '2rem'}}>
                  <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                    Registered Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={registeredName}
                    onChange={(e) => setRegisteredName(e.target.value)}
                    style={{width: '100%', padding: '0.75rem', border: '2px solid #d1d5db', borderRadius: '0.5rem', fontSize: '1rem'}}
                    placeholder="Official registered name"
                  />
                </div>

                <div style={{display: 'flex', gap: '1rem'}}>
                  <button
                    onClick={async () => {
                      if (!horseName.trim()) {
                        setError('Please enter a horse name');
                        return;
                      }

                      try {
                        const response = await fetch(`${API_URL}/horses`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem('token')}`
                          },
                          body: JSON.stringify({
                            name: horseName,
                            foaling_date: horseFoalingDate || null,
                            registered_name: registeredName || null
                          })
                        });

                        if (response.ok) {
                          await loadHorses();
                          setHorseName('');
                          setHorseFoalingDate('');
                          setRegisteredName('');
                          setOnboardingStep(2);
                          setError('');
                        } else {
                          const data = await response.json();
                          setError(data.message || 'Failed to add horse');
                        }
                      } catch (err) {
                        setError('Failed to add horse');
                      }
                    }}
                    disabled={!horseName.trim()}
                    style={{
                      flex: 1,
                      padding: '0.875rem',
                      background: !horseName.trim() ? '#9ca3af' : '#ea580c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: '600',
                      cursor: !horseName.trim() ? 'not-allowed' : 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Add Horse & Continue
                  </button>
                  <button
                    onClick={() => {
                      setOnboardingStep(2);
                      setHorseName('');
                      setHorseFoalingDate('');
                      setRegisteredName('');
                    }}
                    style={{
                      padding: '0.875rem 1.5rem',
                      background: 'transparent',
                      color: '#6b7280',
                      border: '2px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}

            {onboardingStep === 2 && (
              <div>
                <h2 style={{fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem', textAlign: 'center'}}>
                  Step 2: Add Your First Race 🏁
                </h2>
                <p style={{color: '#6b7280', fontSize: '1rem', marginBottom: '2rem', textAlign: 'center'}}>
                  Track race results to see performance analytics and trends over time.
                </p>

                {horses.length > 0 ? (
                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem'}}>
                      Select Horse <span style={{color: '#dc2626'}}>*</span>
                    </label>
                    <select
                      value={selectedHorse || ''}
                      onChange={(e) => setSelectedHorse(e.target.value)}
                      style={{width: '100%', padding: '0.75rem', border: '2px solid #d1d5db', borderRadius: '0.5rem', fontSize: '1rem'}}
                    >
                      <option value="">Choose a horse...</option>
                      {horses.map(h => (
                        <option key={h.id} value={h.id}>{h.name}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div style={{padding: '1rem', background: '#fef3c7', borderRadius: '0.5rem', marginBottom: '1.5rem'}}>
                    <p style={{color: '#92400e', fontSize: '0.875rem', margin: 0}}>
                      You skipped adding a horse. You'll need at least one horse to add races later.
                    </p>
                  </div>
                )}

                <div style={{display: 'flex', gap: '1rem'}}>
                  {horses.length > 0 ? (
                    <button
                      onClick={() => {
                        setActiveView('add-race');
                        setOnboardingStep(3); // Advance to next step
                        setShowOnboarding(false); // Hide modal so user can interact
                      }}
                      style={{
                        flex: 1,
                        padding: '0.875rem',
                        background: '#ea580c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      Add a Race
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setOnboardingStep(3);
                      }}
                      style={{
                        flex: 1,
                        padding: '0.875rem',
                        background: '#ea580c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      Continue
                    </button>
                  )}
                  <button
                    onClick={() => setOnboardingStep(3)}
                    style={{
                      padding: '0.875rem 1.5rem',
                      background: 'transparent',
                      color: '#6b7280',
                      border: '2px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Skip
                  </button>
                  <button
                    onClick={() => setOnboardingStep(1)}
                    style={{
                      padding: '0.875rem 1.5rem',
                      background: 'transparent',
                      color: '#6b7280',
                      border: '2px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}

            {onboardingStep === 3 && (
              <div>
                <h2 style={{fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem', textAlign: 'center'}}>
                  Step 3: Add a Calendar Event 📅
                </h2>
                <p style={{color: '#6b7280', fontSize: '1rem', marginBottom: '2rem', textAlign: 'center'}}>
                  Keep track of upcoming competitions, vet appointments, farrier visits, and more!
                </p>

                <div style={{display: 'flex', gap: '1rem'}}>
                  <button
                    onClick={() => {
                      setActiveView('calendar');
                      setOnboardingStep(4); // Advance to next step
                      setShowOnboarding(false); // Hide modal so user can interact
                    }}
                    style={{
                      flex: 1,
                      padding: '0.875rem',
                      background: '#ea580c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Go to Calendar
                  </button>
                  <button
                    onClick={() => setOnboardingStep(4)}
                    style={{
                      padding: '0.875rem 1.5rem',
                      background: 'transparent',
                      color: '#6b7280',
                      border: '2px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Skip
                  </button>
                </div>
                <button
                  onClick={() => setOnboardingStep(2)}
                  style={{
                    width: '100%',
                    marginTop: '0.5rem',
                    padding: '0.75rem',
                    background: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Back
                </button>
              </div>
            )}

            {onboardingStep === 4 && (
              <div>
                <h2 style={{fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem', textAlign: 'center'}}>
                  Step 4: Upload Horse Documents 📄
                </h2>
                <p style={{color: '#6b7280', fontSize: '1rem', marginBottom: '2rem', textAlign: 'center'}}>
                  Store important documents like Coggins tests, health certificates, and vaccination records. Never lose track of expiration dates!
                </p>

                {horses.length > 0 ? (
                  <div>
                    <div style={{padding: '1rem', background: '#dbeafe', borderRadius: '0.5rem', marginBottom: '1.5rem'}}>
                      <p style={{color: '#1e40af', fontSize: '0.875rem', margin: 0}}>
                        💡 Tip: SaddlePulse will remind you when documents like Coggins are about to expire.
                      </p>
                    </div>

                    <div style={{display: 'flex', gap: '1rem'}}>
                      <button
                        onClick={() => {
                          setActiveView('horse-care');
                          setOnboardingStep(5); // Advance to settings step
                          setShowOnboarding(false); // Hide modal so user can interact
                        }}
                        style={{
                          flex: 1,
                          padding: '0.875rem',
                          background: '#ea580c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.5rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '1rem'
                        }}
                      >
                        Go to Horse Care
                      </button>
                      <button
                        onClick={() => setOnboardingStep(5)}
                        style={{
                          padding: '0.875rem 1.5rem',
                          background: 'transparent',
                          color: '#6b7280',
                          border: '2px solid #d1d5db',
                          borderRadius: '0.5rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        Skip
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{padding: '1rem', background: '#fef3c7', borderRadius: '0.5rem', marginBottom: '1.5rem'}}>
                      <p style={{color: '#92400e', fontSize: '0.875rem', margin: 0}}>
                        You need to add a horse first before uploading documents. You can do this anytime from the dashboard!
                      </p>
                    </div>

                    <button
                      onClick={() => setOnboardingStep(5)}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        background: '#ea580c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      Continue
                    </button>
                  </div>
                )}

                <button
                  onClick={() => setOnboardingStep(3)}
                  style={{
                    width: '100%',
                    marginTop: '0.5rem',
                    padding: '0.75rem',
                    background: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Back
                </button>
              </div>
            )}

            {onboardingStep === 5 && (
              <div>
                <h2 style={{fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem', textAlign: 'center'}}>
                  Step 5: Understand Barn Management ⚙️
                </h2>
                <p style={{color: '#6b7280', fontSize: '1rem', marginBottom: '2rem', textAlign: 'center'}}>
                  Learn how to collaborate with trainers and manage your barn!
                </p>

                <div style={{background: '#f0f9ff', padding: '1.5rem', borderRadius: '0.5rem', marginBottom: '1.5rem', border: '2px solid #3b82f6'}}>
                  <h3 style={{fontSize: '1.125rem', fontWeight: '600', color: '#1e40af', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <Users size={20} />
                    Barn Trainer Mode
                  </h3>
                  <p style={{color: '#374151', fontSize: '0.875rem', marginBottom: '0.75rem'}}>
                    <strong>For Professional Trainers:</strong> Create your barn and share your unique barn code with clients.
                  </p>
                  <ul style={{listStyle: 'none', padding: 0, margin: 0, fontSize: '0.875rem', color: '#374151'}}>
                    <li style={{padding: '0.25rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#3b82f6', marginRight: '0.5rem'}}>•</span>
                      <span>View and manage all client horses in one place</span>
                    </li>
                    <li style={{padding: '0.25rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#3b82f6', marginRight: '0.5rem'}}>•</span>
                      <span>Track horse care, feeding, and medications for your barn</span>
                    </li>
                    <li style={{padding: '0.25rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#3b82f6', marginRight: '0.5rem'}}>•</span>
                      <span>Share calendar events with your entire barn</span>
                    </li>
                  </ul>
                </div>

                <div style={{background: '#fef3c7', padding: '1.5rem', borderRadius: '0.5rem', marginBottom: '2rem', border: '2px solid #f59e0b'}}>
                  <h3 style={{fontSize: '1.125rem', fontWeight: '600', color: '#92400e', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <UserPlus size={20} />
                    Barn Member Mode
                  </h3>
                  <p style={{color: '#374151', fontSize: '0.875rem', marginBottom: '0.75rem'}}>
                    <strong>For Horse Owners:</strong> Join your trainer's barn by entering their barn code in Settings.
                  </p>
                  <ul style={{listStyle: 'none', padding: 0, margin: 0, fontSize: '0.875rem', color: '#374151'}}>
                    <li style={{padding: '0.25rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#f59e0b', marginRight: '0.5rem'}}>•</span>
                      <span>Your trainer can access your horse's care information</span>
                    </li>
                    <li style={{padding: '0.25rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#f59e0b', marginRight: '0.5rem'}}>•</span>
                      <span>See barn-wide calendar events and schedules</span>
                    </li>
                    <li style={{padding: '0.25rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#f59e0b', marginRight: '0.5rem'}}>•</span>
                      <span>Collaborate on feeding schedules and medication tracking</span>
                    </li>
                  </ul>
                </div>

                <div style={{padding: '1rem', background: '#dbeafe', borderRadius: '0.5rem', marginBottom: '2rem'}}>
                  <p style={{color: '#1e40af', fontSize: '0.875rem', margin: 0}}>
                    💡 <strong>Tip:</strong> You can set this up later in Settings. It's completely optional!
                  </p>
                </div>

                <button
                  onClick={() => setOnboardingStep(6)}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    background: '#ea580c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Training Completed!
                </button>
                <button
                  onClick={() => setOnboardingStep(4)}
                  style={{
                    width: '100%',
                    marginTop: '0.5rem',
                    padding: '0.75rem',
                    background: 'transparent',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  Back
                </button>
              </div>
            )}

            {onboardingStep === 6 && (
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '4rem', marginBottom: '1rem'}}>🎉</div>
                <h2 style={{fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem'}}>
                  Training Completed!
                </h2>
                <p style={{color: '#6b7280', fontSize: '1rem', marginBottom: '2rem'}}>
                  You're ready to start tracking your barrel racing journey with SaddlePulse. Explore the features and make the most of your $4.99/month subscription!
                </p>

                <div style={{background: '#f3f4f6', padding: '1.5rem', borderRadius: '0.5rem', marginBottom: '2rem'}}>
                  <h3 style={{fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '1rem'}}>Quick Tips:</h3>
                  <ul style={{listStyle: 'none', padding: 0, margin: 0, textAlign: 'left', fontSize: '0.875rem', color: '#374151'}}>
                    <li style={{padding: '0.5rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#10b981', marginRight: '0.5rem'}}>✓</span>
                      <span>Add races regularly to see performance trends and analytics</span>
                    </li>
                    <li style={{padding: '0.5rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#10b981', marginRight: '0.5rem'}}>✓</span>
                      <span>Track expenses to understand your total investment</span>
                    </li>
                    <li style={{padding: '0.5rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#10b981', marginRight: '0.5rem'}}>✓</span>
                      <span>Use venue analysis to see which arenas you perform best at</span>
                    </li>
                    <li style={{padding: '0.5rem 0', display: 'flex', alignItems: 'start'}}>
                      <span style={{color: '#10b981', marginRight: '0.5rem'}}>✓</span>
                      <span>Upload Coggins tests to get expiration reminders</span>
                    </li>
                  </ul>
                </div>

                <button
                  onClick={async () => {
                    // Mark onboarding as completed
                    try {
                      await fetch(`${API_URL}/auth/complete-onboarding`, {
                        method: 'POST',
                        headers: {
                          Authorization: `Bearer ${localStorage.getItem('token')}`
                        }
                      });

                      // Update user state to reflect completion
                      setUser(prev => ({ ...prev, onboarding_completed: true }));
                    } catch (err) {
                      console.error('Failed to mark onboarding complete:', err);
                    }

                    setShowOnboarding(false);
                    setOnboardingStep(1);
                    setActiveView('dashboard');
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: '#ea580c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '1.125rem'
                  }}
                >
                  Start Using SaddlePulse
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      </main>
    </div>
  );
}
